import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
  Plus,
  X,
  CheckCircle,
  AlertTriangle,
  Info,
  Server,
  ArrowRight,
  ArrowLeft,
  Download,
  FileSpreadsheet,
  RotateCcw,
  Undo2,
  Redo2,
  Share2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Trash2,
  Hand,
  MousePointer,
  Upload,
  Maximize2,
  Eye,
  EyeOff,
  Route,
  Tags,
  Cable,
} from "lucide-react";
import { getSchemaById, getSlugFromProductPage } from "./components/partNumberSchemaUtils";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { db } from "./firebase";
import "./SolutionsBuilder.css";

const STORE_KEY = "gofoton-solutions-v3";
const LIMITS = { racks: 50000, runs: 100000 };
const LOGO =
  "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FGoFoton%20Logo.png?alt=media&token=752e6ff9-fc99-41bc-8074-2eaa74451894";
const IDS = {
  app: "peacoc-any-port-panel",
  classic: "peacoc-high-density-fiber-patch-panel",
  eva: "peacoc-eva",
  nemo: "nemo-fiber-patch-panel",
  mpo: "mpo-patch-cords",
  patch: "simplex-duplex-patch-cords",
  mdc: "mdc-patch-cords",
  fanout: "multifiber-fanout-breakout-cables",
  optics: "400g-800g-16tb",
};

const PANEL_FAMILIES = [
  { id: IDS.classic, label: "PEACOC Classic" },
  { id: IDS.app, label: "PEACOC APP" },
  { id: IDS.eva, label: "PEACOC EVA" },
  { id: IDS.nemo, label: "NEMO" },
];

const CABLE_FAMILIES = [
  { id: IDS.patch, label: "Simplex / Duplex / CS-LC Jumper" },
  { id: IDS.mpo, label: "MPO Jumper Assembly" },
  { id: IDS.mdc, label: "MDC Optical Jumper" },
];

const CABLE_ASSEMBLY_MODES = [
  { id: "breakout", label: "Breakout assembly" },
  { id: "jumper", label: "Individual jumper" },
];

const BREAKOUT_POLARITIES = [
  { id: "A", label: "Method A · straight-through" },
  { id: "B", label: "Method B · reverse mapping" },
  { id: "C", label: "Method C · pair-flipped" },
  { id: "custom", label: "Custom mapping · engineering review" },
];

const BREAKOUT_FURCATIONS = [
  { id: "kit-shrink", label: "Breakout kit + shrink tube" },
  { id: "shrink", label: "Shrink-tube transition" },
  { id: "molded", label: "Molded furcation body" },
  { id: "custom", label: "Custom furcation" },
];

const BREAKOUT_LABEL_MODES = [
  { id: "sequential", label: "Sequential 1 → N" },
  { id: "reverse-b", label: "Reverse labels on End B" },
  { id: "alpha", label: "Alphabetic A → Z" },
];

const BREAKOUT_DEFAULTS = {
  breakoutEnds: "both",
  branchCount: 8,
  fibersPerLeg: 12,
  legLengthAM: 3,
  legLengthBM: 3,
  legDiameterMM: 3,
  staggered: false,
  staggerStepMM: 50,
  pullingGrip: false,
  furcation: "kit-shrink",
  polarity: "B",
  labelMode: "sequential",
  onePerPanel: true,
  showCrossSection: true,
};

const VIEWS = {
  layout: "Layout view",
  rack: "Rack view",
  panel: "Panel view",
  cables: "Cables",
};

const BUDGET_TIERS = {
  [IDS.nemo]: 1,
  [IDS.eva]: 2,
  [IDS.classic]: 3,
  [IDS.app]: 4,
};
const UNIT_METERS = { M: 1, C: 0.01, D: 0.1, F: 0.3048, K: 1000 };
const FRAME_OPTIONS = [
  { id: "7ft", label: "7 ft frame · 40RU", ru: 40, heightM: 2.134 },
  { id: "9ft", label: "9 ft frame · 52RU", ru: 52, heightM: 2.743 },
];

const clone = (value) => JSON.parse(JSON.stringify(value));
const uid = (prefix) => `${prefix}${Math.random().toString(36).slice(2, 10)}`;
const fmt = (value) =>
  Number.isFinite(value) ? value.toLocaleString(undefined, { maximumFractionDigits: 3 }) : "Not set";
const rackKey = (rowId, index) => `${rowId}#${index}`;
const stable = (value) =>
  JSON.stringify(value, function (key, item) {
    return item && !Array.isArray(item) && typeof item === "object"
      ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b)))
      : item;
  });
const schema = (id) => {
  try {
    return getSchemaById(id);
  } catch {
    return null;
  }
};

// Character positions and grouped fields come from the existing ordering schema.
function schemaFields(schemaId) {
  const source = schema(schemaId);
  if (!source) return [];

  const positions = source.positions || [];
  const used = new Set();

  const groups = (source.groupedOptions || []).map((group) => {
    const indexes = [...group.indexes];
    indexes.forEach((index) => used.add(index));
    return {
      ...group,
      key: `g:${group.group}`,
      indexes,
      required: indexes.some((index) => positions.find((p) => p.index === index)?.required),
    };
  });

  return [
    ...groups,
    ...positions.filter((p) => !used.has(p.index)).map((p) => ({ ...p, key: `p:${p.index}`, indexes: [p.index] })),
  ].sort((a, b) => Math.min(...a.indexes) - Math.min(...b.indexes));
}

function fixedValue(field) {
  return field.required && Array.isArray(field.options) && field.options.length === 1
    ? String(field.options[0].code)
    : "";
}

function isLengthField(field) {
  return field.group === "fiberLength" || field.label === "Length Designator";
}

function seedDraft(schemaId, choices) {
  let draft = {};

  Object.entries(choices).forEach(([label, code]) => {
    const field = schemaFields(schemaId).find((f) => f.label === label);
    if (field && Array.isArray(field.options) && field.options.some((o) => String(o.code) === code))
      draft = { ...draft, [field.key]: code };
  });

  return draft;
}

function resolvePart(schemaId, draft = {}, lengthM = null) {
  const source = schema(schemaId);
  const issues = [];

  if (!source) return { code: "", issues: [`Missing schema: ${schemaId}`] };

  const fields = schemaFields(schemaId);
  let values = { ...draft };

  if (lengthM !== null) {
    const length = fields.find((f) => f.group === "fiberLength");
    const unit = fields.find((f) => f.label === "Length Designator");

    if (!length || !unit || !Array.isArray(unit.options))
      return { code: "", issues: ["This schema has no supported grouped cable-length field."] };

    let encoded = false;

    for (const unitCode of ["M", "C", "D", "K", "F"]) {
      if (!unit.options.some((o) => o.code === unitCode)) continue;

      const raw = lengthM / UNIT_METERS[unitCode];
      const rounded = Math.round(raw);

      if (raw > 0 && Math.abs(raw - rounded) < 0.000001 && String(rounded).length <= length.indexes.length) {
        values[unit.key] = unitCode;
        values[length.key] = String(rounded).padStart(length.indexes.length, "0");
        encoded = true;
        break;
      }
    }

    if (!encoded)
      return {
        code: "",
        issues: ["Finished length cannot be encoded exactly by this schema. Request a custom order code."],
      };
  }

  const highest = Math.max(-1, ...(source.positions || []).map((p) => p.index));
  const chars = Array(Math.max(highest + 1, source.maxLength || 0)).fill("");

  fields.forEach((field) => {
    const value = String(values[field.key] ?? fixedValue(field))
      .trim()
      .toUpperCase();

    if (!value) {
      if (field.required) issues.push(`${field.label} is required.`);
      return;
    }

    if (value.length !== field.indexes.length) {
      issues.push(`${field.label} needs ${field.indexes.length} characters.`);
      return;
    }

    const allowed = Array.isArray(field.options)
      ? field.options.some((o) => String(o.code).toUpperCase() === value)
      : true;

    if (
      !allowed &&
      !(field.allowNumeric && /^\d+$/.test(value)) &&
      !(field.allowAlphanumeric && /^[A-Z0-9]+$/.test(value))
    )
      issues.push(`${field.label}: invalid option.`);
    if (field.options === "0-9" && !/^\d+$/.test(value)) issues.push(`${field.label} must be numeric.`);
    if (field.options === "A-Z" && !/^[A-Z]+$/.test(value)) issues.push(`${field.label} must contain letters.`);

    field.indexes.forEach((index, i) => {
      chars[index] = value[i];
    });
  });

  while (chars.length && chars[chars.length - 1] === "") chars.pop();

  if (chars.some((char) => char === "")) issues.push("An earlier character is missing before a later field.");
  if (chars.length < (source.minLength || 0) || chars.length > (source.maxLength || highest + 1))
    issues.push("Part length is outside the schema limits.");

  return { code: issues.length ? "" : chars.join(""), issues: [...new Set(issues)] };
}

// Product-face references, separate from ordering rules. Counts are adapter housings, not fibers.
// The supplied 1RU LC faces show 3 rows of 24 duplex adapters = 72 adapters / 144 fibers.
// Cassette outlines are photo-based planning geometry, not a manufacturer mechanical fit approval.
const FACE_REFERENCE = { rowsPerRU: 3, adaptersPerRow: 24 };
const ADAPTER_FIELDS = [
  "Front Cassette Adapter Type",
  "Cassette Adapter Type",
  "Type of Ports",
  "Connector Type / Adapter Type",
];
const REAR_ADAPTER_FIELDS = ["Input Adapter Type", "Rear Adapter Type", "Rear Cable Connection / Inner Cabling"];
const RU_FIELDS = ["Number of RU", "Number of RUs"];
const APP_COMMON_SIZES = [4, 6, 8, 12];

function fieldFor(id, labels) {
  return schemaFields(id).find((f) => labels.includes(f.label));
}

function fieldValue(id, draft, labels) {
  const f = fieldFor(id, labels);
  return f ? String(draft?.[f.key] ?? fixedValue(f)) : "";
}

function fieldDescription(id, draft, labels) {
  const f = fieldFor(id, labels);
  const value = f ? String(draft?.[f.key] ?? fixedValue(f)) : "";
  return Array.isArray(f?.options) ? f.options.find((o) => String(o.code) === value)?.description || "" : "";
}

function putField(id, draft, labels, value) {
  const f = fieldFor(id, labels);
  if (!f) return draft;
  return { ...draft, [f.key]: String(value) };
}

function panelFieldsFor(c, panelIndex = 1) {
  const layout = c.panelLayouts?.[panelIndex - 1];
  return layout?.panelFields || c.panelFields || {};
}

function panelRU(c, panelIndex = 1) {
  const value = Number(fieldValue(c.panelSchema, panelFieldsFor(c, panelIndex), RU_FIELDS));
  return [1, 2, 4].includes(value) ? value : 1;
}

function totalPanelRU(c) {
  if (Array.isArray(c.panelLayouts) && c.panelLayouts.length) {
    return c.panelLayouts.reduce((sum, layout) => sum + (Number(layout.ru) || 1), 0);
  }

  return Math.max(0, Number(c.panelQty) || 0) * panelRU(c);
}

function frameCapacityRU(c) {
  if (c.frameType === "custom") return Math.max(1, Number(c.customFrameRU) || 40);
  return FRAME_OPTIONS.find((option) => option.id === c.frameType)?.ru || 40;
}

function frameHeightM(c) {
  if (c.frameType === "custom") return Number(c.customFrameHeightM) > 0 ? Number(c.customFrameHeightM) : 2.134;
  return FRAME_OPTIONS.find((option) => option.id === c.frameType)?.heightM || 2.134;
}

function mountingWidthIn(c) {
  return String(c.mountingWidth || "23") === "19" ? 19 : 23;
}

function refreshManualSizing(c) {
  const adapter = frontAdapter(c);
  const fibersPerAdapter = Math.max(1, adapter.fibers || (adapter.kind === "mpo" ? Number(c.frontMpoFibers) || 12 : 1));
  const installedAdapters = totalAdapterPorts(c);
  const installedLines = installedAdapters * fibersPerAdapter;
  const usedRU = totalPanelRU(c);
  const linesPerRU = 72 * fibersPerAdapter;

  c.patchQty = installedAdapters;
  c.requiredPorts = installedLines;
  c.autoSizing = {
    requiredPorts: installedLines,
    requiredAdapters: installedAdapters,
    fibersPerAdapter,
    linesPerRU,
    requiredRU: usedRU,
    panelRU: panelRU(c, 1),
    panelQty: Math.max(0, Number(c.panelQty) || 0),
    panelPlan: (c.panelLayouts || []).map((layout) => Number(layout.ru) || 1),
    chassisCapacity: usedRU * linesPerRU,
    installedLines,
    sparePorts: 0,
    needsReview: c.panelSchema === IDS.classic,
  };

  return c;
}

function panelPortOffset(c, panelIndex = 1) {
  let offset = 0;

  for (let i = 1; i < panelIndex; i++) {
    offset += faceModel(c, i).adapters;
  }

  return offset;
}

function totalAdapterPorts(c) {
  let total = 0;

  for (let i = 1; i <= Math.max(0, c.panelQty || 0); i++) {
    total += faceModel(c, i).adapters;
  }

  return total;
}

function panelForRackPort(c, port) {
  let offset = 0;

  for (let i = 1; i <= Math.max(1, c.panelQty || 1); i++) {
    const count = faceModel(c, i).adapters;

    if (port <= offset + count) return i;
    offset += count;
  }

  return Math.max(1, c.panelQty || 1);
}

function nominalLinesPerRU(c) {
  const adapter = frontAdapter(c);

  if (adapter.fibers) return 72 * adapter.fibers;
  return 72;
}

function appAllowedRU(c) {
  const field = fieldFor(c.panelSchema, RU_FIELDS);

  if (!field || !Array.isArray(field.options)) return [1];

  const values = field.options
    .map((option) => Number(option.code))
    .filter((value) => Number.isInteger(value) && value > 0)
    .sort((a, b) => a - b);

  return values.length ? [...new Set(values)] : [1];
}

function deleteField(id, draft, labels) {
  const field = fieldFor(id, labels);
  if (!field) return draft;

  const next = { ...draft };
  delete next[field.key];
  return next;
}

function applyKnownPanelRules(c, targetSpeed = "") {
  if (c.panelSchema !== IDS.app) return c;

  let draft = { ...c.panelFields };

  const fiber = fieldValue(c.panelSchema, draft, ["Fiber Type"]);
  const front = fieldValue(c.panelSchema, draft, ["Front Cassette Adapter Type"]);
  const input = fieldValue(c.panelSchema, draft, ["Input Adapter Type"]);
  const polish = fieldValue(c.panelSchema, draft, ["Polish / Contact"]);
  const frontIsMpo = ["M", "N"].includes(front);

  if (!frontIsMpo && fiber === "2") {
    draft = putField(c.panelSchema, draft, ["Polish / Contact"], "5");
  } else if (!frontIsMpo && fiber === "1" && !polish) {
    draft = putField(c.panelSchema, draft, ["Polish / Contact"], "6");
  }

  if (targetSpeed === "100G" && Number(c.mpoFiberCount || 24) === 24 && input === "M" && ["K", "L"].includes(front)) {
    draft = putField(c.panelSchema, draft, ["Inner Cable Options"], "C");
  }

  c.panelFields = draft;
  return c;
}

function selectedCassettePortCount(c) {
  const selected = Number(fieldValue(c.panelSchema, c.panelFields, ["Cassette Front Adapter Port Count"]));

  if ([4, 6, 8, 9, 12, 24, 28].includes(selected)) return selected;
  if (c.panelSchema === IDS.eva || c.panelSchema === IDS.classic) return 12;
  return Math.max(1, Math.min(24, Number(c.portsPerModule) || 12));
}

function panelRUSequence(c, requiredAdapters) {
  if (c.panelSchema !== IDS.app) {
    return Array.from({ length: Math.max(1, Math.ceil(requiredAdapters / 72)) }, () => 1);
  }

  const allowed = appAllowedRU(c).sort((a, b) => b - a);
  const maxRU = Math.max(...allowed);
  const result = [];
  let remaining = requiredAdapters;

  while (remaining > 0) {
    if (remaining > 72 * maxRU) {
      result.push(maxRU);
      remaining -= 72 * maxRU;
      continue;
    }

    const needed = Math.max(1, Math.ceil(remaining / 72));
    const ru = [...allowed].sort((a, b) => a - b).find((value) => value >= needed) || maxRU;
    result.push(ru);
    remaining = 0;
  }

  return result;
}

function buildFacePlanForLoad(c, ru, adapterLoad) {
  const rowCount = Math.max(1, ru) * FACE_REFERENCE.rowsPerRU;

  if (c.panelSchema === IDS.nemo) {
    return {
      version: 1,
      family: c.panelSchema,
      rows: Array.from({ length: rowCount }, () => []),
      nemoPorts: Math.max(0, Math.min(72, adapterLoad)),
    };
  }

  const preferred = selectedCassettePortCount(c);
  const perRow =
    c.panelSchema === IDS.eva || c.panelSchema === IDS.classic
      ? 2
      : Math.max(1, Math.floor(24 / Math.min(24, preferred)));

  let remaining = Math.max(0, Math.ceil(adapterLoad / preferred));

  const rows = Array.from({ length: rowCount }, () => {
    const count = Math.min(perRow, remaining);
    remaining -= count;
    return Array(count).fill(preferred);
  });

  return {
    version: 1,
    family: c.panelSchema,
    rows,
    nemoPorts: 72,
    legacyOverflow: remaining,
  };
}

function countPlanAdapters(c, plan) {
  if (c.panelSchema === IDS.nemo) return Math.max(0, plan.nemoPorts || 0);
  return (plan.rows || []).reduce((sum, row) => sum + row.reduce((rowSum, size) => rowSum + size, 0), 0);
}

function fieldsForPanelLoad(c, baseFields, ru, plan) {
  let fields = { ...baseFields };
  fields = putField(c.panelSchema, fields, RU_FIELDS, String(ru));

  const cassetteCount = (plan.rows || []).reduce((sum, row) => sum + row.length, 0);

  if (c.panelSchema === IDS.nemo) {
    fields = putField(
      c.panelSchema,
      fields,
      ["Number of Ports"],
      String(Math.max(0, plan.nemoPorts || 0)).padStart(3, "0")
    );
  } else if (c.panelSchema === IDS.classic) {
    fields = putField(
      c.panelSchema,
      fields,
      ["Number of Trays"],
      cassetteCount ? String(Math.min(6, cassetteCount)) : "X"
    );
  } else if (c.panelSchema === IDS.app) {
    const fieldSelectedPorts = Number(fieldValue(c.panelSchema, fields, ["Cassette Front Adapter Port Count"]));

    const cassettePorts = [4, 6, 8, 9, 12, 24, 28].includes(fieldSelectedPorts)
      ? fieldSelectedPorts
      : selectedCassettePortCount(c);

    const maxPerRow = Math.max(1, Math.floor(24 / Math.min(24, cassettePorts)));
    const fullCount = ru * FACE_REFERENCE.rowsPerRU * maxPerRow;

    fields = putField(
      c.panelSchema,
      fields,
      ["Cassette Front Adapter Port Count"],
      String(cassettePorts).padStart(2, "0")
    );

    const cassetteCode =
      cassetteCount === 0
        ? "H"
        : cassetteCount <= 5
          ? ["", "A", "B", "C", "D", "E"][cassetteCount]
          : cassetteCount === fullCount
            ? "F"
            : "";

    fields = cassetteCode
      ? putField(c.panelSchema, fields, ["Number of Cassettes"], cassetteCode)
      : deleteField(c.panelSchema, fields, ["Number of Cassettes"]);
  }

  return fields;
}

function autoSizeConfig(c, targetSpeed = "") {
  c.requiredPorts = Math.max(
    1,
    Math.round(
      Number(c.requiredPorts) || Math.max(1, (Number(c.patchQty) || 1) * Math.max(1, frontAdapter(c).fibers || 1))
    )
  );

  c.mpoFiberCount = Number(c.mpoFiberCount) || 24;
  c.splitRatio = Math.max(1, Number(c.splitRatio) || 1);
  c.autoSizeEnabled = c.autoSizeEnabled !== false;

  applyKnownPanelRules(c, targetSpeed);

  const adapter = frontAdapter(c);
  const fibersPerAdapter = Math.max(1, adapter.fibers || (adapter.kind === "mpo" ? Number(c.frontMpoFibers) || 12 : 1));

  const requiredAdapters = Math.max(1, Math.ceil(c.requiredPorts / fibersPerAdapter));
  const linesPerRU = 72 * fibersPerAdapter;
  const requiredRU = Math.max(1, Math.ceil(requiredAdapters / 72));
  const ruSequence = panelRUSequence(c, requiredAdapters);

  let remaining = requiredAdapters;
  const baseFields = { ...c.panelFields };

  const panelLayouts = ruSequence.map((ru, index) => {
    const adapterTarget = Math.min(remaining, 72 * ru);
    remaining -= adapterTarget;

    const facePlan = buildFacePlanForLoad(c, ru, adapterTarget);
    const panelFields = fieldsForPanelLoad(c, baseFields, ru, facePlan);

    return {
      id: c.panelLayouts?.[index]?.id || uid("panel"),
      ru,
      adapterTarget,
      facePlan,
      panelFields,
    };
  });

  c.panelLayouts = panelLayouts;
  c.panelQty = panelLayouts.length;
  c.panelFields = { ...(panelLayouts[0]?.panelFields || baseFields) };
  c.facePlan = clone(panelLayouts[0]?.facePlan || buildFacePlanForLoad(c, 1, requiredAdapters));
  c.patchQty = requiredAdapters;
  c.disabledPorts = (c.disabledPorts || []).filter((port) => Number.isInteger(port) && port > 0 && port <= c.patchQty);
  c.portsPerModule = selectedCassettePortCount(c);
  c.modulesPerPanel = (panelLayouts[0]?.facePlan?.rows || []).reduce((sum, row) => sum + row.length, 0);

  const installedAdapters = panelLayouts.reduce((sum, layout) => sum + countPlanAdapters(c, layout.facePlan), 0);

  const chassisCapacity = panelLayouts.reduce((sum, layout) => sum + layout.ru * linesPerRU, 0);

  const installedLines = installedAdapters * fibersPerAdapter;

  c.autoSizing = {
    requiredPorts: c.requiredPorts,
    requiredAdapters,
    fibersPerAdapter,
    linesPerRU,
    requiredRU,
    panelRU: panelLayouts[0]?.ru || 1,
    panelQty: panelLayouts.length,
    panelPlan: panelLayouts.map((layout) => layout.ru),
    chassisCapacity,
    installedLines,
    sparePorts: Math.max(0, installedLines - c.requiredPorts),
    needsReview: c.panelSchema === IDS.classic,
  };

  c.faceMappingReview = true;
  return c;
}

function sizingFor(c) {
  const adapter = frontAdapter(c);
  const fibersPerAdapter = Math.max(1, adapter.fibers || (adapter.kind === "mpo" ? Number(c.frontMpoFibers) || 12 : 1));

  const requiredPorts = c.requiredPorts || Math.max(1, c.patchQty || 1) * fibersPerAdapter;
  const layouts =
    Array.isArray(c.panelLayouts) && c.panelLayouts.length
      ? c.panelLayouts
      : Array.from({ length: Math.max(1, c.panelQty || 1) }, (_, i) => ({
          ru: panelRU(c, i + 1),
          facePlan: facePlan(c, i + 1),
        }));

  const installedAdapters = layouts.reduce((sum, layout) => sum + countPlanAdapters(c, layout.facePlan), 0);

  const linesPerRU = 72 * fibersPerAdapter;
  const installedLines = installedAdapters * fibersPerAdapter;

  return (
    c.autoSizing || {
      requiredPorts,
      requiredAdapters: Math.ceil(requiredPorts / fibersPerAdapter),
      fibersPerAdapter,
      linesPerRU,
      requiredRU: Math.max(1, Math.ceil(requiredPorts / linesPerRU)),
      panelRU: layouts[0]?.ru || 1,
      panelQty: layouts.length,
      panelPlan: layouts.map((layout) => layout.ru || 1),
      chassisCapacity: layouts.reduce((sum, layout) => sum + (layout.ru || 1) * linesPerRU, 0),
      installedLines,
      sparePorts: Math.max(0, installedLines - requiredPorts),
      needsReview: c.panelSchema === IDS.classic,
    }
  );
}

function budgetTier(id) {
  const level = BUDGET_TIERS[id] || 2;

  return {
    level,
    label: "$".repeat(level),
    text: ["", "Value", "Moderate", "Premium", "Highest density"][level] || "Relative tier",
  };
}

function frontAdapter(c) {
  const text = fieldDescription(c.panelSchema, c.panelFields, ADAPTER_FIELDS);
  const polish = fieldDescription(c.panelSchema, c.panelFields, [
    "Polish / Contact",
    "Adapter Type / Color",
    "Port Color",
  ]);

  let kind = "generic";
  let fibers = null;

  if (/SN[-\s]*MT/i.test(text)) {
    kind = "snmt";
  } else if (/MMC/i.test(text)) {
    kind = "mmc";
  } else if (/MDC/i.test(text)) {
    kind = "mdc";
    fibers = 2;
  } else if (/\bSN\b/i.test(text)) {
    kind = "sn";
    fibers = 2;
  } else if (/\bCS\b/i.test(text)) {
    kind = "cs";
    fibers = 2;
  } else if (/LC.*Duplex|Duplex.*LC/i.test(text)) {
    kind = "lc";
    fibers = 2;
  } else if (/LC.*Simplex|Simplex.*LC/i.test(text)) {
    kind = "lc-simplex";
    fibers = 1;
  } else if (/SC.*Simplex|Simplex.*SC|\bSC\b/i.test(text)) {
    kind = "sc";
    fibers = 1;
  } else if (/MPO/i.test(text)) {
    kind = "mpo";
    fibers = c.frontMpoFibers || (/16/i.test(text) ? 16 : null);
  } else if (!text) {
    kind = "lc";
    fibers = 2;
  }

  const color = /APC|green/i.test(polish) ? "apc" : /aqua|multimode|OM3|OM4/i.test(polish) ? "mm" : "sm";

  return {
    kind,
    fibers,
    color,
    label: text || "LC Duplex preview",
    confirmed: !!text,
  };
}

function panelConnectorField(c) {
  return fieldFor(c.panelSchema, ADAPTER_FIELDS);
}

function panelConnectorChoices(c) {
  const field = panelConnectorField(c);

  if (!Array.isArray(field?.options)) return [];

  return field.options.map((option) => ({
    id: String(option.code),
    label: option.description,
  }));
}

function connectorKindFromText(text = "") {
  if (/SN[-\s]*MT/i.test(text)) return "snmt";
  if (/MMC/i.test(text)) return "mmc";
  if (/MDC/i.test(text)) return "mdc";
  if (/MPO/i.test(text)) return "mpo";
  if (/\bSN\b/i.test(text)) return "sn";
  if (/\bCS\b/i.test(text)) return "cs";
  if (/\bSC\b/i.test(text)) return "sc";
  if (/LC.*Simplex|Simplex.*LC/i.test(text)) return "lc-simplex";
  if (/\bLC\b/i.test(text)) return "lc";
  if (/\bFC\b/i.test(text)) return "fc";
  if (/\bST\b/i.test(text)) return "st";
  if (/\bMU\b/i.test(text)) return "mu";
  return "generic";
}

function connectorFibersForKind(kind, description = "") {
  if (kind === "lc-simplex" || kind === "sc" || kind === "fc" || kind === "st" || kind === "mu") return 1;
  if (["lc", "cs", "sn", "mdc"].includes(kind)) return 2;
  if (kind === "mpo") return Number((description.match(/\b(8|12|16|24)\b/) || [])[1]) || 24;
  return 2;
}

function panelConnectorDefinition(c, code) {
  const field = panelConnectorField(c);
  const description = field?.options?.find((option) => String(option.code) === String(code))?.description || "";
  const polishText = `${description} ${fieldDescription(c.panelSchema, c.panelFields, ["Polish / Contact", "Adapter Type / Color", "Port Color"])}`;
  const kind = connectorKindFromText(description);

  return {
    panelCode: String(code || ""),
    description: description || "Connector not selected",
    kind,
    fibers: connectorFibersForKind(kind, description),
    apc: /APC|green/i.test(polishText),
    multimode: /OM3|OM4|aqua|multimode/i.test(polishText),
    custom: ["mmc", "snmt", "generic"].includes(kind),
  };
}

function panelInterfaceField(c, role = "front") {
  return role === "rear" ? fieldFor(c.panelSchema, REAR_ADAPTER_FIELDS) : panelConnectorField(c);
}

function panelInterfaceChoices(c, role = "front") {
  const field = panelInterfaceField(c, role);
  if (!Array.isArray(field?.options)) return [];

  return field.options
    .filter((option) => !/^none|n\/a$/i.test(String(option.description || "")))
    .map((option) => ({ id: String(option.code), label: option.description }));
}

function panelInterfaceCode(c, role = "front") {
  return fieldValue(c.panelSchema, c.panelFields, role === "rear" ? REAR_ADAPTER_FIELDS : ADAPTER_FIELDS);
}

function panelInterfaceDefinition(c, code, role = "front") {
  if (role === "front") return panelConnectorDefinition(c, code);

  const field = panelInterfaceField(c, "rear");
  const description = field?.options?.find((option) => String(option.code) === String(code))?.description || "";
  const kind = connectorKindFromText(description);

  return {
    panelCode: String(code || ""),
    description: description || "Rear interface not selected",
    kind,
    fibers: connectorFibersForKind(kind, description),
    apc: /APC|green/i.test(description),
    multimode: /OM3|OM4|aqua|multimode/i.test(description),
    custom: ["mmc", "snmt", "generic"].includes(kind),
    role: "rear",
  };
}

function cableConnectorFields(schemaId) {
  return schemaFields(schemaId).filter((field) => /connector/i.test(field.label));
}

function cableEndField(schemaId, end = "a") {
  const fields = cableConnectorFields(schemaId);
  const preferred =
    end === "a"
      ? [/Connector Type End 1/i, /Connector.*End A/i, /Connector A/i]
      : [/Connector Type End 2/i, /Connector.*End B/i, /Connector B/i];

  return (
    fields.find((field) => preferred.some((pattern) => pattern.test(field.label))) ||
    (end === "a" ? fields[0] : fields[1] || fields[0])
  );
}

function connectorDescriptionMatches(definition, description) {
  const kind = connectorKindFromText(description);

  if (kind !== definition.kind && !(definition.kind === "lc-simplex" && kind === "lc")) return false;
  if (definition.apc && !/APC/i.test(description)) return false;
  if (!definition.apc && /APC/i.test(description)) return false;
  return true;
}

function connectorCodeForSchema(schemaId, end, definition) {
  const field = cableEndField(schemaId, end);
  if (!field) return "";

  const options = Array.isArray(field.options)
    ? field.options
    : schema(schemaId)?.positions?.find(
        (position) => position.index === field.indexes[end === "a" ? 0 : field.indexes.length - 1]
      )?.options;

  if (!Array.isArray(options)) return "";

  const exact = options.find((option) => connectorDescriptionMatches(definition, String(option.description || "")));
  if (exact) return String(exact.code);

  const custom = options.find((option) => /other|custom/i.test(String(option.description || "")));
  return custom ? String(custom.code) : "";
}

function setCableEndCode(schemaId, draft, end, code) {
  const field = cableEndField(schemaId, end);
  if (!field || !code) return draft;

  const next = { ...draft };

  if (field.indexes.length > 1) {
    const current = String(next[field.key] ?? fixedValue(field))
      .padEnd(field.indexes.length, "X")
      .split("");
    const offset = end === "a" ? 0 : field.indexes.length - 1;
    current[offset] = String(code);
    next[field.key] = current.join("");
  } else {
    next[field.key] = String(code);
  }

  return next;
}

function copyCommonCableFields(fromSchema, fromDraft, toSchema, toDraft) {
  let next = { ...toDraft };

  ["Fiber Type", "Cordage Diameter", "Jacket Rating"].forEach((label) => {
    const sourceField = fieldFor(fromSchema, [label]);
    const targetField = fieldFor(toSchema, [label]);
    const value = sourceField ? String(fromDraft?.[sourceField.key] ?? "") : "";

    if (
      value &&
      targetField &&
      Array.isArray(targetField.options) &&
      targetField.options.some((option) => String(option.code) === value)
    ) {
      next[targetField.key] = value;
    }
  });

  return next;
}

function chooseCableSchema(a, b) {
  if (a.kind === "mpo" && b.kind === "mpo" && schema(IDS.mpo)) return IDS.mpo;
  if (a.kind === "mdc" && b.kind === "mdc" && schema(IDS.mdc)) return IDS.mdc;

  if (
    a.kind === b.kind &&
    a.apc === b.apc &&
    ["lc", "lc-simplex", "sc", "cs", "sn", "fc", "st", "mu"].includes(a.kind) &&
    schema(IDS.patch)
  ) {
    return IDS.patch;
  }

  return schema(IDS.fanout) ? IDS.fanout : IDS.patch;
}

function fiberCountCodeForSchema(schemaId, count) {
  const field = fieldFor(schemaId, ["Fiber Count"]);
  if (!field || !Array.isArray(field.options)) return "";

  const desired = Math.max(1, Number(count) || 1);
  const parsed = field.options.map((option) => ({
    code: String(option.code),
    count: Number((String(option.description).match(/\d+/) || [])[0]) || 0,
    description: String(option.description),
  }));

  const exact = parsed.find((option) => option.count === desired);
  if (exact) return exact.code;

  return parsed.find((option) => /other/i.test(option.description))?.code || "";
}

function suggestedBreakoutDiameterCode(totalFibers) {
  const total = Math.max(1, Number(totalFibers) || 1);
  if (total >= 288) return "M";
  if (total <= 24) return "F";
  return "P";
}

function applySuggestedBreakoutDiameter(draft, totalFibers, { force = false } = {}) {
  const field = fieldFor(IDS.fanout, ["Cordage Diameter"]);
  if (!field) return draft;

  const current = String(draft?.[field.key] || "");
  const currentDescription = Array.isArray(field.options)
    ? field.options.find((option) => String(option.code) === current)?.description || ""
    : "";
  const currentMM = Number((currentDescription.match(/(\d+(?:\.\d+)?)\s*mm/i) || [])[1]);
  const total = Math.max(1, Number(totalFibers) || 1);
  const requiredCode = suggestedBreakoutDiameterCode(total);

  if (force || !current || (total >= 288 && (!Number.isFinite(currentMM) || currentMM < 11))) {
    return { ...draft, [field.key]: requiredCode };
  }

  return draft;
}

function selectedSchemaFiberCount(group) {
  const description = selectedCableDescription(group, "Fiber Count");
  if (!description || /other/i.test(description)) return null;
  return Number((description.match(/\d+/) || [])[0]) || null;
}

function cableFieldsForSpecificSchema(schemaId, a, b, existingDraft = null, existingSchema = null, fiberCount = null) {
  let draft = defaultCableFields(schemaId);

  if (existingSchema && existingDraft) {
    draft = copyCommonCableFields(existingSchema, existingDraft, schemaId, draft);
  }

  draft = setCableEndCode(schemaId, draft, "a", connectorCodeForSchema(schemaId, "a", a));
  draft = setCableEndCode(schemaId, draft, "b", connectorCodeForSchema(schemaId, "b", b));
  draft = putField(
    schemaId,
    draft,
    ["Fiber Count"],
    fiberCountCodeForSchema(schemaId, fiberCount ?? Math.max(a.fibers || 1, b.fibers || 1))
  );

  return { schemaId, draft };
}

function cableFieldsForEndpoints(a, b, existingDraft = null, existingSchema = null) {
  return cableFieldsForSpecificSchema(chooseCableSchema(a, b), a, b, existingDraft, existingSchema);
}

function appSizes() {
  const f = fieldFor(IDS.app, ["Cassette Front Adapter Port Count"]);
  const values = Array.isArray(f?.options)
    ? f.options.map((o) => Number(o.code)).filter((n) => Number.isInteger(n) && n > 0 && n <= 72)
    : [4, 6, 8, 9, 12, 28];
  return [...new Set(values)].sort((a, b) => a - b);
}

function initialFace(c, panelIndex = 1) {
  const ru = panelRU(c, panelIndex);
  const rowCount = ru * FACE_REFERENCE.rowsPerRU;

  if (c.panelSchema === IDS.nemo) {
    const n = Number(fieldValue(c.panelSchema, panelFieldsFor(c, panelIndex), ["Number of Ports"]));
    return {
      version: 1,
      family: c.panelSchema,
      rows: Array.from({ length: rowCount }, () => []),
      nemoPorts: n > 0 ? n : 72,
    };
  }

  const preferred =
    c.panelSchema === IDS.eva || c.panelSchema === IDS.classic ? 12 : Math.max(1, Math.min(72, c.portsPerModule || 4));

  let remaining = Math.max(
    0,
    c.modulesPerPanel ?? (c.panelSchema === IDS.eva || c.panelSchema === IDS.classic ? rowCount * 2 : rowCount * 6)
  );

  const perRow =
    c.panelSchema === IDS.eva || c.panelSchema === IDS.classic
      ? 2
      : Math.max(1, Math.floor(24 / Math.min(24, preferred)));

  const rows = Array.from({ length: rowCount }, () => {
    const count = Math.min(perRow, remaining);
    remaining -= count;
    return Array(count).fill(preferred);
  });

  return {
    version: 1,
    family: c.panelSchema,
    rows,
    nemoPorts: 72,
    legacyOverflow: remaining,
  };
}

function facePlan(c, panelIndex = 1) {
  const layout = c.panelLayouts?.[panelIndex - 1];
  const candidate = layout?.facePlan || (panelIndex === 1 ? c.facePlan : null);
  const original =
    candidate?.version === 1 && candidate.family === c.panelSchema ? candidate : initialFace(c, panelIndex);

  const rowCount = panelRU(c, panelIndex) * FACE_REFERENCE.rowsPerRU;

  return {
    ...original,
    rows: Array.from({ length: rowCount }, (_, i) => original.rows[i] || []),
    nemoPorts: Math.max(0, Math.min(72, original.nemoPorts ?? 72)),
  };
}

function faceModel(c, panelIndex = 1) {
  const plan = facePlan(c, panelIndex);
  const adapter = frontAdapter(c);
  const ru = panelRU(c, panelIndex);
  let number = 0;
  let cassette = 0;
  const isNemo = c.panelSchema === IDS.nemo;

  const rows = plan.rows.map((sizes, rowIndex) => {
    let col = 0;
    const list = isNemo ? [24] : sizes;

    const groups = list.map((size, slot) => {
      const span = isNemo ? 24 : c.panelSchema === IDS.eva || c.panelSchema === IDS.classic ? 12 : Math.min(24, size);

      const group = {
        row: rowIndex,
        slot,
        ports: size,
        start: col,
        span,
        id: `${rowIndex}:${slot}`,
        cassette: isNemo ? null : ++cassette,
      };

      col += span;

      group.items = Array.from({ length: size }, (_, i) => {
        const populated = !isNemo || rowIndex * 24 + i < plan.nemoPorts;
        return {
          local: i + 1,
          number: populated ? ++number : null,
          populated,
        };
      });

      return group;
    });

    return {
      groups,
      span: col,
      label: String.fromCharCode(65 + rowIndex),
      unverified: col > 24 || sizes.some((n) => !APP_COMMON_SIZES.includes(n) && c.panelSchema === IDS.app),
    };
  });

  const warnings = [];

  if (plan.legacyOverflow)
    warnings.push("The cassette count exceeded this panel face. Review the panel before ordering.");
  if (rows.some((row) => row.span > 24)) warnings.push("A cassette row exceeds the 24-position reference width.");
  if (c.panelSchema === IDS.app && rows.some((row) => row.unverified))
    warnings.push("This APP cassette size or combination needs manufacturer footprint confirmation.");
  if (c.panelSchema === IDS.eva && plan.rows.some((row) => row.length > 2 || row.some((n) => n !== 12)))
    warnings.push("EVA preview uses two half-width 12-adapter cassettes per row. Confirm other geometries separately.");
  if (number > 72 * ru)
    warnings.push(
      `The drawing contains ${number} adapters; the reference is ${72 * ru} per ${ru}RU. Do not treat it as a verified fit.`
    );

  const fields = panelFieldsFor(c, panelIndex);
  const rawRU = fieldValue(c.panelSchema, fields, RU_FIELDS);
  if (rawRU === "X") warnings.push("The panel ordering code selects cassette only, not a rack-mounted chassis.");

  return {
    plan,
    rows,
    ru,
    adapter,
    isNemo,
    adapters: number,
    cassettes: cassette,
    fibers: adapter.fibers == null ? null : number * adapter.fibers,
    reference: 72 * ru,
    warnings,
  };
}

function migrateConfig(c) {
  const adapter = frontAdapter(c);
  if (!Array.isArray(c.disabledPorts)) c.disabledPorts = [];
  if (!c.frameType) c.frameType = "7ft";
  if (!c.mountingWidth) c.mountingWidth = "23";
  if (!Number.isFinite(Number(c.customFrameRU))) c.customFrameRU = 40;
  if (!Number.isFinite(Number(c.customFrameHeightM))) c.customFrameHeightM = 2.134;
  const fibersPerAdapter = Math.max(1, adapter.fibers || 1);

  if (!Number.isFinite(Number(c.requiredPorts))) {
    c.requiredPorts = Math.max(1, (Number(c.patchQty) || 1) * fibersPerAdapter);
  }

  c.splitRatio = Math.max(1, Number(c.splitRatio) || 1);
  c.autoSizeEnabled = c.autoSizeEnabled !== false;

  if (!Array.isArray(c.panelLayouts) || !c.panelLayouts.length) {
    const qty = Math.max(1, Number(c.panelQty) || 1);
    const basePlan = c.facePlan?.version === 1 && c.facePlan.family === c.panelSchema ? c.facePlan : initialFace(c);

    c.panelLayouts = Array.from({ length: qty }, (_, i) => ({
      id: uid("panel"),
      ru: panelRU(c),
      adapterTarget: countPlanAdapters(c, basePlan),
      facePlan: clone(basePlan),
      panelFields: clone(c.panelFields || {}),
    }));
  }

  c.panelQty = c.panelLayouts.length;
  c.facePlan = clone(c.panelLayouts[0]?.facePlan || initialFace(c));
  c.faceMappingReview = c.faceMappingReview ?? true;

  return c;
}

function migrateProject(p) {
  [...p.configs, ...Object.values(p.rackOverrides)].forEach(migrateConfig);
  p.paths = (p.paths || []).map(migratePath);
  return p;
}

function cassetteBreakdown(c, panelIndex = 1) {
  const face = faceModel(c, panelIndex);
  if (face.isNemo) return [];

  const counts = new Map();
  face.rows.forEach((row) =>
    row.groups.forEach((group) => counts.set(group.ports, (counts.get(group.ports) || 0) + 1))
  );

  return [...counts.entries()].map(([ports, qty]) => ({ ports, qty }));
}

function modulePart(c, ports) {
  let draft = { ...(c.moduleDrafts?.[ports] || c.moduleFields) };
  draft = putField(c.panelSchema, draft, RU_FIELDS, "X");
  draft = putField(c.panelSchema, draft, ["Number of Cassettes"], "A");

  if (c.panelSchema === IDS.app) {
    draft = putField(c.panelSchema, draft, ["Cassette Front Adapter Port Count"], String(ports).padStart(2, "0"));
  }

  return resolvePart(c.panelSchema, draft);
}

function panelOrder(c, panelIndex = 1) {
  const fields = panelFieldsFor(c, panelIndex);
  const result = resolvePart(c.panelSchema, fields);
  const face = faceModel(c, panelIndex);
  const issues = [...result.issues, ...face.warnings];
  const countText = fieldDescription(c.panelSchema, fields, ["Number of Cassettes"]);
  const adapterCount = fieldValue(c.panelSchema, fields, ["Cassette Front Adapter Port Count"]);
  const sizes = cassetteBreakdown(c, panelIndex).map((item) => item.ports);

  if (!face.isNemo && !c.separateModules) {
    if (/empty/i.test(countText) && face.cassettes)
      issues.push("The ordering code is an empty chassis, but the drawing includes cassettes.");
    if (/^\d+$/.test(countText) && Number(countText) !== face.cassettes)
      issues.push("The ordered cassette quantity differs from the drawn cassette quantity.");
    if (/fully/i.test(countText) && face.adapters < face.reference)
      issues.push("The code says fully loaded; the drawn layout is partially populated.");
    if (c.panelSchema === IDS.app && sizes.length > 1)
      issues.push("A mixed APP assembly needs a custom assembly code or separate cassette orders.");
    if (c.panelSchema === IDS.app && adapterCount && sizes.some((n) => n !== Number(adapterCount)))
      issues.push("The ordered cassette port count differs from the cassette drawing.");
  }

  if (c.separateModules && !/empty/i.test(countText))
    issues.push("Separate cassette purchasing requires an empty chassis code.");

  if (face.isNemo) {
    const portCount = fieldValue(c.panelSchema, fields, ["Number of Ports"]);
    if (portCount && Number(portCount) !== face.adapters)
      issues.push("The ordered NEMO port count differs from the drawing.");
    if (/none|empty/i.test(fieldDescription(c.panelSchema, fields, ADAPTER_FIELDS)) && face.adapters)
      issues.push("The ordering code is empty, but the NEMO drawing contains adapters.");
  }

  if (face.isNemo && c.separateModules)
    issues.push("NEMO is drawn as direct adapters, not interchangeable APP/EVA cassettes.");

  return {
    code: issues.length ? "" : result.code,
    issues: [...new Set(issues)],
    candidate: result.code,
  };
}

function defaultCableFields(schemaId) {
  const choices =
    schemaId === IDS.mpo
      ? {
          "Connector Type End 1": "D",
          "Connector Type End 2": "D",
          "Fiber Type": "3",
          "Cordage Diameter": "F",
          "Jacket Rating": "5",
          "Fiber Count": "H",
        }
      : schemaId === IDS.patch
        ? {
            "Connector Type End": "D",
            "Fiber Type": "3",
            "Cordage Diameter": "E",
            "Jacket Rating": "5",
            "Fiber Count": "B",
          }
        : schemaId === IDS.fanout
          ? {
              "Connector Type End 1": "D",
              "Connector Type End 2": "D",
              "Fiber Type": "3",
              "Cordage Diameter": "P",
              "Jacket Rating": "3",
              "Fiber Count": "P",
            }
          : {};

  let draft = seedDraft(schemaId, choices);

  schemaFields(schemaId)
    .filter((field) => /connector/i.test(field.label))
    .forEach((field) => {
      if (Array.isArray(field.options) && field.options.some((option) => String(option.code) === "D")) {
        draft = {
          ...draft,
          [field.key]: "D",
        };
      }
    });

  return draft;
}

function normalizeBreakout(group) {
  const source = group?.breakout || {};
  return {
    ...BREAKOUT_DEFAULTS,
    ...source,
    branchCount: Math.max(1, Math.min(96, Math.round(Number(source.branchCount) || BREAKOUT_DEFAULTS.branchCount))),
    fibersPerLeg: Math.max(1, Math.round(Number(source.fibersPerLeg) || BREAKOUT_DEFAULTS.fibersPerLeg)),
    legLengthAM: Math.max(0.1, Number(source.legLengthAM) || BREAKOUT_DEFAULTS.legLengthAM),
    legLengthBM: Math.max(0.1, Number(source.legLengthBM) || BREAKOUT_DEFAULTS.legLengthBM),
    legDiameterMM: Math.max(0.25, Number(source.legDiameterMM) || BREAKOUT_DEFAULTS.legDiameterMM),
    staggerStepMM: Math.max(0, Number(source.staggerStepMM) || 0),
  };
}

function breakoutTotalFibers(group) {
  const spec = normalizeBreakout(group);
  return spec.branchCount * spec.fibersPerLeg;
}

function migratePath(path) {
  path.assemblyMode = path.assemblyMode === "breakout" ? "breakout" : "jumper";
  path.breakout = normalizeBreakout(path);
  path.connectorStyleA = path.connectorStyleA || "standard";
  path.connectorStyleB = path.connectorStyleB || "standard";

  if (path.assemblyMode === "breakout" && schema(IDS.fanout)) {
    const previousSchema = path.cableSchema;
    const previousFields = path.cableFields || {};
    const next = cableFieldsForSpecificSchema(
      IDS.fanout,
      path.endpointA || { kind: "mpo", apc: true },
      path.endpointB || { kind: "mpo", apc: true },
      previousFields,
      previousSchema,
      breakoutTotalFibers(path)
    );
    path.cableSchema = next.schemaId;
    path.cableFields = applySuggestedBreakoutDiameter(next.draft, breakoutTotalFibers(path), {
      force: previousSchema !== IDS.fanout,
    });
  }

  return path;
}

function selectedCableDescription(group, label) {
  return fieldDescription(group?.cableSchema, group?.cableFields || {}, [label]);
}

function cableDiameterMM(group) {
  const description = selectedCableDescription(group, "Cordage Diameter");
  const match = description.match(/(\d+(?:\.\d+)?)\s*mm/i);
  return match ? Number(match[1]) : null;
}

function cableFiberCount(group) {
  if (group?.assemblyMode === "breakout") return breakoutTotalFibers(group);
  const description = selectedCableDescription(group, "Fiber Count");
  return (
    Number((description.match(/\d+/) || [])[0]) ||
    Math.max(1, group?.endpointA?.fibers || group?.endpointB?.fibers || 1)
  );
}

function breakoutEndLabels(group, side = "a") {
  const spec = normalizeBreakout(group);
  let labels = Array.from({ length: spec.branchCount }, (_, index) =>
    spec.labelMode === "alpha" ? String.fromCharCode(65 + (index % 26)) : String(index + 1)
  );

  if (side === "b") {
    if (spec.polarity === "B" || spec.labelMode === "reverse-b") labels = [...labels].reverse();
    if (spec.polarity === "C") {
      labels = labels.map((_, index) => labels[index % 2 === 0 ? Math.min(index + 1, labels.length - 1) : index - 1]);
    }
  }

  return labels;
}

function maxLegDiameterForConnector(kind) {
  if (kind === "mdc") return 1.6;
  if (kind === "lc" || kind === "lc-simplex") return 2.0;
  if (kind === "sc") return 5.0;
  return null;
}

function cableCompatibility(group, lengthM = null) {
  if (!group) return { errors: [], warnings: [], requiresCustom: false };

  const errors = [];
  const warnings = [];
  const mode = group.assemblyMode === "breakout" ? "breakout" : "jumper";
  const a = group.endpointA || {};
  const b = group.endpointB || {};
  const fiberCount = cableFiberCount(group);
  const mainDiameter = cableDiameterMM(group);
  const spec = normalizeBreakout(group);
  const legDiameter = mode === "breakout" ? spec.legDiameterMM : mainDiameter;
  let requiresCustom = false;

  [
    ["A", a, group.connectorStyleA],
    ["B", b, group.connectorStyleB],
  ].forEach(([label, endpoint, style]) => {
    const maximum = maxLegDiameterForConnector(endpoint.kind);
    if (maximum != null && legDiameter != null && legDiameter > maximum + 0.0001) {
      errors.push(
        `Connector ${label} (${endpoint.description || endpoint.kind}) is limited to ${maximum} mm cordage; the selected ${fmt(legDiameter)} mm leg is too large.`
      );
    }

    if (style === "uniboot") {
      if (endpoint.kind !== "lc") errors.push(`Connector ${label}: Uniboot is only available for LC duplex.`);
      if (mode === "jumper" && fiberCount !== 2) errors.push(`Connector ${label}: Uniboot requires a two-fiber cable.`);
      if (mode === "breakout" && spec.fibersPerLeg !== 2)
        errors.push(`Connector ${label}: Uniboot requires two fibers per breakout leg.`);
      requiresCustom = true;
      warnings.push(
        `Connector ${label}: the current ordering schema does not distinguish clipped LC duplex from Uniboot LC. A dedicated code or custom quote is required.`
      );
    }

    if (/\bMU\b|\bD4\b/i.test(endpoint.description || "")) {
      requiresCustom = true;
      warnings.push(
        `Connector ${label}: ${endpoint.description} is a legacy datasheet option and requires PLM confirmation.`
      );
    }
  });

  if (
    mode === "jumper" &&
    (a.kind === "mpo" || b.kind === "mpo") &&
    [11, 18].some((value) => Math.abs((mainDiameter || 0) - value) < 0.01)
  ) {
    errors.push(
      "11 mm and 18 mm cordage are not valid for a standard MPO jumper. Use a breakout/high-fiber assembly or select a smaller jumper diameter."
    );
  }

  if (mode === "breakout") {
    if (!schema(IDS.fanout)) errors.push("The multifiber fanout / breakout part-number schema is not available.");
    if (fiberCount >= 288 && mainDiameter != null && mainDiameter < 11)
      errors.push("Breakout assemblies with 288 or more fibers require at least an 11 mm central cable diameter.");
    const schemaFiberCount = selectedSchemaFiberCount(group);
    if (schemaFiberCount != null && schemaFiberCount !== spec.branchCount * spec.fibersPerLeg) {
      errors.push(
        `The selected B-series fiber count (${schemaFiberCount}) does not match ${spec.branchCount} branches × ${spec.fibersPerLeg} fibers (${spec.branchCount * spec.fibersPerLeg}).`
      );
    }
    if (spec.breakoutEnds === "both" && spec.legLengthAM + spec.legLengthBM >= (Number(lengthM) || Infinity))
      warnings.push(
        "The two breakout-leg lengths consume most or all of the finished assembly length. Review the central trunk length."
      );
    if (spec.staggered || spec.pullingGrip || spec.polarity === "custom" || spec.furcation === "custom")
      requiresCustom = true;
    warnings.push(
      "Breakout leg lengths, stagger, pulling grip, labels, and polarity are drawing-level requirements and are not encoded in the current B-series part number."
    );
  }

  if (Number.isFinite(lengthM) && lengthM > 0 && lengthM < 0.6)
    errors.push("Finished cable length must be at least 0.6 m when ordered in centimeter-scale lengths.");

  return { errors: [...new Set(errors)], warnings: [...new Set(warnings)], requiresCustom };
}

function cableOptionRule(group, field, option) {
  const description = String(option.description || "");

  if (/connector/i.test(field.label) && /\bMU\b|\bD4\b/i.test(description)) {
    return { disabled: true, reason: "legacy · use Others or PLM review" };
  }

  if (field.label === "Cordage Diameter") {
    const match = description.match(/(\d+(?:\.\d+)?)\s*mm/i);
    const diameter = match ? Number(match[1]) : null;
    const mode = group?.assemblyMode === "breakout" ? "breakout" : "jumper";
    const a = group?.endpointA || {};
    const b = group?.endpointB || {};

    if (diameter != null && mode === "jumper") {
      const limits = [maxLegDiameterForConnector(a.kind), maxLegDiameterForConnector(b.kind)].filter(Number.isFinite);
      const maximum = limits.length ? Math.min(...limits) : null;
      if (maximum != null && diameter > maximum)
        return { disabled: true, reason: `exceeds ${maximum} mm connector limit` };
      if ((a.kind === "mpo" || b.kind === "mpo") && [11, 18].includes(diameter))
        return { disabled: true, reason: "not for standard MPO jumpers" };
    }

    if (diameter != null && mode === "breakout" && breakoutTotalFibers(group) >= 288 && diameter < 11) {
      return { disabled: true, reason: "288+ fibers require ≥11 mm" };
    }
  }

  if (field.label === "Fiber Count" && group?.assemblyMode === "breakout") {
    const count = Number((description.match(/\d+/) || [])[0]);
    const total = breakoutTotalFibers(group);
    if (count && count !== total && !/other/i.test(description))
      return { disabled: true, reason: `breakout requires ${total} fibers` };
  }

  return {};
}

function resolveCablePart(group, lengthM) {
  const base = resolvePart(group.cableSchema, group.cableFields, lengthM);
  const compatibility = cableCompatibility(group, lengthM);
  const issues = [...base.issues, ...compatibility.errors, ...compatibility.warnings];

  return {
    code: compatibility.errors.length ? "" : base.code,
    candidate: base.code,
    issues: [...new Set(issues)],
    compatibility,
    signature: stable([group.assemblyMode, normalizeBreakout(group), group.connectorStyleA, group.connectorStyleB]),
  };
}

function assembliesPerRackForGroup(project, group, source) {
  if (group?.assemblyMode === "breakout" && normalizeBreakout(group).onePerPanel) {
    return Math.max(1, configFor(project, source).panelQty || 1);
  }
  return Math.max(1, Number(group?.cablesPerRack) || 1);
}

function breakoutAssemblyName(group) {
  if (group?.assemblyMode !== "breakout") return schema(group?.cableSchema)?.productName || "Cable assembly";
  const spec = normalizeBreakout(group);
  return `${schema(IDS.fanout)?.productName || "Breakout cable"} · ${spec.branchCount} × ${spec.fibersPerLeg}F legs`;
}

function baseConfig(id, name) {
  return {
    id,
    name,

    panelSchema: IDS.app,
    panelQty: 1,
    panelFields: seedDraft(IDS.app, {
      "Cassette Type": "P",
      "Fiber Type": "1",
      "Front Cassette Adapter Type": "L",
      "Polish / Contact": "6",
      "Input Adapter Type": "M",
    }),

    modulesPerPanel: 6,
    portsPerModule: 12,

    requiredPorts: 144,
    splitRatio: 1,
    autoSizeEnabled: true,

    separateModules: false,
    moduleFields: {},

    patchQty: 72,
    disabledPorts: [],
    patchM: 2,
    patchFields: defaultCableFields(IDS.patch),

    opticalPart: "",
    opticalQty: 0,

    portHeightM: 1.5,
    frameType: "7ft",
    customFrameRU: 40,
    customFrameHeightM: 2.134,
    mountingWidth: "23",

    existingPanel: false,
    existingPatch: false,
    existingOptics: false,
  };
}

function newPath(rowId = "all", targetId = "networkA") {
  return migratePath({
    id: uid("path"),
    name: "Rack group to network",
    sourceRowId: rowId,
    targetId,
    cablesPerRack: 1,
    connectionRole: "front",
    assemblyMode: "jumper",
    breakout: clone(BREAKOUT_DEFAULTS),
    connectorStyleA: "standard",
    connectorStyleB: "standard",
    cableSchema: IDS.patch,
    cableFields: defaultCableFields(IDS.patch),
    endpointA: { kind: "lc", fibers: 2, description: "LC/PC", apc: false, multimode: false },
    endpointB: { kind: "lc", fibers: 2, description: "LC/PC", apc: false, multimode: false },
    crossXM: 0,
    detourM: 0,
    fixedLengthM: null,
    roundM: 1,
    preferredM: "",
    attenuation: null,
    pairs: null,
    pairLoss: null,
    splices: 0,
    spliceLoss: 0,
    budget: null,
    existingTrunk: false,
  });
}

function initialProject() {
  const compute = baseConfig("compute", "Equipment rack");

  const network = baseConfig("network", "Network / distribution rack");

  compute.requiredPorts = 144;
  network.requiredPorts = 288;

  autoSizeConfig(compute, "");
  autoSizeConfig(network, "");

  return {
    version: 3,
    name: "Fiber infrastructure plan",
    configs: [compute, network],
    rows: [
      { id: "rowA", name: "Row A", count: 6, configId: "compute", offsetM: 0 },
      { id: "rowB", name: "Row B", count: 6, configId: "compute", offsetM: 0 },
    ],
    targets: [{ id: "networkA", name: "Network A", configId: "network", xM: -1.2, yM: 0 }],
    paths: [{ ...newPath(), id: "pathA", name: "Compute to Network A" }],
    rackOverrides: {},
    patchOverrides: {},
    runOverrides: {},
    routing: { rackPitchM: 0.6, rowPitchM: 2.4, pathway: "overhead", pathwayM: 3, slackM: 1, allowancePct: 0 },
  };
}

function validateProject(p) {
  if (
    !p ||
    p.version !== 3 ||
    !Array.isArray(p.rows) ||
    !Array.isArray(p.configs) ||
    !Array.isArray(p.targets) ||
    !Array.isArray(p.paths)
  )
    throw new Error("This is not a version 3 Solutions Builder project.");
  if (
    !p.rows.length ||
    !p.configs.length ||
    !p.targets.length ||
    !p.routing ||
    !p.rackOverrides ||
    !p.patchOverrides ||
    !p.runOverrides
  )
    throw new Error("The project is missing required data.");

  const configIds = new Set(p.configs.map((c) => c.id));

  if (
    configIds.size !== p.configs.length ||
    new Set(p.rows.map((r) => r.id)).size !== p.rows.length ||
    new Set(p.targets.map((t) => t.id)).size !== p.targets.length ||
    new Set(p.paths.map((g) => g.id)).size !== p.paths.length
  )
    throw new Error("Duplicate project identifiers.");

  const nonnegative = (value) => Number.isFinite(value) && value >= 0;

  if (
    p.rows.some(
      (r) => !Number.isSafeInteger(r.count) || r.count < 1 || !configIds.has(r.configId) || !Number.isFinite(r.offsetM)
    )
  )
    throw new Error("Invalid row quantity or configuration.");
  if (p.targets.some((t) => !configIds.has(t.configId) || !Number.isFinite(t.xM) || !Number.isFinite(t.yM)))
    throw new Error("Invalid destination.");

  if (
    p.paths.some(
      (g) =>
        !Number.isSafeInteger(g.cablesPerRack) ||
        g.cablesPerRack < 1 ||
        !p.targets.some((t) => t.id === g.targetId) ||
        (g.sourceRowId !== "all" && !p.rows.some((r) => r.id === g.sourceRowId)) ||
        !Number.isFinite(g.crossXM) ||
        !nonnegative(g.detourM) ||
        !Number.isFinite(g.roundM) ||
        !(g.roundM > 0) ||
        (g.fixedLengthM != null && (!Number.isFinite(g.fixedLengthM) || g.fixedLengthM <= 0))
    )
  )
    throw new Error("Invalid connection group.");

  [...p.configs, ...Object.values(p.rackOverrides)].forEach((c) => {
    if (
      ["panelQty", "modulesPerPanel", "portsPerModule", "patchQty", "opticalQty"].some(
        (key) => !Number.isSafeInteger(c[key]) || c[key] < 0
      ) ||
      !nonnegative(c.patchM) ||
      !Number.isFinite(c.portHeightM) ||
      !c.panelFields ||
      !c.patchFields ||
      !c.moduleFields
    )
      throw new Error("Invalid rack configuration.");
    if (
      c.facePlan &&
      (!Array.isArray(c.facePlan.rows) ||
        c.facePlan.rows.length > 12 ||
        c.facePlan.rows.some(
          (r) => !Array.isArray(r) || r.length > 24 || r.some((n) => !Number.isInteger(n) || n < 1 || n > 72)
        ))
    )
      throw new Error("Invalid cassette row plan.");
  });

  if (
    !Number.isFinite(p.routing.rackPitchM) ||
    !Number.isFinite(p.routing.rowPitchM) ||
    !(p.routing.rackPitchM > 0) ||
    !(p.routing.rowPitchM > 0) ||
    !nonnegative(p.routing.pathwayM) ||
    !nonnegative(p.routing.slackM) ||
    !nonnegative(p.routing.allowancePct)
  )
    throw new Error("Invalid routing settings.");

  Object.values(p.patchOverrides).forEach((value) => {
    if (!(value > 0) || !Number.isFinite(value)) throw new Error("Invalid patch-cord override.");
  });

  Object.values(p.runOverrides).forEach((value) => {
    if (["routeM", "orderM"].some((key) => value[key] != null && (!Number.isFinite(value[key]) || value[key] < 0)))
      throw new Error("Invalid cable override.");
  });

  return p;
}

function readSaved() {
  try {
    const value = localStorage.getItem(STORE_KEY);
    if (!value) return initialProject();

    const parsed = validateProject(JSON.parse(value));

    if (parsed.configs.some((c) => !c.facePlan)) {
      try {
        if (!localStorage.getItem(`${STORE_KEY}-before-faces`))
          localStorage.setItem(`${STORE_KEY}-before-faces`, value);
      } catch {
        /* Keep loading if backup space is unavailable. */
      }
    }

    return migrateProject(parsed);
  } catch {
    return initialProject();
  }
}

function configFor(p, rack) {
  return p.rackOverrides[rack.id] || p.configs.find((c) => c.id === rack.configId) || p.configs[0];
}

function sourceRack(p, row, index) {
  return {
    id: rackKey(row.id, index),
    label: `${row.name} / ${String(index).padStart(2, "0")}`,
    rowId: row.id,
    index,
    configId: row.configId,
    xM: row.offsetM + (index - 1) * p.routing.rackPitchM,
    yM: p.rows.findIndex((r) => r.id === row.id) * p.routing.rowPitchM,
    kind: "source",
  };
}

function destinationRack(target) {
  return { ...target, label: target.name, kind: "target" };
}

function rackExists(p, key) {
  if (p.targets.some((t) => t.id === key)) return true;

  const [rowId, index] = key.split("#");
  return p.rows.some((r) => r.id === rowId && Number(index) >= 1 && Number(index) <= r.count);
}

function pruneProject(p) {
  p.rackOverrides = Object.fromEntries(Object.entries(p.rackOverrides).filter(([key]) => rackExists(p, key)));
  p.patchOverrides = Object.fromEntries(
    Object.entries(p.patchOverrides).filter(([key]) => rackExists(p, key.split("|")[0]))
  );

  p.runOverrides = Object.fromEntries(
    Object.entries(p.runOverrides).filter(([key]) => {
      const [pathId, rackId, index] = key.split("|");
      const path = p.paths.find((g) => g.id === pathId);

      return (
        path &&
        rackExists(p, rackId) &&
        Number(index) <= path.cablesPerRack &&
        (path.sourceRowId === "all" || rackId.startsWith(`${path.sourceRowId}#`))
      );
    })
  );

  return p;
}

function cableRun(p, group, source, target, index) {
  const key = `${group.id}|${source.id}|${index}`;
  const override = p.runOverrides[key] || {};
  const overrideTarget = override.targetId ? p.targets.find((item) => item.id === override.targetId) : null;
  const actualTarget = overrideTarget ? destinationRack(overrideTarget) : target;
  const sourceConfig = configFor(p, source),
    targetConfig = configFor(p, actualTarget),
    r = p.routing;

  const horizontal =
    Math.abs(source.yM - actualTarget.yM) < 0.000001
      ? Math.abs(source.xM - actualTarget.xM)
      : Math.abs(source.xM - group.crossXM) +
        Math.abs(source.yM - actualTarget.yM) +
        Math.abs(actualTarget.xM - group.crossXM);
  const level = r.pathway === "underfloor" ? -r.pathwayM : r.pathwayM;
  const vertical = Math.abs(level - sourceConfig.portHeightM) + Math.abs(level - targetConfig.portHeightM);
  const calculatedRoute = horizontal + vertical + group.detourM;
  const route = override.routeM ?? calculatedRoute;
  const required = route * (1 + r.allowancePct / 100) + r.slackM * 2;

  const preferred = String(group.preferredM || "")
    .split(",")
    .map((s) => Number(s.trim()))
    .filter((v) => Number.isFinite(v) && v > 0)
    .sort((a, b) => a - b);
  const rounded = Math.ceil((required - 0.00000001) / group.roundM) * group.roundM;
  const preferredChoice = preferred.find((v) => v + 0.00000001 >= required);
  const recommended = preferredChoice ?? rounded;
  const ordered = override.orderM ?? group.fixedLengthM ?? recommended;
  const part = resolveCablePart(group, ordered);

  const fingerprint = stable({
    source: [source.xM, source.yM, sourceConfig.portHeightM],
    target: [actualTarget.xM, actualTarget.yM, targetConfig.portHeightM],
    r,
    cross: group.crossXM,
    detour: group.detourM,
    route,
    ordered,
    schema: group.cableSchema,
    fields: group.cableFields,
    assemblyMode: group.assemblyMode,
    breakout: normalizeBreakout(group),
    connectorStyleA: group.connectorStyleA,
    connectorStyleB: group.connectorStyleB,
  });

  const reviewed = override.reviewed === fingerprint;
  const tooShort = ordered + 0.000001 < required;

  return {
    key,
    groupId: group.id,
    groupName: group.name,
    source,
    target: actualTarget,
    index,
    horizontal,
    vertical,
    calculatedRoute,
    route,
    required,
    recommended,
    ordered,
    part,
    fingerprint,
    reviewed,
    tooShort,
    custom: preferred.length > 0 && preferredChoice == null,
    overridden: override.routeM != null || override.orderM != null,
    assemblyMode: group.assemblyMode,
    breakout: normalizeBreakout(group),
    compatibility: part.compatibility,
    status: tooShort
      ? "Too short"
      : part.compatibility?.errors?.length
        ? "Compatibility error"
        : !part.code
          ? "Ordering fields missing"
          : reviewed
            ? "Length reviewed"
            : "Estimated length",
  };
}

function evaluateProject(p) {
  const totalRacks = p.rows.reduce((sum, r) => sum + r.count, 0) + p.targets.length;
  const sourceRacks = p.rows.flatMap((row) => Array.from({ length: row.count }, (_, i) => sourceRack(p, row, i + 1)));
  const totalRuns = p.paths.reduce(
    (sum, group) =>
      sum +
      sourceRacks
        .filter((source) => group.sourceRowId === "all" || group.sourceRowId === source.rowId)
        .reduce((count, source) => count + assembliesPerRackForGroup(p, group, source), 0),
    0
  );

  if (totalRacks > LIMITS.racks || totalRuns > LIMITS.runs) {
    return {
      error: `Browser planning limit: ${LIMITS.racks.toLocaleString()} racks or ${LIMITS.runs.toLocaleString()} cable runs per project. Split this deployment into pods. No partial BOM was generated.`,
      totalRacks,
      totalRuns,
      racks: [],
      runs: [],
      bom: [],
      panels: 0,
      patches: 0,
      issues: [],
    };
  }

  const racks = sourceRacks.concat(p.targets.map(destinationRack));
  const runs = [];
  const groups = new Map();
  const cache = new Map();
  const issues = new Set();
  const faceCache = new Map();

  let panels = 0,
    patches = 0;

  function partFor(id, draft, length = null) {
    const key = stable([id, draft, length]);
    if (!cache.has(key)) cache.set(key, resolvePart(id, draft, length));
    return cache.get(key);
  }

  function add(kind, name, schemaId, draft, length, qty, reviewed = false, existing = false, resolved = null) {
    if (qty <= 0) return;

    const part = resolved || partFor(schemaId, draft, length);
    const key = stable([kind, schemaId, part.code || draft, length, existing, resolved?.signature]);

    const item = groups.get(key) || {
      key,
      kind,
      name,
      schemaId,
      part: part.code,
      length,
      qty: 0,
      estimated: 0,
      existing,
      issues: part.issues,
    };

    item.qty += qty;
    if (kind === "Trunk" && !reviewed) item.estimated += qty;

    groups.set(key, item);
  }

  const patchByRack = new Map();

  Object.entries(p.patchOverrides).forEach(([key, value]) => {
    const [rack, index] = key.split("|");
    if (!patchByRack.has(rack)) patchByRack.set(rack, []);
    patchByRack.get(rack).push([Number(index), value]);
  });

  for (const rack of racks) {
    const c = configFor(p, rack);
    const name = schema(c.panelSchema)?.productName || c.panelSchema;

    panels += c.panelQty;
    const assignedPatches = assignedPatchCount(c);
    patches += assignedPatches;

    let populatedAdapters = 0;

    for (let panelIndex = 1; panelIndex <= Math.max(0, c.panelQty); panelIndex++) {
      const face = faceModel(c, panelIndex);
      const order = panelOrder(c, panelIndex);
      const fields = panelFieldsFor(c, panelIndex);

      populatedAdapters += face.adapters;

      add("Panel", `${name} · ${face.ru}RU`, c.panelSchema, fields, null, 1, false, c.existingPanel, {
        ...order,
        signature: stable([face.plan, fields]),
      });

      if (c.separateModules && !face.isNemo) {
        cassetteBreakdown(c, panelIndex).forEach(({ ports, qty }) =>
          add(
            "Cassette",
            `${name} · ${ports}-adapter cassette`,
            c.panelSchema,
            c.moduleDrafts?.[ports] || c.moduleFields,
            null,
            qty,
            false,
            c.existingPanel,
            { ...modulePart(c, ports), signature: `${panelIndex}:${ports}` }
          )
        );
      }

      face.warnings.forEach((w) => issues.add(`${c.name} · panel ${panelIndex}: ${w}`));
    }

    const usedRU = totalPanelRU(c);
    const availableRU = frameCapacityRU(c);

    if (usedRU > availableRU) {
      issues.add(
        `${c.name}: ${usedRU}RU of panels exceeds the selected ${availableRU}RU frame capacity. The design is still allowed, but the frame or rack plan must be reviewed.`
      );
    }

    if (c.faceMappingReview)
      issues.add(`${c.name}: review the cassette layout and existing per-port length assignments.`);

    const overrides = (patchByRack.get(rack.id) || []).filter(([i]) => isPatchAssigned(c, i));

    add(
      "Patch cord",
      schema(IDS.patch)?.productName || "Patch cord",
      IDS.patch,
      c.patchFields,
      c.patchM,
      Math.max(0, assignedPatches - overrides.length),
      false,
      c.existingPatch
    );

    overrides.forEach(([, length]) =>
      add(
        "Patch cord",
        schema(IDS.patch)?.productName || "Patch cord",
        IDS.patch,
        c.patchFields,
        length,
        1,
        false,
        c.existingPatch
      )
    );

    if (c.opticalQty > 0) {
      const known = (schema(IDS.optics)?.knownPartNumbers || []).find((o) => o.code === c.opticalPart);

      add(
        "Transceiver",
        known?.description?.split(/[—–]/)[0]?.trim() || "Optical transceiver",
        IDS.optics,
        { knownPartNumber: c.opticalPart },
        null,
        c.opticalQty,
        false,
        c.existingOptics,
        { code: known?.code || "", issues: known ? [] : ["Select a known transceiver part number."] }
      );
    }

    if (assignedPatches > populatedAdapters) {
      issues.add(`${rack.label}: patch count exceeds the populated adapter ports.`);
    }

    if (c.separateModules) {
      const countField = schemaFields(c.panelSchema).find((f) => f.label === "Number of Cassettes");
      const countCode = countField && (c.panelFields[countField.key] ?? fixedValue(countField));
      const countText = countField?.options?.find?.((o) => o.code === countCode)?.description || "";

      if (countCode && !/empty/i.test(countText)) {
        issues.add(`${c.name}: separate modules may duplicate modules already included in the panel assembly.`);
      }
    }
  }

  for (const group of p.paths) {
    migratePath(group);
    if (
      String(group.preferredM || "")
        .split(",")
        .some((v) => v.trim() && (!Number.isFinite(Number(v)) || Number(v) <= 0))
    )
      issues.add(`${group.name}: preferred lengths must be positive meter values separated by commas.`);

    const compatibility = cableCompatibility(group, group.fixedLengthM);
    compatibility.errors.forEach((message) => issues.add(`${group.name}: ${message}`));
    compatibility.warnings.forEach((message) => issues.add(`${group.name}: ${message}`));

    const targetData = p.targets.find((t) => t.id === group.targetId);

    if (!targetData) {
      issues.add(`${group.name}: destination missing.`);
      continue;
    }

    const target = destinationRack(targetData);

    for (const source of racks.filter(
      (r) => r.kind === "source" && (group.sourceRowId === "all" || group.sourceRowId === r.rowId)
    )) {
      const assemblyCount = assembliesPerRackForGroup(p, group, source);
      for (let i = 1; i <= assemblyCount; i++) {
        const run = cableRun(p, group, source, target, i);
        runs.push(run);

        add(
          "Trunk",
          breakoutAssemblyName(group),
          group.cableSchema,
          group.cableFields,
          run.ordered,
          1,
          run.reviewed,
          !!group.existingTrunk,
          { ...run.part, signature: stable([run.part.signature, normalizeBreakout(group)]) }
        );
      }
    }
  }

  return {
    totalRacks,
    totalRuns,
    racks,
    runs,
    bom: [...groups.values()].sort((a, b) => a.kind.localeCompare(b.kind) || (a.length || 0) - (b.length || 0)),
    panels,
    patches,
    issues: [...issues],
  };
}

function groupCableRuns(runs) {
  const groups = new Map();

  runs.forEach((run) => {
    const key = stable([run.groupId, run.ordered, run.part.code]);

    const bucket = groups.get(key) || {
      key,
      groupId: run.groupId,
      groupName: run.groupName,
      length: run.ordered,
      part: run.part.code,

      qty: 0,
      reviewedQty: 0,
      tooShortQty: 0,
      orderingIssueQty: 0,

      sources: new Set(),
      destinations: new Set(),

      firstRun: run,
    };

    bucket.qty += 1;

    if (run.reviewed) {
      bucket.reviewedQty += 1;
    }

    if (run.tooShort) {
      bucket.tooShortQty += 1;
    }

    if (!run.part.code) {
      bucket.orderingIssueQty += 1;
    }

    bucket.sources.add(run.source.label);
    bucket.destinations.add(run.target.label);

    groups.set(key, bucket);
  });

  return [...groups.values()]
    .map((bucket) => ({
      ...bucket,

      sourceCount: bucket.sources.size,
      destinationCount: bucket.destinations.size,

      sources: [...bucket.sources],
      destinations: [...bucket.destinations],

      status: bucket.tooShortQty
        ? `${bucket.tooShortQty} too short`
        : bucket.orderingIssueQty
          ? `${bucket.orderingIssueQty} need ordering fields`
          : bucket.reviewedQty === bucket.qty
            ? "All lengths reviewed"
            : `${bucket.reviewedQty} of ${bucket.qty} reviewed`,
    }))
    .sort(
      (a, b) =>
        a.groupName.localeCompare(b.groupName) || a.length - b.length || String(a.part).localeCompare(String(b.part))
    );
}

function downloadText(name, text, type) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");

  a.href = url;
  a.download = name;
  a.click();

  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function csv(rows) {
  return (
    "\uFEFF" +
    rows
      .map((row) =>
        row
          .map((value) => {
            let text = String(value ?? "");
            if (/^[=+@-]/.test(text)) text = `'${text}`;
            return `"${text.replace(/"/g, '""')}"`;
          })
          .join(",")
      )
      .join("\r\n")
  );
}

function NumberInput({
  value,
  onChange,
  min = 0,
  integer = false,
  nullable = false,
  label,
  step = 1,
  id,
  max = Infinity,
}) {
  const [text, setText] = useState(value ?? "");
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setText(value ?? "");
    setInvalid(false);
  }, [value]);

  const commit = () => {
    if (text === "" && nullable) {
      onChange(null);
      setInvalid(false);
      return;
    }

    const next = Number(text);

    if (text === "" || !Number.isFinite(next) || next < min || next > max || (integer && !Number.isSafeInteger(next))) {
      setInvalid(true);
      return;
    }

    setInvalid(false);
    onChange(next);
  };

  return (
    <input
      id={id}
      className="sb-input"
      type="number"
      aria-label={label}
      aria-invalid={invalid}
      value={text}
      min={min}
      max={Number.isFinite(max) ? max : undefined}
      step={step}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
    />
  );
}

function Field({ label, hint, children }) {
  const id = useRef(uid("field")).current;
  const controlId = children?.props?.id || id;

  return (
    <div className="sb-field">
      <div className="sb-field-label">
        <label htmlFor={controlId}>{label}</label>
        {hint && <HelpTip text={hint} />}
      </div>
      {React.isValidElement(children) ? React.cloneElement(children, { id: controlId }) : children}
    </div>
  );
}

function Select({ value, onChange, options, label, id }) {
  return (
    <select id={id} className="sb-input" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o.id} value={o.id} disabled={!!o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Check({ value, onChange, children }) {
  return (
    <label className="sb-check">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <div className="sb-check-text">{children}</div>
    </label>
  );
}

function Section({ title, children, open = false }) {
  return (
    <details className="sb-section" open={open || undefined}>
      <summary>{title}</summary>
      <div className="sb-section-body">{children}</div>
    </details>
  );
}

function Note({ children, warn = false }) {
  return <div className={`sb-note${warn ? " is-warning" : ""}`}>{children}</div>;
}

function Stat({ label, value, unit }) {
  return (
    <div className="sb-stat">
      <div className="sb-stat-label">{label}</div>
      <div className="sb-stat-value">
        {value}
        {unit && <small>{unit}</small>}
      </div>
    </div>
  );
}

function SidebarHeader({ title, subtitle, action = null }) {
  return (
    <div className="sb-sidebar-header">
      <div>
        <h2>{title}</h2>
        {subtitle && <small>{subtitle}</small>}
      </div>
      {action}
    </div>
  );
}

function SidebarSectionHeader({ title, onAdd, addLabel = "Add" }) {
  return (
    <div className="sb-sidebar-section-header">
      <strong>{title}</strong>
      {onAdd && (
        <button type="button" className="sb-icon-control" aria-label={addLabel} title={addLabel} onClick={onAdd}>
          <Plus size={14} />
        </button>
      )}
    </div>
  );
}

function disabledPortSet(config) {
  return new Set((config.disabledPorts || []).map(Number).filter(Number.isInteger));
}

function isPatchAssigned(config, portNumber) {
  return portNumber > 0 && portNumber <= config.patchQty && !disabledPortSet(config).has(portNumber);
}

function assignedPatchCount(config) {
  const disabled = disabledPortSet(config);
  let count = 0;
  for (let i = 1; i <= Math.max(0, config.patchQty || 0); i++) if (!disabled.has(i)) count++;
  return count;
}

function panelSizingSignature(config, fields = config.panelFields) {
  return stable({
    panelSchema: config.panelSchema,
    requiredPorts: config.requiredPorts,
    autoSizeEnabled: config.autoSizeEnabled,
    frontMpoFibers: config.frontMpoFibers,
    mpoFiberCount: config.mpoFiberCount,
    ru: fieldValue(config.panelSchema, fields, RU_FIELDS),
    frontAdapter: fieldValue(config.panelSchema, fields, ADAPTER_FIELDS),
    cassettePorts: fieldValue(config.panelSchema, fields, ["Cassette Front Adapter Port Count"]),
    numberOfPorts: fieldValue(config.panelSchema, fields, ["Number of Ports"]),
  });
}

function SchemaEditor({ schemaId, draft, onChange, cable = false, hidden = [], only = null, optionRule = null }) {
  const fields = schemaFields(schemaId).filter(
    (f) =>
      !(cable && isLengthField(f)) && !fixedValue(f) && !hidden.includes(f.label) && (!only || only.includes(f.label))
  );

  if (!schema(schemaId)) return <Note warn>Schema not found: {schemaId}</Note>;

  return (
    <div className="sb-schema-fields">
      {fields.map((field) => {
        const value = draft[field.key] || "";
        const hasOptions = Array.isArray(field.options) && !field.allowNumeric && !field.allowAlphanumeric;

        const options = hasOptions
          ? [
              { id: "", label: "Choose an option" },
              ...field.options.map((option) => {
                const rule = optionRule?.(field, option, draft) || {};
                const selected = String(option.code) === String(value);
                return {
                  id: option.code,
                  label: `${option.code} · ${option.description}${rule.reason ? ` · ${rule.reason}` : ""}`,
                  disabled: !!rule.disabled && !selected,
                };
              }),
            ]
          : [];

        return (
          <Field
            key={field.key}
            label={`${field.label}${field.required ? "" : " (optional)"}`}
            hint={field.description}
          >
            {hasOptions ? (
              <Select value={value} onChange={(v) => onChange({ ...draft, [field.key]: v })} options={options} />
            ) : (
              <input
                className="sb-input"
                value={value}
                maxLength={field.indexes.length}
                placeholder={`${field.indexes.length} characters`}
                onChange={(e) => onChange({ ...draft, [field.key]: e.target.value.toUpperCase() })}
              />
            )}
          </Field>
        );
      })}
    </div>
  );
}

function ProductImage({ url, name }) {
  url = typeof url === "string" ? url : url?.url || url?.src || "";
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [url]);

  return (
    <div className="sb-product-image">
      {url && !failed ? <img src={url} alt={name} onError={() => setFailed(true)} /> : <Server size={22} />}
    </div>
  );
}

function PanelFamilyPicker({ value, onChange, options, image, name }) {
  const selected = options.find((option) => option.id === value);

  return (
    <label className="sb-panel-family-picker">
      <ProductImage url={image} name={name || selected?.label || "Panel family"} />
      <span className="sb-panel-family-picker-copy">
        <small>Panel family</small>
        <strong>{name || selected?.label || "Choose panel family"}</strong>
      </span>
      <ChevronDown size={17} />
      <select aria-label="Panel family" value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function RackGlyph({ selected = false, mode = "3D" }) {
  return (
    <svg className={`sb-rack-glyph${selected ? " is-selected" : ""}`} viewBox="0 0 54 94" aria-hidden="true">
      {mode === "3D" && (
        <>
          <path className="sb-rack-top" d="M8 14 22 5 47 14 33 23Z" />
          <path className="sb-rack-side" d="M33 23 47 14 47 77 33 86Z" />
        </>
      )}
      <path className="sb-rack-front" d={mode === "3D" ? "M8 14 33 23 33 86 8 77Z" : "M10 10H44V83H10Z"} />
      {[0, 1, 2, 3].map((i) => (
        <rect
          className="sb-rack-slot"
          key={i}
          x={mode === "3D" ? 12 : 15}
          y={26 + i * 12}
          width={mode === "3D" ? 15 : 24}
          height="6"
          rx="1"
        />
      ))}
    </svg>
  );
}

function Pager({ page, pages, onChange }) {
  return (
    pages > 1 && (
      <div className="sb-pager">
        <button type="button" disabled={page <= 0} onClick={() => onChange(page - 1)}>
          Previous
        </button>
        <div>
          {page + 1} / {pages}
        </div>
        <button type="button" disabled={page >= pages - 1} onClick={() => onChange(page + 1)}>
          Next
        </button>
      </div>
    )
  );
}

function activateSvg(event, action) {
  if (event.type === "click" || event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    event.stopPropagation();
    action();
  }
}

function AdapterGraphic({ adapter, width, assigned }) {
  const mid = width / 2;
  const duplexKinds = ["lc", "cs", "sn", "mdc"];
  const multiKinds = ["mpo", "mmc", "snmt"];

  return (
    <>
      <rect
        className={`sb-adapter-body ${adapter.color} ${adapter.kind}`}
        x="1"
        y="4"
        width={width - 2}
        height="19"
        rx="2"
      />
      <rect className="sb-adapter-tag" x="2" y="0" width={width - 4} height="6" rx="0.5" />

      {duplexKinds.includes(adapter.kind) ? (
        <>
          {[mid - 5, mid + 5].map((x, i) => (
            <g key={i}>
              <circle
                className={assigned ? "sb-ferrule-used" : "sb-ferrule-cap"}
                cx={x}
                cy="18"
                r={adapter.kind === "lc" ? 4 : 3.4}
              />
              <circle className="sb-ferrule-center" cx={x} cy="18" r="1.15" />
            </g>
          ))}
          {adapter.kind === "cs" && <path className="sb-vsff-latch" d={`M${mid - 6} 8h12l-2 4h-8Z`} />}
          {adapter.kind === "sn" && <path className="sb-vsff-latch" d={`M${mid - 4} 8h8v4h-8Z`} />}
          {adapter.kind === "mdc" && <path className="sb-vsff-latch" d={`M${mid - 5} 8h10v2h-10Z`} />}
        </>
      ) : adapter.kind === "sc" || adapter.kind === "lc-simplex" ? (
        <>
          <circle className={assigned ? "sb-ferrule-used" : "sb-ferrule-cap"} cx={mid} cy="18" r="5" />
          <circle className="sb-ferrule-center" cx={mid} cy="18" r="1.5" />
        </>
      ) : multiKinds.includes(adapter.kind) ? (
        <>
          <rect className="sb-mpo-opening" x={mid - 9} y="11" width="18" height="10" rx="1" />
          {Array.from({ length: 8 }, (_, i) => (
            <circle className="sb-mpo-dot" key={i} cx={mid - 6 + (i % 4) * 4} cy={14 + Math.floor(i / 4) * 4} r="0.8" />
          ))}
        </>
      ) : (
        <>
          <rect className="sb-generic-opening" x={mid - 6} y="12" width="12" height="9" rx="2" />
          <circle className="sb-ferrule-center" cx={mid} cy="16.5" r="1.5" />
        </>
      )}
    </>
  );
}

function PanelFace({
  config,
  panelIndex = 1,
  selectedPort = 0,
  selectedCassette = "",
  onPort,
  onCassette,
  onAddCassette,
  onClear,
  side = "front",
  compact = false,
}) {
  const face = faceModel(config, panelIndex);
  const fields = panelFieldsFor(config, panelIndex);
  const portOffset = panelPortOffset(config, panelIndex);
  const W = 960,
    left = 37,
    inner = 886,
    rowHeight = 38,
    top = 12,
    H = top + face.rows.length * rowHeight + 14;

  return (
    <div className={`sb-face-wrap${compact ? " compact" : ""}`}>
      <svg
        className="sb-real-panel"
        viewBox={`0 0 ${W} ${H}`}
        aria-label={`${schema(config.panelSchema)?.productName || "Panel"} ${side} face: ${face.adapters} adapter ports`}
        onClick={() => onClear?.()}
      >
        <rect className="sb-chassis" x="1" y="1" width={W - 2} height={H - 2} rx="6" />
        <path className="sb-mount-ear" d={`M1 3H25V${H - 3}H1Z M${W - 25} 3H${W - 1}V${H - 3}H${W - 25}Z`} />

        {[10, H / 2, H - 10].map((y, i) => (
          <g key={i}>
            <circle className="sb-mount-hole" cx="12" cy={y} r="3.6" />
            <circle className="sb-mount-hole" cx={W - 12} cy={y} r="3.6" />
          </g>
        ))}

        {face.rows.map((row, ri) => {
          const y = top + ri * rowHeight,
            unit = inner / Math.max(24, row.span);

          return (
            <g key={ri}>
              <rect className="sb-row-track" x={left - 3} y={y - 4} width={inner + 6} height="34" rx="2" />
              <text className="sb-face-row-label" x="29" y={y + 17} textAnchor="middle">
                {row.label}
              </text>

              {row.groups.map((g) => {
                const x = left + g.start * unit,
                  width = g.span * unit,
                  pitch = (width - 8) / g.ports;
                const select = () => onCassette?.(g.id);
                const isSelected = selectedCassette === g.id;
                const draft = config.moduleDrafts?.[g.ports] || config.moduleFields;
                const rearText = fieldDescription(config.panelSchema, config.separateModules ? draft : fields, [
                  "Inner Cable Options",
                ]);
                const rearMatch = rearText.match(/(\d+)\s*[x×]\s*(\d+)\s*F/i);
                const rearQty = rearMatch ? Math.min(8, Number(rearMatch[1])) : 0;

                return (
                  <g key={g.id}>
                    {!face.isNemo && (
                      <>
                        <rect
                          className={`sb-cassette-shell${isSelected ? " selected" : ""}`}
                          x={x + 1}
                          y={y - 3}
                          width={width - 2}
                          height="33"
                          rx="2"
                        />
                        <path d={`M${x + width - 5} ${y + 7}v12`} className="sb-cassette-handle" />
                        <g
                          className={`sb-cassette-selector${isSelected ? " selected" : ""}`}
                          role={onCassette ? "button" : undefined}
                          tabIndex={onCassette ? 0 : undefined}
                          onClick={(e) => activateSvg(e, select)}
                          onKeyDown={(e) => activateSvg(e, select)}
                        >
                          <title>{`Cassette ${g.cassette} · Row ${row.label} · ${g.ports} ${face.adapter.label} ports. Click this header to edit only this cassette.`}</title>
                          <rect x={x + 3} y={y - 3} width={Math.max(18, width - 8)} height="8" rx="1" />
                          <text className="sb-cassette-number" x={x + 7} y={y + 3}>
                            CAS {g.cassette}
                          </text>
                        </g>
                      </>
                    )}

                    {side === "front" ? (
                      g.items.map((item, pi) => {
                        const n = item.number,
                          globalPort = portOffset + (n || 0);
                        const assigned = n != null && isPatchAssigned(config, globalPort);
                        const selected = n != null && selectedPort === globalPort;
                        const aw = Math.max(8, Math.min(27, pitch - 3));
                        const px = x + 4 + pitch * pi + (pitch - aw) / 2;
                        const selectPort = () => {
                          if (item.populated) onPort?.(globalPort, g.id);
                        };

                        return (
                          <g
                            key={pi}
                            className={`sb-adapter${selected ? " selected" : ""}${assigned ? " assigned" : ""}`}
                            transform={`translate(${px},${y + 2})`}
                            role={item.populated && onPort ? "button" : undefined}
                            tabIndex={item.populated && onPort ? 0 : undefined}
                            onClick={(e) => activateSvg(e, selectPort)}
                            onKeyDown={(e) => activateSvg(e, selectPort)}
                          >
                            <title>{`${face.isNemo ? "Adapter carrier" : `Cassette ${g.cassette}`} · ${row.label}${String(g.start + pi + 1).padStart(2, "0")} · ${n ? `panel port ${n}, rack patch ${globalPort}` : "unpopulated"} · ${face.adapter.label}${face.adapter.fibers ? ` · ${face.adapter.fibers} fibers per port` : ""}${assigned ? " · patch assigned" : ""}`}</title>
                            {item.populated ? (
                              <>
                                <AdapterGraphic adapter={face.adapter} width={aw} assigned={assigned} />
                                <text className="sb-face-port-number" x={aw / 2} y="5" textAnchor="middle">
                                  {g.start + pi + 1}
                                </text>
                                {selected && (
                                  <rect
                                    className="sb-port-selected-ring"
                                    x="-1"
                                    y="-2"
                                    width={aw + 2}
                                    height="29"
                                    rx="3"
                                  />
                                )}
                              </>
                            ) : (
                              <rect className="sb-adapter-blank" x="1" y="4" width={aw - 2} height="20" rx="2" />
                            )}
                          </g>
                        );
                      })
                    ) : (
                      <g
                        className={`sb-rear-pick${isSelected ? " selected" : ""}`}
                        role={onCassette && !face.isNemo ? "button" : undefined}
                        tabIndex={onCassette && !face.isNemo ? 0 : undefined}
                        onClick={(e) => activateSvg(e, select)}
                        onKeyDown={(e) => activateSvg(e, select)}
                      >
                        {rearQty ? (
                          Array.from({ length: rearQty }, (_, i) => {
                            const rx = x + (width * (i + 1)) / (rearQty + 1) - 12;
                            return (
                              <g key={i}>
                                <rect className="sb-rear-interface" x={rx} y={y + 8} width="24" height="14" rx="2" />
                                <text className="sb-rear-text" x={rx + 12} y={y + 18} textAnchor="middle">
                                  {rearMatch[2]}F
                                </text>
                              </g>
                            );
                          })
                        ) : (
                          <>
                            <rect
                              className="sb-rear-unspecified"
                              x={x + width / 2 - 12}
                              y={y + 8}
                              width="24"
                              height="14"
                              rx="2"
                            />
                            <text className="sb-rear-text" x={x + width / 2} y={y + 18} textAnchor="middle">
                              SET
                            </text>
                          </>
                        )}
                        <title>
                          {rearQty
                            ? `${rearText}. F means fibers per internal leg, not meters. This preview does not confirm an external trunk connection.`
                            : "Rear configuration not specified. Click this cassette, then choose the rear interface in the left panel."}
                        </title>
                      </g>
                    )}
                  </g>
                );
              })}

              {row.span < 24 &&
                (() => {
                  const emptyX = left + row.span * unit + 2;
                  const emptyWidth = (24 - row.span) * unit - 4;

                  if (!compact && side === "front" && !face.isNemo && onAddCassette) {
                    return (
                      <g
                        className="sb-empty-cassette-add"
                        role="button"
                        tabIndex="0"
                        onClick={(event) => activateSvg(event, () => onAddCassette(ri))}
                        onKeyDown={(event) => activateSvg(event, () => onAddCassette(ri))}
                      >
                        <title>{`Add a cassette to row ${row.label}`}</title>
                        <rect x={emptyX} y={y - 2} width={emptyWidth} height="30" rx="2" />
                        <path
                          d={`M${emptyX + emptyWidth / 2 - 6} ${y + 13}h12M${emptyX + emptyWidth / 2} ${y + 7}v12`}
                        />
                      </g>
                    );
                  }

                  return (
                    <rect
                      className="sb-empty-cassette-space"
                      x={emptyX}
                      y={y - 2}
                      width={emptyWidth}
                      height="30"
                      rx="2"
                    />
                  );
                })()}
            </g>
          );
        })}
      </svg>

      {!compact && (
        <div className="sb-face-legend">
          {side === "rear" ? (
            <>
              <div>F = fiber count in the internal leg, not cable length</div>
              <div>SET = choose a rear/internal interface</div>
              <div>Click a cassette to configure only its rear options</div>
            </>
          ) : (
            <>
              <div>
                <i className="sb-key-cap" />
                Installed, no patch assigned
              </div>
              <div>
                <i className="sb-key-used" />
                Patch assigned
              </div>
              {!face.isNemo && (
                <div>
                  <i className="sb-key-cassette" />
                  Cassette header / boundary
                </div>
              )}
              <div>
                {face.adapter.kind === "lc"
                  ? "Two fibers = one duplex LC port"
                  : face.adapter.fibers
                    ? `${face.adapter.fibers} fibers per ${face.adapter.label} port`
                    : face.adapter.label}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PanelLayoutEditor({
  config,
  panelIndex = 1,
  onChange,
  selectedCassette,
  onSelectCassette,
  onDeleteCassette,
  context = "all",
}) {
  const face = faceModel(config, panelIndex),
    plan = face.plan;
  const [preset, setPreset] = useState(String(selectedCassettePortCount(config)));
  const [rowIndex, setRowIndex] = useState(0);
  const ri = Math.min(rowIndex, Math.max(0, plan.rows.length - 1));
  const [sr, ss] = selectedCassette ? selectedCassette.split(":").map(Number) : [NaN, NaN];
  const current = Number.isInteger(sr) && Number.isInteger(ss) ? face.rows[sr]?.groups[ss] : null;
  const adapterField = fieldFor(config.panelSchema, ADAPTER_FIELDS);
  const ruField = fieldFor(config.panelSchema, RU_FIELDS);
  const panelFields = panelFieldsFor(config, panelIndex);

  useEffect(
    () => setPreset(String(selectedCassettePortCount(config))),
    [
      config.panelSchema,
      panelIndex,
      fieldValue(config.panelSchema, config.panelFields, ["Cassette Front Adapter Port Count"]),
    ]
  );

  const saveRows = (rows) =>
    onChange({
      panelFacePlan: { panelIndex, facePlan: { ...plan, rows, legacyOverflow: 0 } },
      faceMappingReview: true,
    });
  const fill = (size) => Array(Math.max(1, Math.floor(24 / Math.min(24, size)))).fill(size);
  const sizes = appSizes();

  const changeOne = (size) => {
    if (!current) return;
    const rows = clone(plan.rows);
    rows[sr][ss] = size;
    saveRows(rows);
  };

  const applyPreset = (size) => {
    const numeric = Number(size);
    setPreset(String(numeric));
    saveRows(plan.rows.map(() => fill(numeric)));
  };

  if (context === "cassette") {
    if (!current) return <Note>Select a cassette header in the panel drawing.</Note>;

    return (
      <>
        <SidebarHeader
          title={`Cassette ${current.cassette}`}
          subtitle={`Row ${String.fromCharCode(65 + sr)} · ${current.ports} front adapter ports · Panel ${panelIndex}`}
          action={
            <button
              type="button"
              className="sb-icon-control danger"
              aria-label="Remove selected cassette"
              title="Remove selected cassette"
              onClick={() => onDeleteCassette?.(`${sr}:${ss}`)}
            >
              <Trash2 size={14} />
            </button>
          }
        />

        {config.panelSchema === IDS.app && (
          <Field label="Cassette size / front adapter ports">
            <Select
              value={String(current.ports)}
              onChange={(v) => changeOne(Number(v))}
              options={sizes.map((n) => ({
                id: String(n),
                label: `${n} adapter ports${APP_COMMON_SIZES.includes(n) ? "" : " · footprint review required"}`,
              }))}
            />
          </Field>
        )}

        {adapterField && (
          <Field label="Front adapter type">
            <Select
              value={fieldValue(config.panelSchema, config.panelFields, ADAPTER_FIELDS)}
              onChange={(v) =>
                onChange({ panelFields: putField(config.panelSchema, config.panelFields, ADAPTER_FIELDS, v) })
              }
              options={[
                { id: "", label: "Choose front adapter" },
                ...(adapterField.options || []).map((option) => ({ id: option.code, label: option.description })),
              ]}
            />
          </Field>
        )}

        {config.separateModules && (
          <Section title="Cassette ordering fields" open>
            <SchemaEditor
              schemaId={config.panelSchema}
              draft={config.moduleDrafts?.[current.ports] || config.moduleFields}
              hidden={[...RU_FIELDS, "Cassette Front Adapter Port Count", "Number of Cassettes"]}
              onChange={(draft) => onChange({ moduleDrafts: { ...config.moduleDrafts, [current.ports]: draft } })}
            />
          </Section>
        )}
      </>
    );
  }

  return (
    <>
      {ruField && config.autoSizeEnabled === false && (
        <Field label="Panel height">
          <Select
            value={String(panelRU(config, panelIndex))}
            onChange={(v) => onChange({ manualPanelRU: Number(v), faceMappingReview: true })}
            options={(ruField.options || [])
              .filter((o) => ["1", "2", "4"].includes(String(o.code)))
              .map((o) => ({ id: String(o.code), label: o.description }))}
          />
        </Field>
      )}

      {adapterField && (
        <Field label="Front adapter">
          <Select
            value={fieldValue(config.panelSchema, config.panelFields, ADAPTER_FIELDS)}
            onChange={(v) =>
              onChange({
                panelFields: putField(config.panelSchema, config.panelFields, ADAPTER_FIELDS, v),
                faceMappingReview: true,
              })
            }
            options={[
              { id: "", label: "Choose front adapter" },
              ...(adapterField.options || []).map((o) => ({ id: o.code, label: o.description })),
            ]}
          />
        </Field>
      )}

      {face.adapter.kind === "mpo" && (
        <Field label="Fibers per front MPO adapter">
          <Select
            value={String(config.frontMpoFibers || "")}
            onChange={(v) => onChange({ frontMpoFibers: v ? Number(v) : null })}
            options={[
              { id: "", label: "Not specified" },
              ...[8, 12, 16, 24].map((n) => ({ id: String(n), label: `${n} fibers` })),
            ]}
          />
        </Field>
      )}

      {face.isNemo ? (
        <>
          <Field label="Installed front adapters">
            <NumberInput
              value={plan.nemoPorts}
              min={0}
              integer
              max={72}
              onChange={(v) =>
                onChange({
                  panelFacePlan: { panelIndex, facePlan: { ...plan, nemoPorts: Math.min(72, v) } },
                  panelFields: putField(
                    config.panelSchema,
                    config.panelFields,
                    ["Number of Ports"],
                    String(v).padStart(3, "0")
                  ),
                  faceMappingReview: true,
                })
              }
            />
          </Field>
          <Note>NEMO uses direct adapter carriers rather than interchangeable APP/EVA cassettes.</Note>
        </>
      ) : config.panelSchema === IDS.classic ? (
        <>
          <Field label="Installed Classic trays (0–6)">
            <NumberInput
              value={face.cassettes}
              min={0}
              max={6}
              integer
              onChange={(v) => {
                let remaining = Math.min(6, v);
                const rows = plan.rows.map(() => {
                  const count = Math.min(2, remaining);
                  remaining -= count;
                  return Array(count).fill(12);
                });
                onChange({
                  panelFacePlan: { panelIndex, facePlan: { ...plan, rows, legacyOverflow: 0 } },
                  modulesPerPanel: v,
                  portsPerModule: 12,
                  panelFields: putField(config.panelSchema, config.panelFields, ["Number of Trays"], String(v || "X")),
                  faceMappingReview: true,
                });
              }}
            />
          </Field>
        </>
      ) : config.panelSchema === IDS.eva ? (
        <>
          <Field label={`Installed half-width cassettes (0–${plan.rows.length * 2})`}>
            <NumberInput
              value={face.cassettes}
              min={0}
              integer
              onChange={(v) => {
                let remaining = Math.min(v, plan.rows.length * 2);
                saveRows(
                  plan.rows.map(() => {
                    const n = Math.min(2, remaining);
                    remaining -= n;
                    return Array(n).fill(12);
                  })
                );
              }}
            />
          </Field>
        </>
      ) : (
        <>
          <Field label="Cassette-size preset">
            <Select
              value={preset}
              onChange={applyPreset}
              options={sizes.map((n) => ({
                id: String(n),
                label: `${n} adapter ports${APP_COMMON_SIZES.includes(n) ? "" : " · footprint review required"}`,
              }))}
            />
          </Field>

          <Field label="Cassette row">
            <Select
              value={String(ri)}
              onChange={(v) => setRowIndex(Number(v))}
              options={plan.rows.map((_, i) => ({ id: String(i), label: `Row ${String.fromCharCode(65 + i)}` }))}
            />
          </Field>

          <div className="sb-cassette-chips">
            {plan.rows[ri].map((n, i) => (
              <button
                key={i}
                className={selectedCassette === `${ri}:${i}` ? "selected" : ""}
                onClick={() => onSelectCassette(`${ri}:${i}`)}
              >
                {n} ports
              </button>
            ))}
          </div>

          <div className="sb-actions compact">
            <button
              className="sb-button"
              disabled={plan.rows[ri].length >= 24}
              onClick={() => saveRows(plan.rows.map((r, i) => (i === ri ? [...r, Number(preset)] : r)))}
            >
              <Plus size={12} />
              Cassette
            </button>
            <button className="sb-button" onClick={() => saveRows(plan.rows.map((r, i) => (i === ri ? [] : r)))}>
              Clear row
            </button>
          </div>
        </>
      )}

      {face.warnings.map((w) => (
        <Note key={w} warn>
          {w}
        </Note>
      ))}
    </>
  );
}

function RackPanel({ config, index, selected, selectedPort, onSelect, onOpen, onPort }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(index <= 4);

  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver((entries) => setVisible(entries[0].isIntersecting), {
      rootMargin: "250px",
    });
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  const f = faceModel(config, index);

  return (
    <div ref={ref} className={`sb-elevation-panel${selected ? " selected" : ""}`}>
      <div className="sb-elevation-panel-head">
        <button
          type="button"
          className="sb-panel-select"
          onClick={onSelect}
          onDoubleClick={onOpen}
          aria-pressed={selected}
        >
          <strong>Panel {index}</strong>
          <span>
            {f.ru}RU · {f.adapters} adapter ports
          </span>
        </button>
        <button type="button" className="sb-panel-edit" onClick={onOpen}>
          Edit panel
          <ArrowRight size={12} />
        </button>
      </div>

      {visible ? (
        <PanelFace
          config={config}
          panelIndex={index}
          selectedPort={selectedPort}
          onPort={onPort}
          onCassette={onSelect}
          compact
        />
      ) : (
        <svg className="sb-real-panel" viewBox={`0 0 960 ${26 + f.rows.length * 38}`} aria-hidden="true">
          <rect className="sb-chassis" width="960" height={26 + f.rows.length * 38} />
        </svg>
      )}
    </div>
  );
}

function cableFieldSelection(schemaId, draft, end = "a") {
  const fields = schemaFields(schemaId);

  const preferred =
    end === "a"
      ? [/Connector Type End 1/i, /Connector.*End A/i, /Connector A/i]
      : [/Connector Type End 2/i, /Connector.*End B/i, /Connector B/i];

  let field = fields.find((item) => preferred.some((pattern) => pattern.test(item.label)));

  if (!field) {
    const connectorFields = fields.filter((item) => /Connector Type End|Connector Type/i.test(item.label));

    field = end === "a" ? connectorFields[0] : connectorFields[1] || connectorFields[0];
  }

  if (!field) {
    return {
      code: "",
      description: "Not selected",
      kind: "generic",
      color: "gray",
    };
  }

  const raw = String(draft?.[field.key] ?? fixedValue(field));

  let code = raw;

  let description = Array.isArray(field.options)
    ? field.options.find((option) => String(option.code) === raw)?.description || ""
    : "";

  if (!description && raw && field.indexes.length > 1 && raw.length === field.indexes.length) {
    const offset = end === "a" ? 0 : field.indexes.length - 1;

    const position = schema(schemaId)?.positions?.find((item) => item.index === field.indexes[offset]);

    code = raw[offset] || raw;

    description = Array.isArray(position?.options)
      ? position.options.find((option) => String(option.code) === code)?.description || ""
      : "";
  }

  const text = description || raw || "Not selected";

  const kind = /MDC/i.test(text)
    ? "mdc"
    : /MPO/i.test(text)
      ? "mpo"
      : /\bCS\b/i.test(text)
        ? "cs"
        : /\bSC\b/i.test(text)
          ? "sc"
          : /\bLC\b/i.test(text)
            ? "lc"
            : /\bSN\b/i.test(text)
              ? "sn"
              : /\bFC\b/i.test(text)
                ? "fc"
                : /\bST\b/i.test(text)
                  ? "st"
                  : /\bMU\b/i.test(text)
                    ? "mu"
                    : "generic";

  return {
    code,
    description: text,
    kind,
  };
}

function cableVisualSpec(group) {
  const schemaId = group?.cableSchema || IDS.mpo;

  const draft = group?.cableFields || {};

  const fiber = fieldDescription(schemaId, draft, ["Fiber Type"]) || "Fiber type not selected";

  const fiberCountText = fieldDescription(schemaId, draft, ["Fiber Count"]) || "Fiber count not selected";

  const cordage = fieldDescription(schemaId, draft, ["Cordage Diameter"]) || "Cordage not selected";

  const jacket = fieldDescription(schemaId, draft, ["Jacket Rating"]) || "Jacket not selected";

  const schemaA = cableFieldSelection(schemaId, draft, "a");

  const schemaB = cableFieldSelection(schemaId, draft, "b");

  const a = group?.endpointA ? { ...schemaA, ...group.endpointA } : schemaA;

  const b = group?.endpointB ? { ...schemaB, ...group.endpointB } : schemaB;

  const multimode = /OM1|OM2|OM3|OM4|MMF|multimode/i.test(fiber);

  const fiberCount = Number((fiberCountText.match(/\d+/) || [])[0]) || (/Duplex/i.test(fiberCountText) ? 2 : 1);

  const colorFor = (connector) =>
    /APC|green/i.test(connector.description)
      ? "green"
      : multimode
        ? "aqua"
        : /MPO/i.test(connector.description)
          ? "yellow"
          : "blue";

  const breakout = normalizeBreakout(group);
  const assemblyMode = group?.assemblyMode === "breakout" ? "breakout" : "jumper";

  return {
    schemaId,
    assemblyMode,
    breakout,

    product:
      assemblyMode === "breakout"
        ? schema(IDS.fanout)?.productName || "Multifiber breakout assembly"
        : schema(schemaId)?.productName || "Cable assembly",

    a: {
      ...a,
      color: colorFor(a),
    },

    b: {
      ...b,
      color: colorFor(b),
    },

    fiber,
    fiberCountText:
      assemblyMode === "breakout"
        ? `${breakoutTotalFibers(group)} Fiber · ${breakout.branchCount} × ${breakout.fibersPerLeg}F legs`
        : fiberCountText,
    fiberCount: assemblyMode === "breakout" ? breakoutTotalFibers(group) : fiberCount,
    cordage,
    jacket,

    cableColor: multimode ? "aqua" : "yellow",
  };
}

function ConnectorGlyph({ connector, side, compact = false }) {
  const mirrored = side === "b";
  const kind = connector.kind || "generic";
  const color = connector.color || "gray";
  const duplex = ["lc", "cs", "sn", "mdc"].includes(kind);
  const multifiber = ["mpo", "mmc", "snmt"].includes(kind);
  const bodyY = duplex ? 12 : multifiber ? 17 : 18;
  const bodyH = duplex ? 48 : multifiber ? 38 : 36;

  return (
    <svg
      className={`sb-connector-svg ${kind} ${color} ${side}${compact ? " compact" : ""}`}
      viewBox="0 0 168 72"
      role="img"
      aria-label={`${connector.description} top view`}
    >
      <g transform={mirrored ? "translate(168 0) scale(-1 1)" : undefined}>
        <path className="sb-connector-cable-exit" d="M168 36H134" />
        <path className="sb-connector-strain" d="M136 24h-24L98 36l14 12h24z" />
        <rect className="sb-connector-shell" x="49" y={bodyY} width="64" height={bodyH} rx={multifiber ? 3 : 6} />
        <path
          className="sb-connector-side-detail"
          d={duplex ? "M58 20h44M58 28h44M58 44h44M58 52h44" : "M58 26h44M58 36h44M58 46h44"}
        />

        {kind === "lc" && <path className="sb-connector-latch" d="M67 12l7-7h16l7 7" />}
        {kind === "cs" && <path className="sb-connector-latch" d="M69 12l6-6h13l6 6" />}
        {kind === "sn" && <path className="sb-connector-latch" d="M72 12l4-6h9l4 6" />}
        {kind === "mdc" && <path className="sb-connector-latch" d="M73 12V6h14v6" />}

        {duplex ? (
          <>
            <rect className="sb-connector-nose" x="19" y="17" width="33" height="16" rx="3" />
            <rect className="sb-connector-nose" x="19" y="39" width="33" height="16" rx="3" />
            <rect className="sb-connector-dust-cap" x="5" y="19" width="17" height="12" rx="3" />
            <rect className="sb-connector-dust-cap" x="5" y="41" width="17" height="12" rx="3" />
            <path className="sb-connector-cap-tab" d="M6 22H1v6h5M6 44H1v6h5" />
          </>
        ) : multifiber ? (
          <>
            <rect className="sb-connector-nose" x="17" y="23" width="36" height="26" rx="2" />
            <path className="sb-connector-multifiber-key" d="M24 29h22v5H24zM24 39h22v5H24z" />
            <rect className="sb-connector-dust-cap" x="4" y="26" width="16" height="20" rx="3" />
            <path className="sb-connector-cap-tab" d="M5 29H1v14h4" />
          </>
        ) : (
          <>
            <rect className="sb-connector-nose" x="19" y="27" width="33" height="18" rx="4" />
            <rect className="sb-connector-dust-cap" x="5" y="29" width="17" height="14" rx="3" />
            <path className="sb-connector-cap-tab" d="M6 32H1v8h5" />
          </>
        )}
      </g>
    </svg>
  );
}

function BreakoutCrossSection({ fiberCount = 12, x = 0, y = 0, size = 84 }) {
  const shown = Math.min(24, Math.max(1, Number(fiberCount) || 1));
  const columns = Math.ceil(Math.sqrt(shown));
  const rows = Math.ceil(shown / columns);
  const spacing = 11;
  const width = columns * spacing + 22;
  const height = rows * spacing + 22;

  return (
    <svg
      className="sb-breakout-cross-section"
      x={x}
      y={y}
      width={size}
      height={size}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`${fiberCount} fiber cable cross section`}
    >
      <circle className="sb-breakout-cross-jacket" cx={width / 2} cy={height / 2} r={Math.min(width, height) / 2 - 3} />
      {Array.from({ length: shown }, (_, index) => {
        const col = index % columns;
        const row = Math.floor(index / columns);
        const x = 11 + col * spacing;
        const y = 11 + row * spacing;
        return <circle className={`sb-breakout-fiber fiber-${index % 12}`} key={index} cx={x} cy={y} r="3.2" />;
      })}
      {fiberCount > shown && (
        <text x={width / 2} y={height - 4} textAnchor="middle">
          +{fiberCount - shown}
        </text>
      )}
    </svg>
  );
}

function BreakoutAssemblyPreview({ group, run, quantity = 0, focus = null, onFocus, buckets = [] }) {
  const visual = cableVisualSpec(group);
  const spec = normalizeBreakout(group);
  const lengths = buckets.map((item) => Number(item.length)).filter((value) => Number.isFinite(value) && value > 0);
  const minLength = lengths.length ? Math.min(...lengths) : (run?.ordered ?? group?.fixedLengthM);
  const maxLength = lengths.length ? Math.max(...lengths) : minLength;
  const rangeLabel =
    minLength == null
      ? "Calculated length"
      : Math.abs(maxLength - minLength) < 0.000001
        ? `${fmt(minLength)} m`
        : `${fmt(minLength)}–${fmt(maxLength)} m`;
  const visible = Math.min(12, spec.branchCount);
  const labelsA = breakoutEndLabels(group, "a").slice(0, visible);
  const labelsB = breakoutEndLabels(group, "b").slice(0, visible);
  const top = 50;
  const gap = visible > 1 ? 230 / (visible - 1) : 0;
  const furcationA = 335;
  const furcationB = 665;
  const connectorXLeft = 55;
  const connectorXRight = 945;
  const toggle = (id) => onFocus?.(focus === id ? null : id);
  const sideA = spec.breakoutEnds === "both" || spec.breakoutEnds === "a";
  const sideB = spec.breakoutEnds === "both" || spec.breakoutEnds === "b";

  return (
    <div className="sb-breakout-preview">
      <div className="sb-cable-preview-head">
        <div>
          <small>{visual.product}</small>
          <strong>
            {spec.branchCount} × {spec.fibersPerLeg}F legs · {breakoutTotalFibers(group)}F central cable
          </strong>
        </div>
        <div className="sb-cable-quantity-badge">Qty {fmt(quantity)}</div>
      </div>

      <svg
        className="sb-breakout-drawing"
        viewBox="0 0 1000 365"
        role="img"
        aria-label={`${spec.branchCount} leg breakout cable assembly`}
        onClick={(event) => {
          if (event.target === event.currentTarget) onFocus?.(null);
        }}
      >
        <rect className="sb-breakout-sheet" x="1" y="1" width="998" height="363" rx="12" />
        <text className="sb-breakout-title" x="25" y="28">
          BREAKOUT CABLE ASSEMBLY
        </text>
        <text className="sb-breakout-meta" x="975" y="28" textAnchor="end">
          {spec.furcation === "kit-shrink"
            ? "Breakout kit + shrink tube"
            : BREAKOUT_FURCATIONS.find((item) => item.id === spec.furcation)?.label}
        </text>

        <g
          className={`sb-breakout-hotspot${focus === "cable" ? " active" : ""}`}
          role="button"
          tabIndex="0"
          onClick={(event) => activateSvg(event, () => toggle("cable"))}
          onKeyDown={(event) => activateSvg(event, () => toggle("cable"))}
        >
          <line className="sb-breakout-trunk-shadow" x1={furcationA + 22} y1="165" x2={furcationB - 22} y2="165" />
          <line className="sb-breakout-trunk" x1={furcationA + 22} y1="165" x2={furcationB - 22} y2="165" />
          <rect className="sb-breakout-length-badge" x="454" y="148" width="92" height="34" rx="17" />
          <text className="sb-breakout-length-text" x="500" y="170" textAnchor="middle">
            {rangeLabel}
          </text>
        </g>

        {spec.pullingGrip && (
          <g className="sb-breakout-pulling-grip" aria-label="Protective pulling grip">
            <rect x={furcationA + 46} y="137" width="115" height="56" rx="26" />
            <path
              d={`M${furcationA + 58} 142l90 46M${furcationA + 58} 188l90-46M${furcationA + 82} 139l66 52M${furcationA + 82} 191l66-52`}
            />
            <text x={furcationA + 103} y="210" textAnchor="middle">
              PULLING GRIP
            </text>
          </g>
        )}

        {[
          ["a", furcationA, spec.legLengthAM],
          ["b", furcationB, spec.legLengthBM],
        ].map(([side, x, length]) => (
          <g
            key={side}
            className={`sb-breakout-hotspot furcation${focus === "breakout" ? " active" : ""}`}
            role="button"
            tabIndex="0"
            onClick={(event) => activateSvg(event, () => toggle("breakout"))}
            onKeyDown={(event) => activateSvg(event, () => toggle("breakout"))}
          >
            <rect className="sb-breakout-kit" x={x - 22} y="145" width="44" height="40" rx="5" />
            <rect
              className="sb-breakout-shrink"
              x={side === "a" ? x + 18 : x - 42}
              y="154"
              width="24"
              height="22"
              rx="3"
            />
            <text className="sb-breakout-furcation-label" x={x} y="202" textAnchor="middle">
              Furcation {side.toUpperCase()}
            </text>
            <text className="sb-breakout-leg-length" x={x} y="217" textAnchor="middle">
              {fmt(length)} m legs
            </text>
          </g>
        ))}

        {sideA ? (
          labelsA.map((label, index) => {
            const y = top + index * gap;
            const stagger = spec.staggered ? (index % 4) * 10 : 0;
            const connectorX = connectorXLeft + stagger;
            return (
              <g
                key={`a-${index}`}
                className={`sb-breakout-hotspot leg${focus === "a" ? " active" : ""}`}
                role="button"
                tabIndex="0"
                onClick={(event) => activateSvg(event, () => toggle("a"))}
                onKeyDown={(event) => activateSvg(event, () => toggle("a"))}
              >
                <path
                  className="sb-breakout-leg"
                  d={`M${furcationA - 22} 165 C${furcationA - 95} 165 ${connectorX + 115} ${y} ${connectorX + 40} ${y}`}
                />
                <rect
                  className={`sb-breakout-leg-connector ${visual.a.color}`}
                  x={connectorX}
                  y={y - 9}
                  width="42"
                  height="18"
                  rx="3"
                />
                <rect className="sb-breakout-leg-cap" x={connectorX - 12} y={y - 7} width="14" height="14" rx="3" />
                <text className="sb-breakout-leg-label" x={connectorX + 21} y={y + 4} textAnchor="middle">
                  {label}
                </text>
              </g>
            );
          })
        ) : (
          <g
            className={`sb-breakout-hotspot${focus === "a" ? " active" : ""}`}
            role="button"
            tabIndex="0"
            onClick={(event) => activateSvg(event, () => toggle("a"))}
          >
            <line className="sb-breakout-leg" x1={furcationA - 22} y1="165" x2="110" y2="165" />
            <rect className="sb-breakout-single-end" x="70" y="150" width="44" height="30" rx="4" />
          </g>
        )}

        {sideB ? (
          labelsB.map((label, index) => {
            const y = top + index * gap;
            const stagger = spec.staggered ? (index % 4) * 10 : 0;
            const connectorX = connectorXRight - stagger;
            return (
              <g
                key={`b-${index}`}
                className={`sb-breakout-hotspot leg${focus === "b" ? " active" : ""}`}
                role="button"
                tabIndex="0"
                onClick={(event) => activateSvg(event, () => toggle("b"))}
                onKeyDown={(event) => activateSvg(event, () => toggle("b"))}
              >
                <path
                  className="sb-breakout-leg"
                  d={`M${furcationB + 22} 165 C${furcationB + 95} 165 ${connectorX - 115} ${y} ${connectorX - 40} ${y}`}
                />
                <rect
                  className={`sb-breakout-leg-connector ${visual.b.color}`}
                  x={connectorX - 42}
                  y={y - 9}
                  width="42"
                  height="18"
                  rx="3"
                />
                <rect className="sb-breakout-leg-cap" x={connectorX - 2} y={y - 7} width="14" height="14" rx="3" />
                <text className="sb-breakout-leg-label" x={connectorX - 21} y={y + 4} textAnchor="middle">
                  {label}
                </text>
              </g>
            );
          })
        ) : (
          <g
            className={`sb-breakout-hotspot${focus === "b" ? " active" : ""}`}
            role="button"
            tabIndex="0"
            onClick={(event) => activateSvg(event, () => toggle("b"))}
          >
            <line className="sb-breakout-leg" x1={furcationB + 22} y1="165" x2="890" y2="165" />
            <rect className="sb-breakout-single-end" x="886" y="150" width="44" height="30" rx="4" />
          </g>
        )}

        <g
          className={`sb-breakout-hotspot cross-section${focus === "cable" ? " active" : ""}`}
          role="button"
          tabIndex="0"
          onClick={(event) => activateSvg(event, () => toggle("cable"))}
          onKeyDown={(event) => activateSvg(event, () => toggle("cable"))}
        >
          <BreakoutCrossSection fiberCount={breakoutTotalFibers(group)} x={464} y={238} size={72} />
          <text className="sb-breakout-cross-label" x="500" y="318" textAnchor="middle">
            Cable cross-section
          </text>
        </g>

        <text className="sb-breakout-note" x="25" y="348">
          {spec.branchCount > visible
            ? `Showing ${visible} representative legs per breakout end · ${spec.branchCount} ordered`
            : `${spec.branchCount} labeled legs per breakout end`}{" "}
          · Polarity {spec.polarity} · {spec.staggered ? `${fmt(spec.staggerStepMM)} mm stagger` : "equal leg lengths"}
          {spec.pullingGrip ? " · pulling grip" : ""}
        </text>
      </svg>
    </div>
  );
}

function BreakoutMiniGraphic({ group, ratio = 1 }) {
  const spec = normalizeBreakout(group);
  const shown = Math.min(5, spec.branchCount);
  return (
    <svg
      className="sb-breakout-mini"
      viewBox="0 0 520 54"
      aria-hidden="true"
      style={{ width: `${Math.max(42, ratio * 100)}%` }}
    >
      <line className="sb-breakout-mini-trunk" x1="165" y1="27" x2="355" y2="27" />
      <rect className="sb-breakout-mini-kit" x="150" y="18" width="26" height="18" rx="3" />
      <rect className="sb-breakout-mini-kit" x="344" y="18" width="26" height="18" rx="3" />
      {Array.from({ length: shown }, (_, index) => {
        const y = 7 + index * 10;
        return (
          <g key={index}>
            <path className="sb-breakout-mini-leg" d={`M150 27 C115 27 105 ${y} 72 ${y}`} />
            <rect className="sb-breakout-mini-connector" x="52" y={y - 4} width="22" height="8" rx="2" />
            <path className="sb-breakout-mini-leg" d={`M370 27 C405 27 415 ${y} 448 ${y}`} />
            <rect className="sb-breakout-mini-connector" x="446" y={y - 4} width="22" height="8" rx="2" />
          </g>
        );
      })}
    </svg>
  );
}

function CableAssemblyPreview({ group, run, quantity = 0, focus = null, onFocus, buckets = [] }) {
  if (group?.assemblyMode === "breakout")
    return (
      <BreakoutAssemblyPreview
        group={group}
        run={run}
        quantity={quantity}
        focus={focus}
        onFocus={onFocus}
        buckets={buckets}
      />
    );
  const visual = cableVisualSpec(group);
  const lengths = buckets.map((item) => Number(item.length)).filter((value) => Number.isFinite(value) && value > 0);
  const minLength = lengths.length ? Math.min(...lengths) : (run?.ordered ?? group?.fixedLengthM);
  const maxLength = lengths.length ? Math.max(...lengths) : minLength;
  const rangeLabel =
    minLength == null
      ? "Calculated length"
      : Math.abs(maxLength - minLength) < 0.000001
        ? `${fmt(minLength)} m`
        : `${fmt(minLength)}–${fmt(maxLength)} m`;

  const toggle = (id) => onFocus?.(focus === id ? null : id);

  return (
    <div className="sb-cable-assembly-preview">
      <div className="sb-cable-preview-head">
        <div>
          <small>{visual.product}</small>
          <strong>
            {visual.a.description} → {visual.b.description}
          </strong>
        </div>
        <div className="sb-cable-quantity-badge">Qty {fmt(quantity)}</div>
      </div>

      <div
        className="sb-cable-assembly-art"
        aria-label={`${visual.a.description} to ${visual.b.description} cable preview`}
      >
        <button
          type="button"
          className={`sb-cable-hotspot connector${focus === "a" ? " active" : ""}`}
          onClick={() => toggle("a")}
        >
          <ConnectorGlyph connector={visual.a} side="a" />
        </button>

        <button
          type="button"
          className={`sb-cable-hotspot cable${focus === "cable" ? " active" : ""}`}
          onClick={() => toggle("cable")}
        >
          <div className={`sb-cable-jacket ${visual.cableColor}`}>
            <i />
            <span>{rangeLabel}</span>
            <small>{visual.fiberCountText} inside one cable jacket</small>
          </div>
        </button>

        <button
          type="button"
          className={`sb-cable-hotspot connector${focus === "b" ? " active" : ""}`}
          onClick={() => toggle("b")}
        >
          <ConnectorGlyph connector={visual.b} side="b" />
        </button>
      </div>

      <div className="sb-cable-preview-labels">
        <button type="button" className={focus === "a" ? "active" : ""} onClick={() => toggle("a")}>
          <small>Connector A</small>
          <strong>{visual.a.description}</strong>
        </button>
        <button type="button" className={focus === "cable" ? "active" : ""} onClick={() => toggle("cable")}>
          <small>Construction</small>
          <strong>
            {visual.fiberCountText} · {visual.cordage} · {visual.jacket}
          </strong>
        </button>
        <button type="button" className={focus === "b" ? "active" : ""} onClick={() => toggle("b")}>
          <small>Connector B</small>
          <strong>{visual.b.description}</strong>
        </button>
      </div>
    </div>
  );
}

function CableVariantScale({ buckets, visual, group, onSelect }) {
  const maxLength = Math.max(1, ...buckets.map((bucket) => Number(bucket.length) || 0));

  return (
    <div className="sb-cable-variant-scale">
      {buckets.map((bucket) => {
        const ratio = Math.max(0.14, (Number(bucket.length) || 0) / maxLength);

        return (
          <button
            type="button"
            key={bucket.key}
            className="sb-cable-scale-row"
            onClick={() => onSelect(bucket.firstRun)}
          >
            <div className="sb-cable-scale-copy">
              <strong>
                {fmt(bucket.length)} m · {bucket.part || "Ordering fields required"}
              </strong>
              <small>
                {bucket.sourceCount} source location(s) → {bucket.destinationCount} destination(s) · {bucket.status}
              </small>
            </div>

            <div
              className="sb-cable-scale-graphic"
              aria-label={`${fmt(bucket.length)} meter cable, quantity ${fmt(bucket.qty)}`}
            >
              <div className="sb-cable-scale-track">
                {group?.assemblyMode === "breakout" ? (
                  <BreakoutMiniGraphic group={group} ratio={ratio} />
                ) : (
                  <div className="sb-cable-scale-assembly" style={{ width: `${Math.max(42, ratio * 100)}%` }}>
                    <ConnectorGlyph connector={visual.a} side="a" compact />
                    <span className={`sb-cable-scale-line ${visual.cableColor}`} />
                    <ConnectorGlyph connector={visual.b} side="b" compact />
                  </div>
                )}
              </div>
            </div>

            <b>× {fmt(bucket.qty)}</b>
          </button>
        );
      })}
    </div>
  );
}

function CablePathSchematic({ project, runs, selectedRun, onSelectRun }) {
  const visible = runs.slice(0, 60);
  const rowIndex = new Map(project.rows.map((row, index) => [row.id, index]));
  const W = 1120;
  const H = Math.max(330, 150 + project.rows.length * 92);
  const sourceX = 155;
  const targetX = 950;
  const rackStep = Math.max(18, Math.min(38, 650 / Math.max(1, ...project.rows.map((row) => row.count))));

  return (
    <div className="sb-cable-path-card">
      <svg viewBox={`0 0 ${W} ${H}`} className="sb-cable-path-svg" role="img" aria-label="Cable path schematic">
        <defs>
          <marker id="sb-cable-path-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <path d="M0 0L7 3.5L0 7Z" />
          </marker>
        </defs>
        <rect x="0" y="0" width={W} height={H} rx="12" className="sb-cable-path-bg" />
        <text x="28" y="34" className="sb-cable-path-title">
          CABLE PATH SCHEMATIC
        </text>
        <text x={W - 28} y="34" textAnchor="end" className="sb-cable-path-meta">
          {runs.length} physical assemblies · click a path to inspect
        </text>

        {project.rows.map((row, index) => {
          const y = 86 + index * 92;
          return (
            <g key={row.id}>
              <text x="30" y={y + 8} className="sb-cable-path-row-label">
                {row.name}
              </text>
              <line x1={sourceX - 25} y1={y} x2={720} y2={y} className="sb-cable-path-row-line" />
              {Array.from({ length: Math.min(row.count, 26) }, (_, i) => {
                const x = sourceX + i * rackStep;
                return (
                  <g key={i}>
                    <rect x={x - 9} y={y - 15} width="18" height="30" rx="2" className="sb-cable-path-rack" />
                    <text x={x} y={y + 28} textAnchor="middle" className="sb-cable-path-rack-label">
                      {String.fromCharCode(65 + index)}
                      {String(i + 1).padStart(2, "0")}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}

        <rect x={targetX - 76} y={H / 2 - 44} width="152" height="88" rx="6" className="sb-cable-path-target" />
        <text x={targetX} y={H / 2 - 5} textAnchor="middle" className="sb-cable-path-target-title">
          Distribution
        </text>
        <text x={targetX} y={H / 2 + 15} textAnchor="middle" className="sb-cable-path-target-sub">
          {project.targets.map((item) => item.name).join(" / ")}
        </text>

        {visible.map((item, index) => {
          const rIndex = rowIndex.get(item.source.rowId) || 0;
          const y = 86 + rIndex * 92;
          const x = sourceX + (Math.max(1, item.source.index) - 1) * rackStep;
          const bendX = 760 + (index % 7) * 10;
          const selected = item.key === selectedRun?.key;
          return (
            <polyline
              key={item.key}
              points={`${x},${y} ${bendX},${y} ${bendX},${H / 2} ${targetX - 76},${H / 2}`}
              className={`sb-cable-path-line${selected ? " selected" : ""}`}
              markerEnd="url(#sb-cable-path-arrow)"
              onClick={() => onSelectRun(item)}
            >
              <title>{`${item.source.label} → ${item.target.label} · ${fmt(item.ordered)} m`}</title>
            </polyline>
          );
        })}
      </svg>
      {runs.length > visible.length && (
        <small className="sb-cable-path-overflow">
          Showing the first {visible.length} of {runs.length} physical paths. The schedule and BOM retain every
          assembly.
        </small>
      )}
    </div>
  );
}

function CableConfiguratorView({
  group,
  run,
  groupRuns,
  buckets,
  project,
  optical,
  focus,
  onFocus,
  onSelectRun,
  onExport,
  paths,
  pathId,
  onPathChange,
  displayMode,
  onDisplayMode,
}) {
  if (!group)
    return (
      <div className="sb-center-card sb-cables-card">
        <Note>Add a cable group using the plus button in the left sidebar.</Note>
      </div>
    );

  const visual = cableVisualSpec(group);
  const groupBuckets = buckets.filter((bucket) => bucket.groupId === group.id);
  const selectedRunKey = run?.key || "";

  return (
    <div
      className="sb-center-card sb-cables-card"
      onClick={(event) => {
        if (!event.target.closest("button,input,select,summary,a,.sb-cable-path-line")) onFocus?.(null);
      }}
    >
      <div className="sb-cable-view-header">
        <div>
          <h2>Cables</h2>
          <p className="sb-muted">
            {group.name} · {fmt(groupRuns.length)} physical assemblies
          </p>
        </div>

        <div className="sb-cable-view-controls">
          <Select
            value={pathId}
            onChange={onPathChange}
            options={paths.map((item) => ({ id: item.id, label: item.name }))}
          />
          <Select
            value={selectedRunKey}
            onChange={(key) => {
              const item = groupRuns.find((candidate) => candidate.key === key);
              if (item) onSelectRun(item);
            }}
            options={
              groupRuns.length
                ? groupRuns.map((item) => ({
                    id: item.key,
                    label: `${item.source.label} → ${item.target.label} · ${fmt(item.ordered)} m`,
                  }))
                : [{ id: "", label: "No physical assemblies" }]
            }
          />
          <div className="sb-segment sb-cable-display-toggle">
            <button className={displayMode === "assembly" ? "active" : ""} onClick={() => onDisplayMode("assembly")}>
              Assembly
            </button>
            <button className={displayMode === "path" ? "active" : ""} onClick={() => onDisplayMode("path")}>
              Path
            </button>
          </div>
          <button className="sb-button" onClick={onExport}>
            <Download size={13} />
            Schedule
          </button>
        </div>
      </div>

      {displayMode === "path" ? (
        <CablePathSchematic project={project} runs={groupRuns} selectedRun={run} onSelectRun={onSelectRun} />
      ) : (
        <CableAssemblyPreview
          group={group}
          run={run}
          quantity={groupRuns.length}
          focus={focus}
          onFocus={onFocus}
          buckets={groupBuckets}
        />
      )}

      {displayMode === "assembly" && run && focus === "route" && (
        <button type="button" className="sb-cable-route-diagram active" onClick={() => onFocus?.(null)}>
          <div className="sb-route-endpoint">
            <strong>{run.source.label}</strong>
            <small>Source</small>
          </div>
          <div className="sb-route-segment">
            <span>{fmt(run.horizontal)} m horizontal</span>
          </div>
          <div className="sb-route-rise">
            <span>{fmt(run.vertical)} m vertical</span>
          </div>
          <div className="sb-route-segment">
            <span>{fmt(runGroupValue(group, "detourM"))} m detour</span>
          </div>
          <div className="sb-route-endpoint">
            <strong>{run.target.label}</strong>
            <small>Destination</small>
          </div>
        </button>
      )}

      {run && (
        <Note warn={run.tooShort || !run.part.code || !!run.compatibility?.errors?.length}>
          {run.status}. {run.part.code || run.part.issues[0]}
        </Note>
      )}
      {run?.compatibility?.warnings?.map((message) => (
        <Note key={message}>{message}</Note>
      ))}

      <div className={`sb-cable-quantity-list${focus === "variants" ? " active" : ""}`}>
        <div className="sb-view-head">
          <div>
            <h3>Required cable variants in this group</h3>
            <small>Finished lengths are shown proportionally. Equivalent assemblies are grouped and counted.</small>
          </div>
        </div>
        <CableVariantScale
          buckets={groupBuckets}
          visual={visual}
          group={group}
          onSelect={(item) => {
            onSelectRun(item);
            onFocus?.("variants");
          }}
        />
        {!groupBuckets.length && <Note>No physical cable runs are generated for this group yet.</Note>}
      </div>

      <div className="sb-route-assumption-summary">
        <strong>Routing model</strong>
        <span>
          {project.routing.pathway === "overhead" ? "Overhead tray" : "Underfloor pathway"} ·{" "}
          {fmt(project.routing.pathwayM)} m pathway level · {fmt(project.routing.slackM)} m service allowance per end ·{" "}
          {fmt(project.routing.allowancePct)}% route allowance
        </span>
      </div>

      <div className="sb-cable-summary-grid bottom">
        <Stat label="Assemblies" value={fmt(groupRuns.length)} />
        <Stat label="Selected route" value={run ? fmt(run.required) : "—"} unit={run ? "m" : ""} />
        <Stat label="Finished length" value={run ? fmt(run.ordered) : "—"} unit={run ? "m" : ""} />
        <Stat label="Fiber count" value={visual.fiberCountText} />
        {optical && (
          <>
            <Stat label="Estimated loss" value={fmt(optical.total)} unit="dB" />
            <Stat label="Budget margin" value={fmt(optical.margin)} unit="dB" />
          </>
        )}
      </div>
    </div>
  );
}

function runGroupValue(group, key) {
  const value = Number(group?.[key]);

  return Number.isFinite(value) ? value : 0;
}

function Layout2DMap({
  project,
  selectedId,
  selectedRowId,
  showLabels = true,
  onSelect,
  onSelectRow,
  onOpen,
  onClear,
}) {
  const W = 1240;
  const left = 150;
  const right = 330;
  const top = 74;
  const rowPitch = 92;
  const rackWidth = 25;
  const rackHeight = 42;
  const H = Math.max(330, top + project.rows.length * rowPitch + 78);
  const targetX = W - 190;
  const targetY = H / 2;
  const maxRackCount = Math.max(1, ...project.rows.map((row) => row.count));
  const rackStep = Math.max(30, Math.min(57, (W - left - right) / maxRackCount));

  return (
    <div className="sb-layout-map-card sb-layout-schematic-card">
      <svg
        className="sb-layout-map sb-layout-schematic"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Rack and distribution schematic"
        onClick={() => onClear?.()}
      >
        <defs>
          <marker id="sb-layout-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <path d="M0 0L7 3.5L0 7Z" />
          </marker>
        </defs>
        <rect className="sb-map-floor" x="0" y="0" width={W} height={H} rx="10" />
        <text className="sb-map-plan-title" x="28" y="34">
          RACK &amp; DISTRIBUTION PLAN
        </text>
        <text className="sb-map-plan-meta" x={W - 28} y="34" textAnchor="end">
          {project.rows.length} rows · {project.rows.reduce((sum, row) => sum + row.count, 0)} equipment racks ·{" "}
          {project.targets.length} distribution racks
        </text>

        {project.rows.map((row, rowPosition) => {
          const rowCode = String.fromCharCode(65 + (rowPosition % 26));
          const y = top + rowPosition * rowPitch;
          const config = project.configs.find((item) => item.id === row.configId);
          const rowSelected = selectedRowId === row.id;
          const visible = Math.min(row.count, 26);
          const lastX = left + (visible - 1) * rackStep;

          return (
            <g key={row.id} className={`sb-layout-schematic-row${rowSelected ? " selected" : ""}`}>
              <rect
                className="sb-layout-row-hit"
                x="18"
                y={y - 30}
                width={W - 36}
                height="72"
                rx="8"
                role="button"
                tabIndex="0"
                onClick={(event) => {
                  event.stopPropagation();
                  onSelectRow?.(row);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    event.stopPropagation();
                    onSelectRow?.(row);
                  }
                }}
              />
              <text className="sb-map-row-title" x="34" y={y - 2}>
                {rowCode} · {row.name}
              </text>
              <text className="sb-map-row-sub" x="34" y={y + 17}>
                {row.count} racks · {config?.name || "Rack configuration"}
              </text>
              <line className="sb-layout-row-spine" x1={left - 20} y1={y} x2={lastX + rackWidth + 20} y2={y} />

              {Array.from({ length: visible }, (_, index) => {
                const rack = sourceRack(project, row, index + 1);
                const x = left + index * rackStep;
                const rackLabel = `${rowCode}${String(index + 1).padStart(2, "0")}`;
                const selected = rack.id === selectedId;
                return (
                  <g
                    key={rack.id}
                    className={`sb-layout-rack-node${selected ? " selected" : ""}`}
                    role="button"
                    tabIndex="0"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelect(rack);
                    }}
                    onDoubleClick={(event) => {
                      event.stopPropagation();
                      onOpen(rack);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();
                        onSelect(rack);
                      }
                    }}
                  >
                    <title>{`${rackLabel} · ${rack.label}`}</title>
                    <rect x={x} y={y - rackHeight / 2} width={rackWidth} height={rackHeight} rx="3" />
                    <line x1={x + 4} y1={y - rackHeight / 2 + 7} x2={x + rackWidth - 4} y2={y - rackHeight / 2 + 7} />
                    <line x1={x + 4} y1={y + rackHeight / 2 - 7} x2={x + rackWidth - 4} y2={y + rackHeight / 2 - 7} />
                    {showLabels && (
                      <text
                        className="sb-layout-rack-label"
                        x={x + rackWidth / 2}
                        y={y + rackHeight / 2 + 16}
                        textAnchor="middle"
                      >
                        {rackLabel}
                      </text>
                    )}
                  </g>
                );
              })}

              {row.count > visible && (
                <text className="sb-map-more" x={lastX + rackWidth + 28} y={y + 4}>
                  +{row.count - visible}
                </text>
              )}

              {project.paths
                .filter((path) => path.sourceRowId === "all" || path.sourceRowId === row.id)
                .slice(0, 3)
                .map((path, index) => {
                  const targetIndex = Math.max(
                    0,
                    project.targets.findIndex((item) => item.id === path.targetId)
                  );
                  const endY = targetY + (targetIndex - (project.targets.length - 1) / 2) * 58;
                  const startX = lastX + rackWidth;
                  const bendX = W - right + 36 + index * 9;
                  return (
                    <polyline
                      key={`${path.id}:${row.id}`}
                      className="sb-layout-relationship"
                      markerEnd="url(#sb-layout-arrow)"
                      points={`${startX},${y} ${bendX},${y} ${bendX},${endY} ${targetX - 54},${endY}`}
                    />
                  );
                })}
            </g>
          );
        })}

        <g className="sb-layout-distribution">
          <rect
            x={targetX - 66}
            y={targetY - 55 - Math.max(0, project.targets.length - 1) * 29}
            width="132"
            height={110 + Math.max(0, project.targets.length - 1) * 58}
            rx="10"
          />
          <text
            x={targetX}
            y={targetY - 72 - Math.max(0, project.targets.length - 1) * 29}
            textAnchor="middle"
            className="sb-layout-distribution-label"
          >
            DISTRIBUTION
          </text>
          {project.targets.map((target, index) => {
            const rack = destinationRack(target);
            const y = targetY + (index - (project.targets.length - 1) / 2) * 58;
            const selected = rack.id === selectedId;
            return (
              <g
                key={rack.id}
                className={`sb-layout-target-node${selected ? " selected" : ""}`}
                role="button"
                tabIndex="0"
                onClick={(event) => {
                  event.stopPropagation();
                  onSelect(rack);
                }}
                onDoubleClick={(event) => {
                  event.stopPropagation();
                  onOpen(rack);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    event.stopPropagation();
                    onSelect(rack);
                  }
                }}
              >
                <rect x={targetX - 48} y={y - 19} width="96" height="38" rx="5" />
                <text x={targetX} y={y + 4} textAnchor="middle">
                  {target.name}
                </text>
              </g>
            );
          })}
        </g>
      </svg>

      <div className="sb-layout-map-legend">
        <span>
          <i className="rack" />
          Equipment rack
        </span>
        <span>
          <i className="target" />
          Network / distribution
        </span>
        <span>
          <i className="route" />
          Logical pathway relationship
        </span>
        <small>Planning schematic. Exact physical assemblies remain in Cables and the BOM.</small>
      </div>
    </div>
  );
}

function MetricBar({ label, value, detail }) {
  const active = Math.max(0, Math.min(5, Math.ceil(value / 20)));

  return (
    <div className="sb-profile-metric">
      <div className="sb-profile-metric-head">
        <span>{label}</span>
        <small>{detail}</small>
      </div>

      <div className="sb-profile-meter" aria-label={`${label}: ` + `${Math.round(value)} out of 100`}>
        {Array.from({ length: 5 }, (_, i) => (
          <i key={i} className={i < active ? "active" : ""} />
        ))}
      </div>
    </div>
  );
}

function configurationProfile(c, face, rackRuns) {
  const sizing = sizingFor(c);

  const split = Math.max(1, Number(c.splitRatio) || 1);

  const familyDensity =
    {
      [IDS.nemo]: 42,
      [IDS.eva]: 58,
      [IDS.classic]: 76,
      [IDS.app]: 92,
    }[c.panelSchema] || 55;

  const connectorAdjustment =
    face.adapter.kind === "sc" ? -14 : face.adapter.kind === "lc-simplex" ? -10 : face.adapter.kind === "mpo" ? 8 : 0;

  const density = Math.max(10, Math.min(100, familyDensity + connectorAdjustment));

  const connectorCost = face.adapter.kind === "mpo" ? 12 : ["lc-simplex", "sc"].includes(face.adapter.kind) ? -4 : 2;

  const cost = Math.max(10, Math.min(100, budgetTier(c.panelSchema).level * 20 + connectorCost));

  const capacity = Math.max(1, sizing.installedLines || face.fibers * c.panelQty || face.adapters * c.panelQty);

  const utilization = Math.max(0, Math.min(100, (sizing.requiredPorts / capacity) * 100));

  const rackEfficiency = Math.max(
    0,
    Math.min(100, (sizing.requiredRU / Math.max(1, sizing.panelQty * sizing.panelRU)) * 100)
  );

  const headroom = Math.max(0, 100 - utilization);

  const reviewed = rackRuns.length ? (rackRuns.filter((item) => item.reviewed).length / rackRuns.length) * 100 : 0;

  const splitScore = split === 1 ? 8 : Math.min(100, 20 + Math.log2(split) * 16);

  return {
    metrics: [
      {
        label: "Split ratio",
        value: splitScore,
        detail: split === 1 ? "1:1 · no passive split" : `1:${split}`,
      },
      {
        label: "Density",
        value: density,
        detail: `${fmt(sizing.linesPerRU)} ` + `lines / RU`,
      },
      {
        label: "Relative cost",
        value: cost,
        detail: `${budgetTier(c.panelSchema).label} ` + `· planning tier`,
      },
      {
        label: "Port utilization",
        value: utilization,
        detail: `${Math.round(utilization)}% assigned`,
      },
      {
        label: "Rack efficiency",
        value: rackEfficiency,
        detail: `${fmt(sizing.requiredRU)} RU required`,
      },
      {
        label: "Upgrade headroom",
        value: headroom,
        detail: `${fmt(Math.max(0, capacity - sizing.requiredPorts))} spare lines`,
      },
    ],

    capacity,

    spare: Math.max(0, capacity - sizing.requiredPorts),

    reviewed,
  };
}

const DEPLOYMENTS = [
  {
    id: "telecom",
    label: "Telecom / Broadband / Utilities",
    copy: "Traditional central-office, head-end, outside-plant and distribution applications.",
    panel: IDS.app,
    speed: "",
    defaultRows: 1,
    defaultRacks: 1,
    defaultPorts: 144,
  },
  {
    id: "enterprise",
    label: "Enterprise / Custom",
    copy: "Smaller customer-premises and single-rack deployments, generally 1–500 lines.",
    panel: IDS.eva,
    speed: "",
    defaultRows: 1,
    defaultRacks: 1,
    defaultPorts: 144,
  },
  {
    id: "datacenter",
    label: "Data Center",
    copy: "AI, GPU, cloud and hyperscale deployments. Higher-complexity system design.",
    panel: IDS.app,
    speed: "800G",
    defaultRows: 2,
    defaultRacks: 6,
    defaultPorts: 576,
  },
];

function savedProjectExists() {
  try {
    return Boolean(localStorage.getItem(STORE_KEY));
  } catch {
    return false;
  }
}

function HelpTip({ text }) {
  const [open, setOpen] = useState(false);
  const button = useRef(null),
    tip = useRef(null),
    id = useRef(uid("tip"));

  useLayoutEffect(() => {
    if (!open) return;

    const position = () => {
      if (!button.current || !tip.current) return;

      const rect = button.current.getBoundingClientRect();
      const width = Math.min(260, window.innerWidth - 24);
      const left = Math.max(12, Math.min(rect.left - 12, window.innerWidth - width - 12));

      tip.current.style.setProperty("--tip-x", `${left}px`);
      tip.current.style.setProperty("--tip-width", `${width}px`);

      const height = tip.current.getBoundingClientRect().height;
      const top =
        rect.bottom + 8 + height <= window.innerHeight - 12 ? rect.bottom + 8 : Math.max(12, rect.top - height - 8);

      tip.current.style.setProperty("--tip-y", `${top}px`);
    };

    const dismiss = (e) => {
      if (
        (e.type === "keydown" && e.key === "Escape") ||
        (e.type === "pointerdown" && !button.current?.contains(e.target) && !tip.current?.contains(e.target))
      )
        setOpen(false);
    };

    position();

    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", dismiss);

    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", dismiss);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        className="sb-help"
        ref={button}
        aria-label="More information"
        aria-describedby={open ? id.current : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen(true)}
      >
        <Info size={13} />
      </button>
      {open &&
        createPortal(
          <div className="sb-help-portal" ref={tip} role="tooltip" id={id.current}>
            {text}
          </div>,
          document.body
        )}
    </>
  );
}

function SmartStart({ project, hasSaved, onContinue, onCreate, onClose }) {
  const storedType = DEPLOYMENTS.some((item) => item.id === project.deploymentType)
    ? project.deploymentType
    : "telecom";

  const initialStarter = DEPLOYMENTS.find((item) => item.id === storedType) || DEPLOYMENTS[0];

  const [type, setType] = useState(storedType);

  const [name, setName] = useState(project.onboardingComplete ? "New fiber layout" : project.name);

  const [rows, setRows] = useState(project.onboardingComplete ? initialStarter.defaultRows : project.rows.length);

  const [racks, setRacks] = useState(
    project.onboardingComplete ? initialStarter.defaultRacks : project.rows[0]?.count || initialStarter.defaultRacks
  );

  const [lines, setLines] = useState(project.configs?.[0]?.requiredPorts ?? initialStarter.defaultPorts);

  const [speed, setSpeed] = useState(project.targetSpeed || initialStarter.speed || "");

  const [existing, setExisting] = useState({
    panels: false,
    patches: false,
    optics: false,
    trunks: false,
  });

  const [error, setError] = useState("");

  const dialog = useRef(null);

  const starter = DEPLOYMENTS.find((item) => item.id === type) || DEPLOYMENTS[0];

  const asksForLines = ["telecom", "enterprise"].includes(type);
  const isDataCenter = type === "datacenter";

  useEffect(() => {
    const previous = document.activeElement;

    dialog.current?.focus();

    return () => previous?.focus?.();
  }, []);

  const chooseType = (deployment) => {
    setType(deployment.id);
    setRows(deployment.defaultRows);
    setRacks(deployment.defaultRacks);
    setLines(deployment.defaultPorts);
    setSpeed(deployment.speed);
    setError("");
  };

  const keydown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }

    if (e.key !== "Tab") {
      return;
    }

    const focusable = [...dialog.current.querySelectorAll('button,input,select,[tabindex="0"]')].filter(
      (node) => !node.disabled
    );

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first?.focus();
    }
  };

  const build = () => {
    if (
      isDataCenter &&
      (!Number.isSafeInteger(rows) ||
        rows < 1 ||
        !Number.isSafeInteger(racks) ||
        racks < 1 ||
        rows * racks + 1 > LIMITS.racks)
    ) {
      setError(`Use positive whole numbers, up to ` + `${LIMITS.racks.toLocaleString()} ` + `total racks per project.`);

      return;
    }

    if (lines != null && (!Number.isSafeInteger(lines) || lines < 1)) {
      setError(
        "Required lines must be a positive " +
          "whole number, or leave the field " +
          "blank to use the suggested default."
      );

      return;
    }

    if (
      hasSaved &&
      !window.confirm(
        "Create a new project? This replaces " +
          "the current browser project. Use " +
          "Continue to keep it, or save a " +
          "project file first."
      )
    ) {
      return;
    }

    const requestedLines = lines == null ? starter.defaultPorts : lines;

    const next = initialProject();

    next.name = name.trim() || "Fiber layout";

    next.deploymentType = type;

    next.targetSpeed = speed;

    next.onboardingComplete = true;

    const startingRows = isDataCenter ? rows : 1;
    const startingRacks = isDataCenter ? racks : 1;

    next.rows = Array.from({ length: startingRows }, (_, i) => ({
      id: `row${i + 1}`,
      name: isDataCenter ? `Row ${i + 1}` : "Primary rack",
      count: startingRacks,
      configId: "compute",
      offsetM: 0,
    }));

    next.configs[0].name =
      type === "datacenter"
        ? "Compute / GPU rack"
        : type === "telecom"
          ? "Telecom equipment rack"
          : "Enterprise equipment rack";

    next.configs.forEach((c, index) => {
      c.panelSchema = schema(starter.panel) ? starter.panel : IDS.app;

      c.requiredPorts = index === 0 ? requestedLines : Math.max(starter.defaultPorts, requestedLines);

      c.splitRatio = 1;
      c.autoSizeEnabled = true;

      c.existingPanel = existing.panels;

      c.existingPatch = existing.patches;

      c.existingOptics = existing.optics;

      autoSizeConfig(c, speed);
    });

    next.paths[0].name = "Equipment to Network A";

    next.paths[0].existingTrunk = existing.trunks;

    const preferBreakout = type !== "enterprise" && requestedLines >= 48;
    next.paths[0].assemblyMode = preferBreakout ? "breakout" : "jumper";
    next.paths[0].connectionRole = preferBreakout ? "rear" : "front";
    next.paths[0].breakout = {
      ...clone(BREAKOUT_DEFAULTS),
      branchCount: Math.max(1, Math.min(24, Math.ceil(Math.min(requestedLines, 288) / 12))),
      onePerPanel: true,
    };
    migratePath(next.paths[0]);
    syncPatchAndCableInterfaces(next, next.configs[0]);
    syncPatchAndCableInterfaces(next, next.configs[1]);

    onCreate(next);
  };

  return (
    <div className="sb-onboard-shade" onKeyDown={keydown}>
      <div
        className="sb-onboard"
        ref={dialog}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sb-start-title"
      >
        <div className="sb-onboard-head">
          <div>
            <div className="sb-eyebrow">Start a project</div>

            <h2 id="sb-start-title">What are you building?</h2>

            <p>Choose the application, then give the builder the customer requirement.</p>
          </div>

          <button className="sb-close" aria-label="Close setup" onClick={onClose}>
            <X size={19} />
          </button>
        </div>

        <div className="sb-start-types">
          {DEPLOYMENTS.map((deployment) => (
            <button
              key={deployment.id}
              className={type === deployment.id ? "selected" : ""}
              onClick={() => chooseType(deployment)}
            >
              <strong>{deployment.label}</strong>

              <small>{deployment.copy}</small>
            </button>
          ))}
        </div>

        <Field label="Project name">
          <input className="sb-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>

        <div className={`sb-start-fields${isDataCenter ? "" : " sb-start-fields-compact"}`}>
          {asksForLines && (
            <Field
              label="Required fiber lines (optional)"
              hint={
                "A duplex LC adapter carries two fiber lines. " +
                "For example, 200 fiber lines require 100 duplex LC adapter ports."
              }
            >
              <NumberInput value={lines} nullable min={1} integer onChange={setLines} />
            </Field>
          )}

          {isDataCenter && (
            <>
              <Field label="Rows">
                <NumberInput value={rows} min={1} integer onChange={setRows} />
              </Field>

              <Field label="Racks per row">
                <NumberInput value={racks} min={1} integer onChange={setRacks} />
              </Field>
            </>
          )}

          <Field label="Target link speed">
            <Select
              value={speed}
              onChange={setSpeed}
              options={[
                { id: "", label: "Not decided" },
                ...["100G", "400G", "800G", "1.6T"].map((value) => ({ id: value, label: value })),
              ]}
            />
          </Field>
        </div>

        {asksForLines && (
          <div className="sb-start-sizing-preview">
            <strong>
              {fmt(lines == null ? starter.defaultPorts : lines)}
              {" requested fiber lines"}
            </strong>

            <small>
              Telecom and enterprise projects begin with one physical rack. The builder adds the required panel height,
              panel quantity, cassette loading and patch-cord count inside that rack. Add another rack only when the
              actual site requires it.
            </small>
          </div>
        )}

        <details className="sb-start-existing">
          <summary>Equipment you already have</summary>

          <div className="sb-start-checks">
            {[
              ["panels", "Panels / chassis"],
              ["patches", "Patch cords"],
              ["optics", "Transceivers"],
              ["trunks", "Trunk cables"],
            ].map(([key, label]) => (
              <Check
                key={key}
                value={existing[key]}
                onChange={(value) =>
                  setExisting((state) => ({
                    ...state,
                    [key]: value,
                  }))
                }
              >
                {label}
              </Check>
            ))}
          </div>
        </details>

        <p className="sb-start-note">
          The first result is a planning recommendation. Product compatibility, polarity and final engineering approval
          remain separate checks.
        </p>

        {error && <Note warn>{error}</Note>}

        <div className="sb-start-actions">
          {hasSaved && (
            <button className="sb-button" onClick={onContinue}>
              Continue current project
            </button>
          )}

          <button className="sb-button primary" onClick={build}>
            Build layout
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// True WebGL geometry. Rack positions use the same x/y values as cableRun().
function Layout3D({
  layoutKey,
  selectedId,
  tool,
  route,
  showCovers,
  showLabels,
  apiRef,
  onSelect,
  onClear,
  onOpen,
  onZoom,
  onFallback,
}) {
  const host = useRef(null),
    sceneRef = useRef(null),
    latest = useRef(null);
  const [error, setError] = useState("");

  latest.current = { selectedId, tool, route, onSelect, onClear, onOpen, onZoom };

  useEffect(() => {
    const node = host.current;
    if (!node) return;

    setError("");

    let renderer;

    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: "high-performance" });
    } catch {
      setError("3D could not start. Enable hardware acceleration or use the 2D overview.");
      return;
    }

    const css = getComputedStyle(node);
    const color = (key, fallback) => css.getPropertyValue(key).trim() || fallback;
    const scene = new THREE.Scene();

    scene.background = new THREE.Color(color("--sb-scene-bg", "#f5f7f9"));

    const camera = new THREE.PerspectiveCamera(38, 1, 0.05, 1000000);

    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;

    node.appendChild(renderer.domElement);

    const canvas = renderer.domElement;
    canvas.setAttribute("aria-label", "Interactive 3D rack layout");
    canvas.tabIndex = 0;

    const controls = new OrbitControls(camera, canvas);

    controls.enableDamping = true;
    controls.dampingFactor = 0.12;
    controls.minPolarAngle = 0.12;
    controls.maxPolarAngle = Math.PI / 2 - 0.05;
    controls.screenSpacePanning = true;
    controls.minDistance = 1.8;

    const layout = JSON.parse(layoutKey);

    const racks = layout.rows
      .flatMap((row, r) =>
        Array.from({ length: row.count }, (_, i) => ({
          id: rackKey(row.id, i + 1),
          rowId: row.id,
          index: i + 1,
          label: `${row.name} / ${String(i + 1).padStart(2, "0")}`,
          rackLabel: `${String.fromCharCode(65 + (r % 26))}${String(i + 1).padStart(2, "0")}`,
          kind: "source",
          xM: row.offsetM + i * layout.rackPitch,
          yM: r * layout.rowPitch,
          panelQty: row.panelQty || 0,
          panelRU: row.panelRU || 1,
          panelPlan: Array.isArray(row.panelPlan) ? row.panelPlan : [],
          frameCapacityRU: Number(row.frameCapacityRU) || 40,
          frameHeightM: Number(row.frameHeightM) || 2.134,
          mountingWidth: Number(row.mountingWidth) || 23,
        }))
      )
      .concat(
        layout.targets.map((t) => ({
          ...t,
          label: t.name,
          kind: "target",
          panelPlan: Array.isArray(t.panelPlan) ? t.panelPlan : [],
          frameCapacityRU: Number(t.frameCapacityRU) || 40,
          frameHeightM: Number(t.frameHeightM) || 2.134,
          mountingWidth: Number(t.mountingWidth) || 23,
        }))
      );

    const byId = new Map(racks.map((r, i) => [r.id, { ...r, instance: i }]));

    let minX = Infinity,
      maxX = -Infinity,
      minZ = Infinity,
      maxZ = -Infinity;

    racks.forEach((r) => {
      minX = Math.min(minX, r.xM);
      maxX = Math.max(maxX, r.xM);
      minZ = Math.min(minZ, r.yM - 1);
      maxZ = Math.max(maxZ, r.yM);
    });

    const originX = (minX + maxX) / 2,
      originZ = (minZ + maxZ) / 2;
    const width = Math.max(3, maxX - minX + 2.4),
      depth = Math.max(3, maxZ - minZ + 2.4);
    const geometries = new Set(),
      materials = new Set(),
      textures = new Set(),
      instances = [];

    const geo = (g) => {
      geometries.add(g);
      return g;
    };
    const mat = (m) => {
      materials.add(m);
      return m;
    };

    const box = geo(new THREE.BoxGeometry(1, 1, 1));
    const shell = mat(
      new THREE.MeshStandardMaterial({ color: color("--sb-scene-shell", "#e8edf1"), roughness: 0.45, metalness: 0.16 })
    );
    const metal = mat(
      new THREE.MeshStandardMaterial({ color: color("--sb-scene-rail", "#d6e0e7"), roughness: 0.4, metalness: 0.3 })
    );
    const faceMaterial = mat(
      new THREE.MeshPhysicalMaterial({
        color: "#ffffff",
        roughness: 0.48,
        metalness: 0.06,
        clearcoat: 0.7,
        clearcoatRoughness: 0.3,
      })
    );

    const dummy = new THREE.Object3D();
    const rackWidth = Math.min(0.58, Math.max(0.2, layout.rackPitch * 0.94));
    const detail = racks.length <= 2000;
    const rackHeightFor = (r) => Math.max(1.6, Math.min(3.05, Number(r.frameHeightM) || 2.134));
    const rackCenterYFor = (r) => 0.04 + rackHeightFor(r) / 2;

    function part(size, offset, material) {
      const mesh = new THREE.InstancedMesh(box, material, racks.length);
      const sizeFor = typeof size === "function" ? size : () => size;
      const offsetFor = typeof offset === "function" ? offset : () => offset;

      racks.forEach((r, i) => {
        const resolvedSize = sizeFor(r, i);
        const resolvedOffset = offsetFor(r, i, resolvedSize);
        dummy.position.set(
          r.xM - originX + resolvedOffset[0],
          resolvedOffset[1],
          r.yM - originZ - 0.5 + resolvedOffset[2]
        );
        dummy.rotation.set(0, 0, 0);
        dummy.scale.set(...resolvedSize);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
      });

      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingSphere();
      mesh.castShadow = detail;
      mesh.receiveShadow = detail;

      scene.add(mesh);
      instances.push(mesh);

      return mesh;
    }

    const bodies = part(
      (r) => [rackWidth, rackHeightFor(r), 1],
      (r) => [0, rackCenterYFor(r), 0],
      shell
    );

    const faces = part(
      (r) => [rackWidth - 0.1, Math.max(0.4, rackHeightFor(r) - 0.25), 0.018],
      (r) => [0, rackCenterYFor(r) + 0.02, 0.508],
      faceMaterial
    );

    const hitMaterial = mat(
      new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        depthWrite: false,
      })
    );

    const hitboxes = part(
      (r) => [rackWidth, rackHeightFor(r), 1],
      (r) => [0, rackCenterYFor(r), 0],
      hitMaterial
    );

    hitboxes.castShadow = false;
    hitboxes.receiveShadow = false;

    bodies.visible = showCovers;
    faces.visible = showCovers;

    const normalColor = new THREE.Color(color("--sb-scene-face", "#c9d6df"));

    const targetColor = new THREE.Color(color("--sb-scene-target", "#aebfc9"));

    const selectionColor = new THREE.Color(color("--sb-scene-selected", "#96ccd0"));

    racks.forEach((r, i) => faces.setColorAt(i, r.kind === "target" ? targetColor : normalColor));

    faces.instanceColor.needsUpdate = true;

    part([rackWidth + 0.015, 0.07, 1.015], (r) => [0, rackHeightFor(r) + 0.075, 0], metal);

    part([rackWidth + 0.025, 0.08, 1.025], [0, 0.04, 0], metal);

    if (detail) {
      part(
        (r) => [0.026, Math.max(0.4, rackHeightFor(r) - 0.2), 0.022],
        (r) => [-rackWidth / 2 + 0.036, rackCenterYFor(r) + 0.02, 0.524],
        metal
      );

      part(
        (r) => [0.026, Math.max(0.4, rackHeightFor(r) - 0.2), 0.022],
        (r) => [rackWidth / 2 - 0.036, rackCenterYFor(r) + 0.02, 0.524],
        metal
      );

      [0.18, 0.34, 0.5, 0.66, 0.82].forEach((fraction) =>
        part([rackWidth - 0.13, 0.017, 0.026], (r) => [0, 0.1 + rackHeightFor(r) * fraction, 0.53], metal)
      );
    }

    // Open-frame geometry is shown when cabinet covers are hidden. It models
    // front and rear mounting rails, side cable-management channels and
    // horizontal supports instead of leaving only a flat front panel plane.
    const openFrameMaterial = mat(
      new THREE.MeshStandardMaterial({
        color: color("--sb-scene-open-frame", "#aeb9c3"),
        roughness: 0.52,
        metalness: 0.38,
      })
    );
    const openFrameDark = mat(
      new THREE.MeshStandardMaterial({
        color: color("--sb-scene-open-frame-dark", "#2c3742"),
        roughness: 0.58,
        metalness: 0.28,
      })
    );
    const openFrameMeshes = [];
    const openPart = (size, offset, material = openFrameMaterial) => {
      const mesh = part(size, offset, material);
      mesh.visible = !showCovers;
      openFrameMeshes.push(mesh);
      return mesh;
    };

    if (detail) {
      // Rear uprights complete the four-post frame.
      openPart(
        (r) => [0.034, Math.max(0.4, rackHeightFor(r) - 0.16), 0.034],
        (r) => [-rackWidth / 2 + 0.045, rackCenterYFor(r) + 0.02, -0.48]
      );
      openPart(
        (r) => [0.034, Math.max(0.4, rackHeightFor(r) - 0.16), 0.034],
        (r) => [rackWidth / 2 - 0.045, rackCenterYFor(r) + 0.02, -0.48]
      );

      // Front and rear top/bottom cross-members.
      [0.12, 0.98].forEach((fraction) => {
        openPart([rackWidth - 0.08, 0.035, 0.035], (r) => [0, 0.04 + rackHeightFor(r) * fraction, 0.48]);
        openPart([rackWidth - 0.08, 0.035, 0.035], (r) => [0, 0.04 + rackHeightFor(r) * fraction, -0.48]);
      });

      // Side depth braces and vertical cable-management channels.
      [-1, 1].forEach((direction) => {
        [0.16, 0.5, 0.84].forEach((fraction) =>
          openPart([0.04, 0.035, 0.9], (r) => [
            direction * (rackWidth / 2 - 0.04),
            0.04 + rackHeightFor(r) * fraction,
            0,
          ])
        );
        openPart(
          (r) => [0.055, Math.max(0.4, rackHeightFor(r) - 0.38), 0.72],
          (r) => [direction * (rackWidth / 2 + 0.035), rackCenterYFor(r) + 0.02, 0],
          openFrameDark
        );
      });
    }

    const panelMaterial = mat(
      new THREE.MeshStandardMaterial({
        color: color("--sb-scene-panel", "#152233"),
        roughness: 0.5,
        metalness: 0.18,
      })
    );

    const maxPanelSlots = detail
      ? Math.min(
          80,
          Math.max(
            0,
            ...racks.map((r) => {
              const plan =
                Array.isArray(r.panelPlan) && r.panelPlan.length
                  ? r.panelPlan
                  : Array.from({ length: Number(r.panelQty) || 0 }, () => Number(r.panelRU) || 1);
              return plan.length;
            })
          )
        )
      : 0;

    for (let slot = 0; slot < maxPanelSlots; slot++) {
      const mesh = new THREE.InstancedMesh(box, panelMaterial, racks.length);

      racks.forEach((r, i) => {
        const plan =
          Array.isArray(r.panelPlan) && r.panelPlan.length
            ? r.panelPlan.map((value) => Math.max(1, Number(value) || 1))
            : Array.from({ length: Number(r.panelQty) || 0 }, () => Math.max(1, Number(r.panelRU) || 1));
        const visible = slot < plan.length;
        const capacity = Math.max(1, Number(r.frameCapacityRU) || 40);
        const ru = visible ? plan[slot] : 1;
        const usedBefore = plan.slice(0, slot).reduce((sum, value) => sum + value, 0);
        const usableFrameHeight = Math.max(0.8, rackHeightFor(r) - 0.36);
        const panelHeight = Math.max(0.018, usableFrameHeight * (ru / capacity));
        const widthFactor = Number(r.mountingWidth) === 19 ? 0.83 : 1;

        dummy.position.set(
          r.xM - originX,
          0.18 + usableFrameHeight * (usedBefore / capacity) + panelHeight / 2,
          r.yM - originZ + 0.015
        );
        dummy.rotation.set(0, 0, 0);
        dummy.scale.set(
          visible ? Math.max(0.05, (rackWidth - 0.12) * widthFactor) : 0.0001,
          visible ? panelHeight : 0.0001,
          visible ? 0.055 : 0.0001
        );
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
      });

      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingSphere();
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.visible = !showCovers;
      scene.add(mesh);
      instances.push(mesh);
    }

    const floor = new THREE.Mesh(
      geo(new THREE.PlaneGeometry(width, depth)),
      mat(
        new THREE.MeshStandardMaterial({ color: color("--sb-scene-floor", "#eef2f5"), roughness: 0.95, metalness: 0 })
      )
    );

    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.012;
    floor.receiveShadow = true;
    scene.add(floor);

    const gridPoints = [];
    const spacing = Math.max(1, Math.ceil(Math.max(width, depth) / 80));

    for (let x = -width / 2; x <= width / 2; x += spacing) gridPoints.push(x, 0.001, -depth / 2, x, 0.001, depth / 2);
    for (let z = -depth / 2; z <= depth / 2; z += spacing) gridPoints.push(-width / 2, 0.001, z, width / 2, 0.001, z);

    const gridGeo = geo(new THREE.BufferGeometry());
    gridGeo.setAttribute("position", new THREE.Float32BufferAttribute(gridPoints, 3));

    scene.add(
      new THREE.LineSegments(
        gridGeo,
        mat(
          new THREE.LineBasicMaterial({
            color: color("--sb-scene-grid", "#dfe6ed"),
            transparent: true,
            opacity: 0.7,
          })
        )
      )
    );

    scene.add(new THREE.HemisphereLight("#ffffff", "#bdc9d5", 2.5));

    const light = new THREE.DirectionalLight("#ffffff", 3.0);
    const span = Math.max(width, depth);

    light.position.set(-span * 0.5, span * 0.9, span * 0.7);
    scene.add(light);

    if (racks.length <= 500) {
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;

      light.castShadow = true;
      light.shadow.mapSize.set(2048, 2048);
      light.shadow.camera.left = -span / 2;
      light.shadow.camera.right = span / 2;
      light.shadow.camera.top = span / 2;
      light.shadow.camera.bottom = -span / 2;
      light.shadow.camera.far = span * 4 + 10;
      light.shadow.normalBias = 0.025;
      light.shadow.bias = -0.00015;
    }

    function label(text, x, y, z, { fontSize = 54, worldHeight = 0.27, maxWorldWidth = 1.3, paddingX = 22 } = {}) {
      const measure = document.createElement("canvas").getContext("2d");
      if (!measure) return;

      measure.font = `700 ${fontSize}px "Segoe UI", Arial, sans-serif`;
      const measured = Math.ceil(measure.measureText(text).width);
      const image = document.createElement("canvas");
      image.width = Math.max(92, Math.min(620, measured + paddingX * 2));
      image.height = 92;

      const ctx = image.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, image.width, image.height);
      ctx.beginPath();
      ctx.roundRect(3, 3, image.width - 6, image.height - 6, 14);
      ctx.fillStyle = "rgba(255,255,255,0.94)";
      ctx.fill();
      ctx.strokeStyle = "rgba(92,112,130,0.48)";
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.font = `700 ${fontSize}px "Segoe UI", Arial, sans-serif`;
      ctx.fillStyle = color("--sb-scene-label", "#445b70");
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, image.width / 2, image.height / 2 + 1, image.width - paddingX);

      const texture = new THREE.CanvasTexture(image);
      texture.colorSpace = THREE.SRGBColorSpace;
      textures.add(texture);

      const sprite = new THREE.Sprite(
        mat(new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false, depthTest: false }))
      );
      const proportionalWidth = Math.min(maxWorldWidth, worldHeight * (image.width / image.height));
      sprite.position.set(x - originX, y, z - originZ);
      sprite.scale.set(proportionalWidth, worldHeight, 1);
      sprite.renderOrder = 30;
      scene.add(sprite);
    }

    if (showLabels && racks.length <= 150) {
      racks
        .filter((item) => item.kind === "source")
        .forEach((item) =>
          label(item.rackLabel, item.xM, rackHeightFor(item) + 0.34, item.yM - 0.5, {
            fontSize: 50,
            worldHeight: 0.23,
            maxWorldWidth: 0.66,
            paddingX: 18,
          })
        );
      racks
        .filter((item) => item.kind === "target")
        .forEach((item) =>
          label(item.label, item.xM, rackHeightFor(item) + 0.42, item.yM - 0.5, {
            fontSize: 42,
            worldHeight: 0.31,
            maxWorldWidth: 1.35,
            paddingX: 24,
          })
        );
    } else if (showLabels && layout.rows.length <= 20) {
      layout.rows.forEach((item, i) =>
        label(item.name, item.offsetM - 0.85, 0.28, i * layout.rowPitch + 0.1, {
          fontSize: 42,
          worldHeight: 0.3,
          maxWorldWidth: 1.3,
          paddingX: 24,
        })
      );
    }

    const outline = new THREE.LineSegments(
      geo(new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1))),
      mat(new THREE.LineBasicMaterial({ color: color("--sb-scene-outline", "#398f9c") }))
    );

    outline.visible = false;
    scene.add(outline);

    const routeLine = new THREE.Line(
      geo(new THREE.BufferGeometry()),
      mat(
        new THREE.LineDashedMaterial({
          color: color("--sb-scene-route", "#48949a"),
          dashSize: 0.15,
          gapSize: 0.07,
          transparent: true,
          opacity: 0.85,
          depthTest: false,
        })
      )
    );

    routeLine.renderOrder = 5;
    routeLine.visible = false;
    scene.add(routeLine);

    let dirty = true,
      lastSelected = -1,
      lastZoom = -1,
      frame = 0,
      down = null,
      hovered = "";
    const radius = Math.sqrt(width * width + depth * depth + 7) / 2;
    let fitDistance = 12;

    const setSelection = (id) => {
      if (lastSelected >= 0)
        faces.setColorAt(lastSelected, racks[lastSelected].kind === "target" ? targetColor : normalColor);

      const selected = byId.get(id);
      outline.visible = !!selected;

      if (selected) {
        faces.setColorAt(selected.instance, selectionColor);
        const selectedHeight = rackHeightFor(selected);
        outline.position.set(selected.xM - originX, 0.04 + selectedHeight / 2, selected.yM - originZ - 0.5);
        outline.scale.set(rackWidth + 0.035, selectedHeight + 0.1, 1.035);
      }

      lastSelected = selected?.instance ?? -1;
      faces.instanceColor.needsUpdate = true;
      dirty = true;
    };

    const fit = () => {
      const vfov = THREE.MathUtils.degToRad(camera.fov),
        hfov = 2 * Math.atan(Math.tan(vfov / 2) * camera.aspect);

      fitDistance = radius / Math.sin(Math.min(vfov, hfov) / 2);
      controls.target.set(0, 0.75, 0);
      camera.position
        .copy(controls.target)
        .add(new THREE.Vector3(1, 1.15, 1.35).normalize().multiplyScalar(fitDistance));
      camera.far = Math.max(1000, fitDistance * 20);
      camera.updateProjectionMatrix();

      controls.maxDistance = Math.max(100, fitDistance * 8);
      controls.update();
      dirty = true;
    };

    const focus = () => {
      const r = byId.get(latest.current.selectedId);
      if (!r) return;

      const direction = camera.position.clone().sub(controls.target).normalize();

      controls.target.set(r.xM - originX, rackCenterYFor(r), r.yM - originZ - 0.5);
      camera.position.copy(controls.target).add(direction.multiplyScalar(6));
      controls.update();
      dirty = true;
    };

    const zoom = (factor) => {
      const delta = camera.position.clone().sub(controls.target);
      delta.setLength(THREE.MathUtils.clamp(delta.length() / factor, controls.minDistance, controls.maxDistance));
      camera.position.copy(controls.target).add(delta);
      controls.update();
      dirty = true;
    };

    const setTool = (value) => {
      controls.mouseButtons.LEFT = value === "pan" ? THREE.MOUSE.PAN : THREE.MOUSE.ROTATE;
      canvas.dataset.tool = value;
      dirty = true;
    };

    const setRoute = (points) => {
      routeLine.visible = !!points?.length;
      geometries.delete(routeLine.geometry);
      routeLine.geometry.dispose();

      const next = geo(
        new THREE.BufferGeometry().setFromPoints(
          (points || []).map((p) => new THREE.Vector3(p[0] - originX, p[1], p[2] - originZ))
        )
      );

      routeLine.geometry = next;
      routeLine.computeLineDistances();
      dirty = true;
    };

    const raycaster = new THREE.Raycaster(),
      pointer = new THREE.Vector2();

    const hit = (e) => {
      const rect = canvas.getBoundingClientRect();
      pointer.set(((e.clientX - rect.left) / rect.width) * 2 - 1, (-(e.clientY - rect.top) / rect.height) * 2 + 1);
      raycaster.setFromCamera(pointer, camera);

      const intersection = raycaster.intersectObject(hitboxes, false)[0];
      return intersection && racks[intersection.instanceId];
    };

    const pointerDown = (e) => {
      if (e.button === 0) down = { x: e.clientX, y: e.clientY };
    };

    const pointerUp = (e) => {
      if (
        down &&
        e.button === 0 &&
        Math.hypot(e.clientX - down.x, e.clientY - down.y) < 5 &&
        latest.current.tool === "select"
      ) {
        const found = hit(e);
        if (found) latest.current.onSelect(found);
        else latest.current.onClear?.();
      }

      down = null;
    };

    const doubleClick = (e) => {
      if (latest.current.tool === "select") {
        const found = hit(e);
        if (found) latest.current.onOpen(found);
      }
    };

    const pointerMove = (e) => {
      if (down || latest.current.tool !== "select" || racks.length > 5000) return;

      const found = hit(e),
        text = found?.label || "";

      if (text !== hovered) {
        canvas.title = text;
        hovered = text;
        canvas.classList.toggle("over-rack", !!found);
      }
    };

    const contextLost = (e) => {
      e.preventDefault();
      setError("The 3D graphics context was lost. Switch to 2D, then return to 3D to restart it.");
    };

    canvas.addEventListener("pointerdown", pointerDown);
    canvas.addEventListener("pointerup", pointerUp);
    canvas.addEventListener("pointermove", pointerMove);
    canvas.addEventListener("dblclick", doubleClick);
    canvas.addEventListener("webglcontextlost", contextLost);

    const changed = () => {
      dirty = true;
    };
    controls.addEventListener("change", changed);

    const resize = () => {
      const w = Math.max(1, node.clientWidth),
        h = Math.max(1, node.clientHeight);
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      dirty = true;
    };

    const observer = new ResizeObserver(resize);
    observer.observe(node);

    resize();
    fit();
    setSelection(latest.current.selectedId);
    setTool(latest.current.tool);
    setRoute(latest.current.route);

    const api = { fit, focus, zoom, setSelection, setTool, setRoute };
    apiRef.current = api;
    sceneRef.current = api;

    const animate = () => {
      frame = requestAnimationFrame(animate);
      controls.update();

      if (dirty) {
        renderer.render(scene, camera);
        dirty = false;

        const percent = Math.round((fitDistance / camera.position.distanceTo(controls.target)) * 100);

        if (percent !== lastZoom) {
          lastZoom = percent;
          latest.current.onZoom(percent);
        }
      }
    };

    animate();

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      controls.dispose();

      canvas.removeEventListener("pointerdown", pointerDown);
      canvas.removeEventListener("pointerup", pointerUp);
      canvas.removeEventListener("pointermove", pointerMove);
      canvas.removeEventListener("dblclick", doubleClick);
      canvas.removeEventListener("webglcontextlost", contextLost);

      instances.forEach((mesh) => mesh.dispose());
      geometries.forEach((g) => g.dispose());
      materials.forEach((m) => m.dispose());
      textures.forEach((t) => t.dispose());

      light.shadow.map?.dispose();
      renderer.dispose();
      canvas.remove();

      if (apiRef.current === api) apiRef.current = null;
      sceneRef.current = null;
    };
  }, [layoutKey, apiRef, showCovers, showLabels]);

  useEffect(() => sceneRef.current?.setSelection(selectedId), [selectedId, layoutKey]);
  useEffect(() => sceneRef.current?.setTool(tool), [tool, layoutKey]);
  useEffect(() => sceneRef.current?.setRoute(route), [route, layoutKey]);

  return (
    <div className="sb-three-surface" ref={host}>
      {error && (
        <div className="sb-three-error">
          <Note warn>{error}</Note>
          <button className="sb-button" onClick={onFallback}>
            Open 2D overview
          </button>
        </div>
      )}
    </div>
  );
}

// Undo history stays in memory; project persistence remains in localStorage.
const HISTORY_LIMIT = 60;
const HISTORY_BYTES = 24 * 1024 * 1024;

function trimSnapshots(list) {
  let bytes = 0;
  const result = [];

  for (let i = list.length - 1; i >= 0; i--) {
    const size = list[i].length * 2;

    if (result.length && (bytes + size > HISTORY_BYTES || result.length >= HISTORY_LIMIT)) break;

    bytes += size;
    result.unshift(list[i]);
  }

  return result;
}

function useProjectHistory(initializer) {
  const [state, setState] = useState(() => ({ present: initializer(), past: [], future: [] }));
  const current = useRef(state);

  const publish = (next) => {
    current.current = next;
    setState(next);
  };

  // Run the recipe once in the event handler, not inside a React state updater.
  const change = (recipe) => {
    const history = current.current;
    const next = pruneProject(recipe(clone(history.present)));
    const before = JSON.stringify(history.present);

    if (before === JSON.stringify(next)) return false;

    publish({ present: next, past: trimSnapshots([...history.past, before]), future: [] });
    return true;
  };

  const undo = () => {
    const history = current.current;
    if (!history.past.length) return false;

    publish({
      present: JSON.parse(history.past[history.past.length - 1]),
      past: history.past.slice(0, -1),
      future: trimSnapshots([...history.future, JSON.stringify(history.present)]),
    });

    return true;
  };

  const redo = () => {
    const history = current.current;
    if (!history.future.length) return false;

    publish({
      present: JSON.parse(history.future[history.future.length - 1]),
      past: trimSnapshots([...history.past, JSON.stringify(history.present)]),
      future: history.future.slice(0, -1),
    });

    return true;
  };

  const replace = (next) => publish({ present: clone(next), past: [], future: [] });

  return {
    project: state.present,
    change,
    replace,
    undo,
    redo,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    getCurrent: () => current.current.present,
  };
}

function editingText(target) {
  const el = target instanceof Element ? target : document.activeElement;

  if (
    el?.closest('textarea,select,[contenteditable="true"],[contenteditable=""],[role="textbox"],[role="combobox"]') ||
    el?.isContentEditable
  )
    return true;

  const input = el?.closest("input");
  return !!input && !["checkbox", "radio", "button", "submit", "reset", "range"].includes(input.type);
}

function affectedRackIds(p, rackId) {
  if (p.rackOverrides[rackId]) return new Set([rackId]);

  const [rowId] = rackId.split("#");
  const configId = p.targets.find((t) => t.id === rackId)?.configId || p.rows.find((r) => r.id === rowId)?.configId;
  const result = new Set();

  p.rows
    .filter((r) => r.configId === configId)
    .forEach((r) => {
      for (let i = 1; i <= r.count; i++) {
        const id = rackKey(r.id, i);
        if (!p.rackOverrides[id]) result.add(id);
      }
    });

  p.targets.filter((t) => t.configId === configId && !p.rackOverrides[t.id]).forEach((t) => result.add(t.id));

  return result;
}

function removeSelectionFromProject(p, selection) {
  if (!selection || !rackExists(p, selection.rackId)) return p;

  const [rowId] = selection.rackId.split("#");
  const configId =
    p.targets.find((t) => t.id === selection.rackId)?.configId || p.rows.find((r) => r.id === rowId)?.configId;
  const c = p.rackOverrides[selection.rackId] || p.configs.find((item) => item.id === configId);

  if (!c) return p;
  migrateConfig(c);

  const affected = affectedRackIds(p, selection.rackId);
  const ranges = [];

  if (selection.type === "panel") {
    const panelIndex = selection.panelIndex;
    if (!Number.isInteger(panelIndex) || panelIndex < 1 || panelIndex > c.panelQty) return p;

    const offset = panelPortOffset(c, panelIndex);
    const capacity = faceModel(c, panelIndex).adapters;
    if (capacity > 0) ranges.push([offset + 1, offset + capacity]);

    c.panelLayouts.splice(panelIndex - 1, 1);
    c.panelQty = c.panelLayouts.length;
    c.facePlan = clone(c.panelLayouts[0]?.facePlan || initialFace(c));
  } else if (selection.type === "cassette") {
    const panelIndex = Math.max(1, Math.min(c.panelQty, selection.panelIndex || 1));
    const before = faceModel(c, panelIndex);
    if (before.isNemo) return p;

    const [r, s] = selection.cassetteId.split(":").map(Number);
    const group = before.rows[r]?.groups[s];
    if (!group || !group.items.length) return p;

    const offset = panelPortOffset(c, panelIndex);
    const first = offset + group.items[0].number;
    const last = offset + group.items[group.items.length - 1].number;
    ranges.push([first, last]);

    const plan = clone(before.plan);
    plan.rows[r].splice(s, 1);
    plan.legacyOverflow = 0;

    c.panelLayouts[panelIndex - 1] = {
      ...c.panelLayouts[panelIndex - 1],
      facePlan: plan,
    };

    if (panelIndex === 1) c.facePlan = clone(plan);

    const next = faceModel(c, panelIndex);
    const sizes = cassetteBreakdown(c, panelIndex);
    c.modulesPerPanel = next.cassettes;
    c.portsPerModule = sizes.length === 1 ? sizes[0].ports : c.portsPerModule;
  } else if (selection.type === "port") {
    const port = Number(selection.port);
    if (!Number.isInteger(port) || port < 1 || port > totalAdapterPorts(c)) return p;
    c.disabledPorts = [...new Set([...(c.disabledPorts || []), port])].sort((a, b) => a - b);
    delete p.patchOverrides[`${selection.rackId}|${port}`];
    c.faceMappingReview = true;
    return p;
  } else {
    return p;
  }

  const oldQty = c.patchQty;
  const assignedRemoved = ranges.reduce((sum, [start, end]) => sum + Math.max(0, Math.min(oldQty, end) - start + 1), 0);
  c.patchQty = Math.max(0, oldQty - assignedRemoved);

  const nextOverrides = {};

  Object.entries(p.patchOverrides).forEach(([key, length]) => {
    const split = key.lastIndexOf("|");
    const id = key.slice(0, split);
    const n = Number(key.slice(split + 1));

    if (!affected.has(id) || !Number.isInteger(n)) {
      nextOverrides[key] = length;
      return;
    }

    let shift = 0;

    for (const [start, end] of ranges) {
      if (n >= start && n <= end) return;
      if (n > end) shift += end - start + 1;
    }

    nextOverrides[`${id}|${n - shift}`] = length;
  });

  p.patchOverrides = nextOverrides;
  c.faceMappingReview = true;
  if (c.autoSizeEnabled === false) refreshManualSizing(c);
  return p;
}

function syncPatchAndCableInterfaces(project, changedConfig) {
  const frontCode = panelInterfaceCode(changedConfig, "front");
  const frontDefinition = panelInterfaceDefinition(changedConfig, frontCode, "front");
  const patch = cableFieldsForEndpoints(frontDefinition, frontDefinition, changedConfig.patchFields, IDS.patch);
  if (patch.schemaId === IDS.patch) changedConfig.patchFields = patch.draft;

  project.paths.forEach((path) => {
    const role = path.connectionRole || "front";
    const sourceRows = project.rows.filter((row) => path.sourceRowId === "all" || row.id === path.sourceRowId);
    const sourceConfig = sourceRows
      .map((row) => project.configs.find((item) => item.id === row.configId))
      .find(Boolean);
    const targetData = project.targets.find((item) => item.id === path.targetId);
    const targetConfig = targetData ? project.configs.find((item) => item.id === targetData.configId) : null;
    if (!sourceConfig || !targetConfig) return;
    if (sourceConfig !== changedConfig && targetConfig !== changedConfig) return;

    const sourceDefinition = panelInterfaceDefinition(sourceConfig, panelInterfaceCode(sourceConfig, role), role);
    const targetDefinition = panelInterfaceDefinition(targetConfig, panelInterfaceCode(targetConfig, role), role);
    const next =
      path.assemblyMode === "breakout"
        ? cableFieldsForSpecificSchema(
            IDS.fanout,
            sourceDefinition,
            targetDefinition,
            path.cableFields,
            path.cableSchema,
            breakoutTotalFibers(path)
          )
        : cableFieldsForEndpoints(sourceDefinition, targetDefinition, path.cableFields, path.cableSchema);
    path.cableSchema = next.schemaId;
    path.cableFields = next.draft;
    path.endpointA = sourceDefinition;
    path.endpointB = targetDefinition;
  });
}

function BomPreview({ project, model, images, onClose, onExport }) {
  const totalQuantity = model.bom.reduce((sum, item) => sum + (Number(item.qty) || 0), 0);
  const purchaseQuantity = model.bom.reduce((sum, item) => sum + (item.existing ? 0 : Number(item.qty) || 0), 0);

  useEffect(() => {
    const previous = document.activeElement;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div
      className="sb-bom-modal-shade"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="sb-bom-document" role="dialog" aria-modal="true" aria-labelledby="sb-bom-title">
        <header className="sb-bom-document-head">
          <div>
            <img src={LOGO} alt="GoFoton" />
            <div>
              <small>DRAFT MATERIAL SUMMARY</small>
              <h2 id="sb-bom-title">{project.name || "Fiber infrastructure plan"}</h2>
            </div>
          </div>
          <button type="button" className="sb-icon-control" aria-label="Close bill of materials" onClick={onClose}>
            <X size={18} />
          </button>
        </header>

        <div className="sb-bom-document-summary">
          <Stat label="BOM line items" value={fmt(model.bom.length)} />
          <Stat label="Total material quantity" value={fmt(totalQuantity)} />
          <Stat label="Purchase quantity" value={fmt(purchaseQuantity)} />
          <Stat label="Physical cable runs" value={fmt(model.totalRuns)} />
        </div>

        <div className="sb-bom-document-table-wrap">
          <table className="sb-bom-document-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Category</th>
                <th>Part number / specification</th>
                <th>Length</th>
                <th>Qty</th>
                <th>Purchase</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {model.bom.map((item) => (
                <tr key={item.key}>
                  <td>
                    <div className="sb-bom-document-product">
                      <ProductImage url={images[item.schemaId]} name={item.name} />
                      <strong>{item.name}</strong>
                    </div>
                  </td>
                  <td>{item.kind}</td>
                  <td>
                    <code>{item.part || "Ordering fields required"}</code>
                  </td>
                  <td>{item.length != null ? `${fmt(item.length)} m` : "—"}</td>
                  <td>{fmt(item.qty)}</td>
                  <td>{fmt(item.existing ? 0 : item.qty)}</td>
                  <td>
                    {item.existing
                      ? "Existing"
                      : item.issues?.length
                        ? "Review required"
                        : item.estimated
                          ? `${item.estimated} estimated`
                          : "Ready"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <footer className="sb-bom-document-foot">
          <small>
            Draft planning output. Final compatibility, polarity, pricing and engineering approval remain separate
            checks.
          </small>
          <div>
            <button type="button" className="sb-button" onClick={onClose}>
              Close
            </button>
            <button type="button" className="sb-button primary" disabled={!!model.error} onClick={onExport}>
              <FileSpreadsheet size={14} />
              Export CSV
            </button>
          </div>
        </footer>
      </section>
    </div>,
    document.body
  );
}

function ProjectNameControl({ name, onRename, onDirty, onSave, onOpen }) {
  const [draft, setDraft] = useState(name);
  const [open, setOpen] = useState(false);

  const input = useRef(null);
  const toggle = useRef(null);
  const menu = useRef(null);
  const cancel = useRef(false);
  const renameTimer = useRef(null);

  useEffect(() => {
    setDraft(name);
    onDirty(false);
  }, [name, onDirty]);

  useEffect(() => () => clearTimeout(renameTimer.current), []);

  const commit = () => {
    if (cancel.current) {
      cancel.current = false;
      return;
    }

    const next = draft.trim() || name;
    setDraft(next);
    onDirty(false);

    if (next !== name) onRename(next);
  };

  useLayoutEffect(() => {
    if (!open) return;

    const position = () => {
      if (!toggle.current || !menu.current) return;

      const r = toggle.current.getBoundingClientRect();
      const width = Math.min(230, window.innerWidth - 24);

      menu.current.style.setProperty(
        "--menu-x",
        `${Math.max(12, Math.min(r.right - width, window.innerWidth - width - 12))}px`
      );
      menu.current.style.setProperty("--menu-y", `${r.bottom + 8}px`);
      menu.current.style.setProperty("--menu-width", `${width}px`);
    };

    const close = (e) => {
      if (e.type === "keydown" && e.key === "Escape") {
        setOpen(false);
        toggle.current?.focus();
      }

      if (e.type === "pointerdown" && !menu.current?.contains(e.target) && !toggle.current?.contains(e.target))
        setOpen(false);
    };

    position();
    menu.current?.querySelector("button")?.focus();

    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);

    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [open]);

  const menuAction = (action) => {
    setOpen(false);
    action();
  };

  return (
    <div className="sb-project-control">
      <FileSpreadsheet size={15} aria-hidden="true" />

      <input
        ref={input}
        className="sb-project-name"
        aria-label="Project name, click to rename"
        title="Click to rename this project"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          onDirty(e.target.value.trim() !== name);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();

          if (e.key === "Escape") {
            cancel.current = true;
            setDraft(name);
            onDirty(false);
            e.currentTarget.blur();
          }
        }}
      />

      <button
        type="button"
        ref={toggle}
        className="sb-project-toggle"
        aria-label="Project actions"
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Project actions"
        onClick={() => setOpen((v) => !v)}
      >
        <ChevronDown size={15} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menu}
            className="sb-project-menu"
            role="dialog"
            aria-label="Project actions"
            onBlur={(e) => {
              if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget) && e.relatedTarget !== toggle.current)
                setOpen(false);
            }}
          >
            <button
              type="button"
              onClick={() =>
                menuAction(() => {
                  renameTimer.current = setTimeout(() => {
                    input.current?.focus();
                    input.current?.select();
                  }, 0);
                })
              }
            >
              <Pencil size={14} />
              Rename project
            </button>

            <button type="button" onClick={() => menuAction(onSave)}>
              <Download size={14} />
              Save project file
            </button>
            <button type="button" onClick={() => menuAction(onOpen)}>
              <Upload size={14} />
              Open project file
            </button>
          </div>,
          document.body
        )}
    </div>
  );
}

export default function SolutionsBuilder() {
  const history = useProjectHistory(readSaved);
  const project = history.project;
  const setProject = history.replace;

  const [showStart, setShowStart] = useState(() => !project.onboardingComplete);
  const [hasSaved, setHasSaved] = useState(savedProjectExists);
  const [showRoute, setShowRoute] = useState(false);

  const [showRackCovers, setShowRackCovers] = useState(true);
  const [showRackLabels, setShowRackLabels] = useState(true);
  const [threeZoom, setThreeZoom] = useState(100);
  const [nameDirty, setNameDirty] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [deletionSelection, setDeletionSelection] = useState(null);
  const [layoutSelection, setLayoutSelection] = useState(null);
  const [panelSelection, setPanelSelection] = useState(null);
  const [cableFocus, setCableFocus] = useState(null);
  const [cableDisplay, setCableDisplay] = useState("assembly");

  const workspaceRef = useRef(null);
  const navigation = useRef([]);
  const keyboardActions = useRef(null);
  const sceneApi = useRef(null);

  const [view, setView] = useState("layout");
  const [rightTab, setRightTab] = useState("summary");
  const [showBomPreview, setShowBomPreview] = useState(false);
  const [rowId, setRowId] = useState(() => project.rows[0].id);
  const [rackIndex, setRackIndex] = useState(1);
  const [targetId, setTargetId] = useState("");
  const [pathId, setPathId] = useState(() => project.paths[0]?.id || "");
  const [cableIndex, setCableIndex] = useState(1);
  const [panelIndex, setPanelIndex] = useState(1);
  const [selectedPort, setSelectedPort] = useState(1);
  const [selectedCassette, setSelectedCassette] = useState("");
  const [side, setSide] = useState("front");
  const [rowPage, setRowPage] = useState(0);
  const [runPage, setRunPage] = useState(0);

  const [tool, setTool] = useState("select");
  const [mode, setMode] = useState("3D");
  const [camera, setCamera] = useState({ x: 0, y: 0, scale: 1 });
  const [dragging, setDragging] = useState(false);
  const [images, setImages] = useState({});
  const [saved, setSaved] = useState("Not saved yet");
  const [message, setMessage] = useState("");
  const [setupRows, setSetupRows] = useState(2);
  const [setupRacks, setSetupRacks] = useState(6);

  const dragRef = useRef(null),
    stageRef = useRef(null),
    importRef = useRef(null);

  const model = useMemo(() => evaluateProject(project), [project]);
  const row = project.rows.find((r) => r.id === rowId) || project.rows[0];
  const index = Math.min(Math.max(1, rackIndex), row.count);
  const selectedTarget = project.targets.find((t) => t.id === targetId);
  const rack = selectedTarget ? destinationRack(selectedTarget) : sourceRack(project, row, index);
  const config = configFor(project, rack);
  const currentPanel = Math.min(Math.max(panelIndex, 1), Math.max(config.panelQty, 1));
  const face = useMemo(() => faceModel(config, currentPanel), [config, currentPanel]);
  const isCustom = !!project.rackOverrides[rack.id];
  const group = project.paths.find((g) => g.id === pathId) || project.paths[0];

  const groupRuns = useMemo(
    () => model.runs.filter((item) => !group || item.groupId === group.id),
    [model.runs, group]
  );

  const cableBuckets = useMemo(() => groupCableRuns(model.runs), [model.runs]);

  const rackRuns = model.runs.filter((item) => item.source.id === rack.id || item.target.id === rack.id);

  const run = groupRuns.find((r) => r.source.id === rack.id && r.index === cableIndex) || groupRuns[0];
  const runGroup = run ? project.paths.find((g) => g.id === run.groupId) : group;
  const runOverride = run ? project.runOverrides[run.key] || {} : {};
  const port = Math.min(Math.max(1, selectedPort), Math.max(1, totalAdapterPorts(config)));
  const patchKey = `${rack.id}|${port}`;
  const patchLength = project.patchOverrides[patchKey] ?? config.patchM;
  const profileUses = model.racks.filter((r) => r.configId === rack.configId && !project.rackOverrides[r.id]).length;
  const panelPart = panelOrder(config, currentPanel);

  const sizing = sizingFor(config);

  const budget = budgetTier(config.panelSchema);

  const selectedFrameCapacity = frameCapacityRU(config);
  const selectedFrameUsedRU = totalPanelRU(config);

  const profile = configurationProfile(config, face, rackRuns);

  const summaryRackScoped = view !== "layout" || layoutSelection?.type === "rack" || layoutSelection?.type === "target";

  const projectSummary = (() => {
    let requiredLines = 0;
    let installedLines = 0;
    let usedRU = 0;
    let availableRU = 0;
    let densityScore = 0;
    let costScore = 0;
    let splitScore = 0;
    let weighted = 0;
    const runsByRack = new Map();

    model.runs.forEach((runItem) => {
      [runItem.source.id, runItem.target.id].forEach((id) => {
        if (!runsByRack.has(id)) runsByRack.set(id, []);
        runsByRack.get(id).push(runItem);
      });
    });

    model.racks.forEach((item) => {
      const rackConfig = configFor(project, item);
      const rackFace = faceModel(rackConfig, 1);
      const rackSizing = sizingFor(rackConfig);
      const rackProfile = configurationProfile(rackConfig, rackFace, runsByRack.get(item.id) || []);

      requiredLines += Number(rackSizing.requiredPorts) || 0;
      installedLines += Number(rackProfile.capacity) || 0;
      usedRU += totalPanelRU(rackConfig);
      availableRU += frameCapacityRU(rackConfig);
      densityScore += rackProfile.metrics.find((metric) => metric.label === "Density")?.value || 0;
      costScore += rackProfile.metrics.find((metric) => metric.label === "Relative cost")?.value || 0;
      splitScore += rackProfile.metrics.find((metric) => metric.label === "Split ratio")?.value || 0;
      weighted += 1;
    });

    const utilization = installedLines > 0 ? Math.min(100, (requiredLines / installedLines) * 100) : 0;
    const efficiency = availableRU > 0 ? Math.min(100, (usedRU / availableRU) * 100) : 0;
    const spare = Math.max(0, installedLines - requiredLines);

    return {
      requiredLines,
      installedLines,
      spare,
      usedRU,
      availableRU,
      metrics: [
        { label: "Split ratio", value: weighted ? splitScore / weighted : 0, detail: "Across all rack configurations" },
        { label: "Density", value: weighted ? densityScore / weighted : 0, detail: "Weighted project profile" },
        { label: "Relative cost", value: weighted ? costScore / weighted : 0, detail: "Weighted planning tier" },
        { label: "Port utilization", value: utilization, detail: `${Math.round(utilization)}% assigned` },
        { label: "Frame utilization", value: efficiency, detail: `${fmt(usedRU)} of ${fmt(availableRU)} RU` },
        { label: "Upgrade headroom", value: Math.max(0, 100 - utilization), detail: `${fmt(spare)} spare lines` },
      ],
    };
  })();

  const summaryProfile = summaryRackScoped
    ? profile
    : { metrics: projectSummary.metrics, capacity: projectSummary.installedLines, spare: projectSummary.spare };
  const summarySizing = summaryRackScoped
    ? sizing
    : { requiredPorts: projectSummary.requiredLines, requiredRU: projectSummary.usedRU };
  const summaryPanels = summaryRackScoped ? config.panelQty : model.panels;
  const summaryPatches = summaryRackScoped ? assignedPatchCount(config) : model.patches;
  const summaryRuns = summaryRackScoped ? rackRuns.length : model.totalRuns;
  const summaryRacks = summaryRackScoped ? 1 : model.totalRacks;

  const knownOptics = schema(IDS.optics)?.knownPartNumbers || [];

  const cableSourceRack = run?.source || rack;
  const defaultTargetData = project.targets.find((item) => item.id === group?.targetId);
  const cableTargetRack = run?.target || (defaultTargetData ? destinationRack(defaultTargetData) : rack);
  const cableSourceConfig = configFor(project, cableSourceRack);
  const cableTargetConfig = configFor(project, cableTargetRack);
  const cableRole = group?.connectionRole || "front";
  const cableSourceConnectorCode = panelInterfaceCode(cableSourceConfig, cableRole);
  const cableTargetConnectorCode = panelInterfaceCode(cableTargetConfig, cableRole);
  const cableSourceConnectorChoices = panelInterfaceChoices(cableSourceConfig, cableRole);
  const cableTargetConnectorChoices = panelInterfaceChoices(cableTargetConfig, cableRole);

  const cableConstructionLabels = group
    ? schemaFields(group.cableSchema)
        .filter((field) => !isLengthField(field) && !/connector/i.test(field.label))
        .map((field) => field.label)
    : [];

  const selectedPanelGroup = (() => {
    if (!selectedCassette) return null;
    const [r, s] = selectedCassette.split(":").map(Number);
    return face.rows[r]?.groups[s] || null;
  })();

  const change = (fn) => {
    if (history.change(fn)) setSaved("Saving on this browser");
  };

  const updateConfig = (patch) =>
    change((p) => {
      const target = p.rackOverrides[rack.id] || p.configs.find((c) => c.id === rack.configId);
      const previousFamily = target.panelSchema;
      const previousPlan = stable(facePlan(target, currentPanel));
      const previousSizingSignature = panelSizingSignature(target);
      const nextPatch = { ...patch };
      const panelFaceChange = nextPatch.panelFacePlan;
      const manualPanelRU = nextPatch.manualPanelRU;
      delete nextPatch.panelFacePlan;
      delete nextPatch.manualPanelRU;

      const manualFaceChange =
        !!panelFaceChange || manualPanelRU != null || Object.prototype.hasOwnProperty.call(nextPatch, "facePlan");
      Object.assign(target, nextPatch);
      const sizingChanged = previousSizingSignature !== panelSizingSignature(target);
      const shouldAutoSize = target.autoSizeEnabled !== false && !manualFaceChange && sizingChanged;

      if (previousFamily !== target.panelSchema) {
        target.panelFields = nextPatch.panelFields || {};
        target.moduleFields = nextPatch.moduleFields || {};
        target.moduleDrafts = {};
        target.frontMpoFibers = null;
        target.panelLayouts = [];
      }

      if (shouldAutoSize && target.autoSizeEnabled !== false) {
        autoSizeConfig(target, p.targetSpeed || "");
      } else if (manualPanelRU != null && target.autoSizeEnabled === false) {
        migrateConfig(target);
        const nextRU = [1, 2, 4].includes(Number(manualPanelRU)) ? Number(manualPanelRU) : 1;
        target.panelLayouts = target.panelLayouts.map((layout) => {
          const plan = clone(layout.facePlan || initialFace(target));
          plan.rows = Array.from(
            { length: nextRU * FACE_REFERENCE.rowsPerRU },
            (_, rowIndex) => plan.rows?.[rowIndex] || []
          );
          return {
            ...layout,
            ru: nextRU,
            facePlan: plan,
            panelFields: fieldsForPanelLoad(target, layout.panelFields || target.panelFields, nextRU, plan),
            adapterTarget: countPlanAdapters(target, plan),
          };
        });
        target.panelFields = { ...(target.panelLayouts[0]?.panelFields || target.panelFields) };
        target.facePlan = clone(target.panelLayouts[0]?.facePlan || initialFace(target));
        refreshManualSizing(target);
      } else if (panelFaceChange) {
        migrateConfig(target);
        const index = Math.max(0, Math.min(target.panelLayouts.length - 1, panelFaceChange.panelIndex - 1));
        const existingLayout = target.panelLayouts[index] || {
          ru: panelRU(target, index + 1),
          panelFields: panelFieldsFor(target, index + 1),
        };
        const synchronizedFields = fieldsForPanelLoad(
          target,
          existingLayout.panelFields || target.panelFields,
          existingLayout.ru || panelRU(target, index + 1),
          panelFaceChange.facePlan
        );
        target.panelLayouts[index] = {
          ...existingLayout,
          facePlan: clone(panelFaceChange.facePlan),
          panelFields: synchronizedFields,
          adapterTarget: countPlanAdapters(target, panelFaceChange.facePlan),
        };
        target.facePlan = clone(target.panelLayouts[0]?.facePlan || panelFaceChange.facePlan);
        target.panelFields = { ...(target.panelLayouts[0]?.panelFields || target.panelFields) };

        const f = faceModel(target, index + 1);
        const sizes = cassetteBreakdown(target, index + 1);
        target.modulesPerPanel = f.cassettes;
        target.portsPerModule = sizes.length === 1 ? sizes[0].ports : target.portsPerModule;
        if (target.autoSizeEnabled === false) refreshManualSizing(target);
      } else {
        migrateConfig(target);
        target.facePlan = clone(facePlan(target, 1));
        if (target.autoSizeEnabled === false) refreshManualSizing(target);
      }

      if (stable(facePlan(target, currentPanel)) !== previousPlan) target.faceMappingReview = true;
      syncPatchAndCableInterfaces(p, target);
      return p;
    });

  const updateCurrentPanelFields = (draft) =>
    change((p) => {
      const target = p.rackOverrides[rack.id] || p.configs.find((item) => item.id === rack.configId);
      if (!target) return p;

      migrateConfig(target);

      const index = Math.max(0, Math.min(target.panelLayouts.length - 1, currentPanel - 1));
      const existing = target.panelLayouts[index] || {
        id: uid("panel"),
        ru: panelRU(target, currentPanel),
        adapterTarget: 0,
        facePlan: clone(facePlan(target, currentPanel)),
        panelFields: clone(panelFieldsFor(target, currentPanel)),
      };

      const oldFields = existing.panelFields || panelFieldsFor(target, currentPanel);
      const requestedRU = Number(fieldValue(target.panelSchema, draft, RU_FIELDS));
      const nextRU = [1, 2, 4].includes(requestedRU) ? requestedRU : existing.ru || panelRU(target, currentPanel);
      let nextPlan = clone(existing.facePlan || facePlan(target, currentPanel));
      const rowCount = Math.max(1, nextRU * FACE_REFERENCE.rowsPerRU);
      nextPlan.rows = Array.from({ length: rowCount }, (_, rowIndex) => nextPlan.rows?.[rowIndex] || []);

      const oldSize = Number(fieldValue(target.panelSchema, oldFields, ["Cassette Front Adapter Port Count"]));
      const newSize = Number(fieldValue(target.panelSchema, draft, ["Cassette Front Adapter Port Count"]));
      const validSize = [4, 6, 8, 9, 12, 24, 28].includes(newSize) ? newSize : selectedCassettePortCount(target);

      const distribute = (count) => {
        let remaining = Math.max(0, count);
        const perRow = Math.max(1, Math.floor(24 / Math.min(24, validSize)));
        return Array.from({ length: rowCount }, () => {
          const amount = Math.min(perRow, remaining);
          remaining -= amount;
          return Array(amount).fill(validSize);
        });
      };

      if (target.panelSchema === IDS.app && [4, 6, 8, 9, 12, 24, 28].includes(newSize) && newSize !== oldSize) {
        const existingCount = (nextPlan.rows || []).reduce((sum, row) => sum + row.length, 0);
        nextPlan.rows = distribute(existingCount);
      }

      const cassetteField = fieldFor(target.panelSchema, ["Number of Cassettes"]);
      const oldCassetteCode = cassetteField ? String(oldFields?.[cassetteField.key] ?? "") : "";
      const newCassetteCode = cassetteField ? String(draft?.[cassetteField.key] ?? "") : "";

      if (target.panelSchema === IDS.app && cassetteField && newCassetteCode && newCassetteCode !== oldCassetteCode) {
        const maxPerRow = Math.max(1, Math.floor(24 / Math.min(24, validSize)));
        const fullCount = rowCount * maxPerRow;
        const codeCounts = { H: 0, A: 1, B: 2, C: 3, D: 4, E: 5, F: fullCount };
        if (Object.prototype.hasOwnProperty.call(codeCounts, newCassetteCode))
          nextPlan.rows = distribute(codeCounts[newCassetteCode]);
      }

      const synchronizedFields = fieldsForPanelLoad(target, draft, nextRU, nextPlan);

      target.panelLayouts[index] = {
        ...existing,
        ru: nextRU,
        facePlan: nextPlan,
        panelFields: synchronizedFields,
        adapterTarget: countPlanAdapters(target, nextPlan),
      };

      const interfaceLabels = [...ADAPTER_FIELDS, ...REAR_ADAPTER_FIELDS, "Inner Cable Options"];
      const changedInterfaceKeys = schemaFields(target.panelSchema)
        .filter(
          (field) => interfaceLabels.includes(field.label) && synchronizedFields[field.key] !== oldFields[field.key]
        )
        .map((field) => field.key);

      if (changedInterfaceKeys.length) {
        target.panelLayouts = target.panelLayouts.map((layout, layoutIndex) => {
          if (layoutIndex === index) return layout;
          const fields = { ...(layout.panelFields || target.panelFields) };
          changedInterfaceKeys.forEach((key) => {
            fields[key] = synchronizedFields[key];
          });
          return { ...layout, panelFields: fields };
        });
      }

      target.facePlan = clone(target.panelLayouts[0]?.facePlan || nextPlan);
      target.panelFields = { ...(target.panelLayouts[0]?.panelFields || synchronizedFields) };
      target.modulesPerPanel = faceModel(target, index + 1).cassettes;
      target.faceMappingReview = true;
      if (target.autoSizeEnabled === false) refreshManualSizing(target);

      syncPatchAndCableInterfaces(p, target);
      return p;
    });

  const setManualPanelRU = (ru) =>
    change((p) => {
      const target = p.rackOverrides[rack.id] || p.configs.find((item) => item.id === rack.configId);
      if (!target) return p;

      migrateConfig(target);
      const nextRU = [1, 2, 4].includes(Number(ru)) ? Number(ru) : 1;

      target.panelLayouts = target.panelLayouts.map((layout) => {
        const plan = clone(layout.facePlan || initialFace(target));
        const rowCount = nextRU * FACE_REFERENCE.rowsPerRU;
        plan.rows = Array.from({ length: rowCount }, (_, index) => plan.rows?.[index] || []);
        const fields = fieldsForPanelLoad(target, layout.panelFields || target.panelFields, nextRU, plan);
        return {
          ...layout,
          ru: nextRU,
          facePlan: plan,
          panelFields: fields,
          adapterTarget: countPlanAdapters(target, plan),
        };
      });

      target.panelQty = target.panelLayouts.length;
      target.panelFields = { ...(target.panelLayouts[0]?.panelFields || target.panelFields) };
      target.facePlan = clone(target.panelLayouts[0]?.facePlan || initialFace(target));
      target.faceMappingReview = true;
      refreshManualSizing(target);
      syncPatchAndCableInterfaces(p, target);
      return p;
    });

  const setManualPanelCount = (quantity) =>
    change((p) => {
      const target = p.rackOverrides[rack.id] || p.configs.find((item) => item.id === rack.configId);
      if (!target) return p;

      migrateConfig(target);
      const qty = Math.max(1, Math.round(Number(quantity) || 1));
      const template = target.panelLayouts[0] || {
        id: uid("panel"),
        ru: panelRU(target, 1),
        facePlan: clone(facePlan(target, 1)),
        panelFields: clone(panelFieldsFor(target, 1)),
        adapterTarget: totalAdapterPorts(target),
      };

      if (qty < target.panelLayouts.length) {
        target.panelLayouts = target.panelLayouts.slice(0, qty);
      } else {
        while (target.panelLayouts.length < qty) {
          target.panelLayouts.push({ ...clone(template), id: uid("panel") });
        }
      }

      target.panelQty = target.panelLayouts.length;
      target.panelFields = { ...(target.panelLayouts[0]?.panelFields || target.panelFields) };
      target.facePlan = clone(target.panelLayouts[0]?.facePlan || initialFace(target));
      target.faceMappingReview = true;
      refreshManualSizing(target);
      return p;
    });

  const updateRow = (patch) =>
    change((p) => {
      Object.assign(
        p.rows.find((r) => r.id === row.id),
        patch
      );
      return p;
    });

  const updateGroup = (patch) =>
    change((p) => {
      const found = p.paths.find((g) => g.id === group?.id);
      if (found) Object.assign(found, patch);
      return p;
    });

  const updateAssemblyMode = (mode) =>
    change((p) => {
      const found = p.paths.find((item) => item.id === group?.id);
      if (!found) return p;
      found.assemblyMode = mode === "breakout" ? "breakout" : "jumper";

      if (found.assemblyMode === "breakout") {
        found.connectionRole = "rear";
        found.breakout = normalizeBreakout(found);
        const next = cableFieldsForSpecificSchema(
          IDS.fanout,
          found.endpointA || {},
          found.endpointB || {},
          group.cableFields,
          group.cableSchema,
          breakoutTotalFibers(found)
        );
        found.cableSchema = next.schemaId;
        found.cableFields = applySuggestedBreakoutDiameter(next.draft, breakoutTotalFibers(found), { force: true });
      } else {
        const next = cableFieldsForEndpoints(
          found.endpointA || {},
          found.endpointB || {},
          found.cableFields,
          found.cableSchema
        );
        found.cableSchema = next.schemaId;
        found.cableFields = next.draft;
      }

      migratePath(found);
      return p;
    });

  const updateBreakout = (patch) =>
    change((p) => {
      const found = p.paths.find((item) => item.id === group?.id);
      if (!found) return p;
      found.assemblyMode = "breakout";
      found.breakout = { ...normalizeBreakout(found), ...patch };
      const next = cableFieldsForSpecificSchema(
        IDS.fanout,
        found.endpointA || {},
        found.endpointB || {},
        found.cableFields || group.cableFields,
        found.cableSchema || group.cableSchema,
        breakoutTotalFibers(found)
      );
      found.cableSchema = next.schemaId;
      found.cableFields = applySuggestedBreakoutDiameter(next.draft, breakoutTotalFibers(found));
      return p;
    });

  const updateConnectorStyle = (end, style) =>
    change((p) => {
      const found = p.paths.find((item) => item.id === group?.id);
      if (!found) return p;
      found[end === "a" ? "connectorStyleA" : "connectorStyleB"] = style;
      return p;
    });

  const updateCableEndpoint = (end, panelCode) =>
    change((p) => {
      const found = p.paths.find((item) => item.id === group?.id);
      if (!found) return p;

      const sourceRack = run?.source || rack;
      const defaultTarget = p.targets.find((item) => item.id === found.targetId);
      const selectedTarget = run ? run.target : defaultTarget ? destinationRack(defaultTarget) : rack;

      const sourceConfig = configFor(p, sourceRack);
      const targetConfig = configFor(p, selectedTarget);
      const editedConfig = end === "a" ? sourceConfig : targetConfig;
      const role = found.connectionRole || "front";
      const labels = role === "rear" ? REAR_ADAPTER_FIELDS : ADAPTER_FIELDS;

      editedConfig.panelFields = putField(editedConfig.panelSchema, editedConfig.panelFields, labels, panelCode);

      if (role === "front") {
        autoSizeConfig(editedConfig, p.targetSpeed || "");
      } else {
        applyKnownPanelRules(editedConfig, p.targetSpeed || "");
      }

      const sourceCode = end === "a" ? panelCode : panelInterfaceCode(sourceConfig, role);

      const targetCode = end === "b" ? panelCode : panelInterfaceCode(targetConfig, role);

      const sourceDefinition = panelInterfaceDefinition(sourceConfig, sourceCode, role);
      const targetDefinition = panelInterfaceDefinition(targetConfig, targetCode, role);
      const nextCable =
        found.assemblyMode === "breakout"
          ? cableFieldsForSpecificSchema(
              IDS.fanout,
              sourceDefinition,
              targetDefinition,
              found.cableFields,
              found.cableSchema,
              breakoutTotalFibers(found)
            )
          : cableFieldsForEndpoints(sourceDefinition, targetDefinition, found.cableFields, found.cableSchema);

      found.cableSchema = nextCable.schemaId;
      found.cableFields = nextCable.draft;
      found.endpointA = sourceDefinition;
      found.endpointB = targetDefinition;

      return p;
    });

  const updateCableRole = (role) =>
    change((p) => {
      const found = p.paths.find((item) => item.id === group?.id);
      if (!found) return p;

      found.connectionRole = role;

      const sourceRack = run?.source || rack;
      const defaultTarget = p.targets.find((item) => item.id === found.targetId);
      const targetRack = run?.target || (defaultTarget ? destinationRack(defaultTarget) : rack);
      const sourceConfig = configFor(p, sourceRack);
      const targetConfig = configFor(p, targetRack);
      const sourceCode = panelInterfaceCode(sourceConfig, role);
      const targetCode = panelInterfaceCode(targetConfig, role);
      const sourceDefinition = panelInterfaceDefinition(sourceConfig, sourceCode, role);
      const targetDefinition = panelInterfaceDefinition(targetConfig, targetCode, role);
      const nextCable =
        found.assemblyMode === "breakout"
          ? cableFieldsForSpecificSchema(
              IDS.fanout,
              sourceDefinition,
              targetDefinition,
              found.cableFields,
              found.cableSchema,
              breakoutTotalFibers(found)
            )
          : cableFieldsForEndpoints(sourceDefinition, targetDefinition, found.cableFields, found.cableSchema);

      found.cableSchema = nextCable.schemaId;
      found.cableFields = nextCable.draft;
      found.endpointA = sourceDefinition;
      found.endpointB = targetDefinition;

      return p;
    });

  const updateRouting = (patch) =>
    change((p) => {
      Object.assign(p.routing, patch);
      return p;
    });

  const updateRun = (patch) => {
    if (run)
      change((p) => {
        p.runOverrides[run.key] = { ...(p.runOverrides[run.key] || {}), ...patch, reviewed: null };
        return p;
      });
  };

  const selectRun = (item) => {
    changeView("cables");
    setRowId(item.source.rowId);
    setRackIndex(item.source.index);
    setTargetId("");
    setPathId(item.groupId);
    setCableIndex(item.index);
  };

  const openValidationLocation = (kind, text = "") => {
    const issueRack = model.racks.find(
      (item) => text.startsWith(`${item.label}:`) || text.startsWith(`${item.label} ·`)
    );

    if (issueRack) {
      if (issueRack.kind === "target") {
        setTargetId(issueRack.id);
        setLayoutSelection({ type: "target", targetId: issueRack.id });
      } else {
        setTargetId("");
        setRowId(issueRack.rowId);
        setRackIndex(issueRack.index);
        setLayoutSelection({ type: "rack", rackId: issueRack.id });
      }
    }

    if (kind === "bom") {
      setRightTab("bom");
      return;
    }

    if (kind === "short") {
      const item = model.runs.find((runItem) => runItem.tooShort);
      if (item) selectRun(item);
      setCableFocus("route");
      return;
    }

    if (kind === "review") {
      const item = model.runs.find((runItem) => !runItem.reviewed);
      if (item) selectRun(item);
      setCableFocus("variants");
      return;
    }

    if (/cable|length|route|trunk|path/i.test(text)) {
      changeView("cables");
      setCableFocus(/length|route/i.test(text) ? "route" : null);
    } else if (/cassette|panel|adapter|ordering code|port/i.test(text)) {
      changeView("panel");
      clearPanelSelection();
    } else {
      changeView("rack");
    }
  };

  const resetCamera = () => {
    sceneApi.current?.fit();
    setCamera({ x: 0, y: 0, scale: 1 });
    dragRef.current = null;
    setDragging(false);
  };

  const changeView = (next) => {
    if (next !== view) navigation.current = [...navigation.current, workspaceSnapshot()].slice(-100);

    setView(next);
    setDeletionSelection(null);
    setTool("select");
    resetCamera();
  };

  useEffect(() => {
    setSaved("Saving on this browser");

    const timer = setTimeout(() => {
      try {
        localStorage.setItem(STORE_KEY, JSON.stringify(project));
        setSaved("Saved on this browser");
      } catch {
        setSaved("Browser save failed. Export the project.");
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [project]);

  useEffect(() => {
    if (stageRef.current) {
      stageRef.current.style.setProperty("--pan-x", `${camera.x}px`);
      stageRef.current.style.setProperty("--pan-y", `${camera.y}px`);
      stageRef.current.style.setProperty("--zoom", String(camera.scale));
    }
  }, [camera, view, mode]);

  useEffect(() => {
    let cancelled = false;

    Promise.all(
      [...new Set(Object.values(IDS))].map(async (id) => {
        const slug = getSlugFromProductPage(schema(id)?.productPage);
        if (!slug) return [id, null];

        try {
          const snap = await getDocs(query(collection(db, "products"), where("slug", "==", slug), limit(1)));
          return [id, snap.empty ? null : snap.docs[0].data().image];
        } catch (error) {
          console.error("Product image lookup failed", id, error);
          return [id, null];
        }
      })
    ).then((entries) => {
      if (!cancelled) setImages(Object.fromEntries(entries));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const startPan = (event) => {
    if (tool !== "pan" || event.button !== 0 || event.target.closest("button,input,select,summary,a")) return;

    event.preventDefault();

    dragRef.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      baseX: camera.x,
      baseY: camera.y,
    };

    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  };

  const movePan = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;

    setCamera((c) => ({
      ...c,
      x: drag.baseX + event.clientX - drag.x,
      y: drag.baseY + event.clientY - drag.y,
    }));
  };

  const endPan = (event) => {
    dragRef.current = null;
    setDragging(false);

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const exportBOM = () => {
    if (model.error) {
      setMessage(model.error);
      return;
    }

    downloadText(
      "GoFoton-draft-BOM.csv",
      csv([
        [
          "Status",
          "Category",
          "Product",
          "Part number",
          "Finished length (m)",
          "Required quantity",
          "Purchase quantity",
          "Existing quantity",
          "Estimated lengths",
          "Ordering issues",
        ],
        ...model.bom.map((item) => [
          "DRAFT: compatibility not validated",
          item.kind,
          item.name,
          item.part,
          item.length,
          item.qty,
          item.existing ? 0 : item.qty,
          item.existing ? item.qty : 0,
          item.estimated,
          item.issues.join("; "),
        ]),
      ]),
      "text/csv;charset=utf-8"
    );
  };

  const exportSchedule = () =>
    downloadText(
      "GoFoton-cable-schedule.csv",
      csv([
        [
          "Group",
          "Assembly type",
          "Source",
          "Destination",
          "Cable",
          "Branches",
          "Fibers per branch",
          "Polarity",
          "End A breakout (m)",
          "End B breakout (m)",
          "Stagger (mm)",
          "Pulling grip",
          "Horizontal (m)",
          "Vertical (m)",
          "Route before slack (m)",
          "Required incl. allowances (m)",
          "Finished cable (m)",
          "Status",
          "Part number",
          "Engineering notes",
        ],
        ...model.runs.map((item) => {
          const path = project.paths.find((candidate) => candidate.id === item.groupId);
          const breakout = normalizeBreakout(path);
          return [
            item.groupName,
            path?.assemblyMode === "breakout" ? "Breakout" : "Individual jumper",
            item.source.label,
            item.target.label,
            item.index,
            path?.assemblyMode === "breakout" ? breakout.branchCount : "",
            path?.assemblyMode === "breakout" ? breakout.fibersPerLeg : "",
            path?.assemblyMode === "breakout" ? breakout.polarity : "",
            path?.assemblyMode === "breakout" ? breakout.legLengthAM : "",
            path?.assemblyMode === "breakout" ? breakout.legLengthBM : "",
            path?.assemblyMode === "breakout" && breakout.staggered ? breakout.staggerStepMM : "",
            path?.assemblyMode === "breakout" ? (breakout.pullingGrip ? "Yes" : "No") : "",
            item.horizontal,
            item.vertical,
            item.route,
            item.required,
            item.ordered,
            item.status,
            item.part.code,
            item.part.issues.join("; "),
          ];
        }),
      ]),
      "text/csv;charset=utf-8"
    );

  const importProject = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      if (file.size > 15000000) throw new Error("Project file is too large.");

      const value = migrateProject(validateProject(JSON.parse(await file.text())));

      if (window.confirm("Replace the current project with this file?")) {
        navigation.current = [];
        setDeletionSelection(null);
        setLayoutSelection(null);
        setPanelSelection(null);
        setCableFocus(null);
        setView("layout");
        setProject(value);
        setRowId(value.rows[0].id);
        setRackIndex(1);
        setTargetId("");
        setPathId(value.paths[0]?.id || "");
        setRunPage(0);
        setRowPage(0);
        resetCamera();
      }
    } catch (error) {
      setMessage(`Import failed: ${error.message}`);
    }

    event.target.value = "";
  };

  const resizeRows = (nextRows = setupRows, nextRacks = setupRacks) => {
    if (
      !Number.isSafeInteger(nextRows) ||
      nextRows < 1 ||
      !Number.isSafeInteger(nextRacks) ||
      nextRacks < 1 ||
      nextRows * nextRacks > LIMITS.racks
    ) {
      setMessage("Enter valid positive counts within the browser planning limit.");
      return;
    }

    change((p) => {
      p.rows = Array.from({ length: nextRows }, (_, i) =>
        p.rows[i]
          ? { ...p.rows[i], count: nextRacks }
          : { id: uid("row"), name: `Row ${i + 1}`, count: nextRacks, configId: p.configs[0].id, offsetM: 0 }
      );
      p.paths = p.paths.filter(
        (item) => item.sourceRowId === "all" || p.rows.some((row) => row.id === item.sourceRowId)
      );
      return p;
    });

    setRowPage(0);
    setRackIndex(1);
    setLayoutSelection(null);
  };

  const addRow = () => {
    const id = uid("row");

    change((p) => {
      p.rows.push({ id, name: `Row ${p.rows.length + 1}`, count: 6, configId: p.configs[0].id, offsetM: 0 });
      return p;
    });

    setRowId(id);
    setTargetId("");
  };

  const duplicateSelectedRack = () => {
    if (rack.kind !== "source") return;

    const sourceId = rack.id;
    const sourceRowId = rack.rowId;
    let nextId = "";
    let nextIndex = 1;

    change((p) => {
      const sourceRow = p.rows.find((item) => item.id === sourceRowId);
      if (!sourceRow) return p;

      nextIndex = sourceRow.count + 1;
      nextId = rackKey(sourceRow.id, nextIndex);
      sourceRow.count = nextIndex;

      if (p.rackOverrides[sourceId]) {
        p.rackOverrides[nextId] = clone(p.rackOverrides[sourceId]);
      }

      Object.entries(p.patchOverrides).forEach(([key, length]) => {
        if (key.startsWith(`${sourceId}|`)) {
          p.patchOverrides[`${nextId}|${key.slice(sourceId.length + 1)}`] = length;
        }
      });

      return p;
    });

    if (nextId) {
      setRowId(sourceRowId);
      setRackIndex(nextIndex);
      setTargetId("");
      setLayoutSelection({ type: "rack", rackId: nextId });
    }
  };

  const addTarget = () => {
    const id = uid("network");

    change((p) => {
      p.targets.push({
        id,
        name: `Network ${p.targets.length + 1}`,
        configId: p.targets[0].configId,
        xM: 0,
        yM: p.rows.length * p.routing.rowPitchM,
      });
      return p;
    });

    setTargetId(id);
  };

  const addGroup = (duplicate = false) => {
    const id = uid("path");

    change((p) => {
      if (duplicate && group) {
        const target = p.targets.find((t) => t.id === group.targetId),
          newId = uid("network");

        p.targets.push({ ...clone(target), id: newId, name: `${target.name} B`, xM: target.xM + 3 });
        p.paths.push({ ...clone(group), id, name: `${group.name} · B`, targetId: newId, crossXM: group.crossXM + 3 });
      } else {
        const nextPath = { ...newPath("all", p.targets[0].id), id };
        const preferBreakout = p.deploymentType !== "enterprise";
        nextPath.assemblyMode = preferBreakout ? "breakout" : "jumper";
        nextPath.connectionRole = preferBreakout ? "rear" : "front";
        migratePath(nextPath);
        p.paths.push(nextPath);
      }

      return p;
    });

    setPathId(id);
    setCableIndex(1);
    setRunPage(0);
  };

  const setIndividual = (checked) => {
    if (
      !checked &&
      !window.confirm(
        "Discard this rack’s equipment changes and use its shared configuration again? Cable-length overrides will remain."
      )
    )
      return;

    change((p) => {
      if (checked) p.rackOverrides[rack.id] = clone(config);
      else delete p.rackOverrides[rack.id];

      return p;
    });
  };

  const newConfiguration = () => {
    const name = window.prompt("Name for the new shared rack configuration", `${config.name} copy`);
    if (!name?.trim()) return;

    change((p) => {
      const id = uid("config");

      p.configs.push({ ...clone(config), id, name: name.trim() });

      if (selectedTarget) p.targets.find((t) => t.id === targetId).configId = id;
      else p.rows.find((r) => r.id === row.id).configId = id;

      delete p.rackOverrides[rack.id];
      return p;
    });
  };

  const optical = (() => {
    if (!run || !runGroup) return null;

    const a = configFor(project, run.source),
      b = configFor(project, run.target);
    const available = [runGroup.attenuation, runGroup.pairs, runGroup.pairLoss, runGroup.budget].every(
      (v) => v !== null && v !== "" && Number.isFinite(v)
    );

    if (!available) return null;

    const length = run.ordered + a.patchM + b.patchM;
    const total =
      (length / 1000) * runGroup.attenuation +
      runGroup.pairs * runGroup.pairLoss +
      runGroup.splices * runGroup.spliceLoss;

    return { total, margin: runGroup.budget - total, length };
  })();

  const missing = model.bom.filter((item) => !item.part && !item.existing).length;
  const estimated = model.runs.filter((item) => !item.reviewed).length;
  const tooShort = model.runs.filter((item) => item.tooShort).length;
  const runPages = Math.max(1, Math.ceil(groupRuns.length / 25)),
    actualRunPage = Math.min(runPage, runPages - 1);
  const rowPages = Math.max(1, Math.ceil(project.rows.length / 6)),
    actualRowPage = Math.min(rowPage, rowPages - 1);
  const configOptions = project.configs.map((c) => ({ id: c.id, label: c.name }));
  const image = images[config.panelSchema];
  const is3D = view === "layout" && mode === "3D" && !model.error;

  const layoutKey = JSON.stringify({
    rows: project.rows.map((r) => {
      const c = project.configs.find((item) => item.id === r.configId) || project.configs[0];

      return {
        id: r.id,
        name: r.name,
        count: r.count,
        offsetM: r.offsetM,
        panelQty: c.panelQty,
        panelRU: panelRU(c),
        panelPlan: (c.panelLayouts || []).map((layout) => Number(layout.ru) || 1),
        frameCapacityRU: frameCapacityRU(c),
        frameHeightM: frameHeightM(c),
        mountingWidth: mountingWidthIn(c),
      };
    }),

    targets: project.targets.map((t) => {
      const c = project.configs.find((item) => item.id === t.configId) || project.configs[0];

      return {
        id: t.id,
        name: t.name,
        xM: t.xM,
        yM: t.yM,
        panelQty: c.panelQty,
        panelRU: panelRU(c),
        panelPlan: (c.panelLayouts || []).map((layout) => Number(layout.ru) || 1),
        frameCapacityRU: frameCapacityRU(c),
        frameHeightM: frameHeightM(c),
        mountingWidth: mountingWidthIn(c),
      };
    }),

    rackPitch: project.routing.rackPitchM,
    rowPitch: project.routing.rowPitchM,
  });

  const routePoints = (() => {
    if (!showRoute) return null;

    const r = rackRuns.find((item) => item.groupId === group?.id) || rackRuns[0];
    if (!r) return null;

    const g = project.paths.find((item) => item.id === r.groupId);
    const a = configFor(project, r.source),
      b = configFor(project, r.target);
    const height = project.routing.pathway === "underfloor" ? -project.routing.pathwayM : project.routing.pathwayM;

    const points = [
      [r.source.xM, a.portHeightM, r.source.yM],
      [r.source.xM, height, r.source.yM],
    ];

    if (Math.abs(r.source.yM - r.target.yM) > 0.000001) {
      points.push([g.crossXM, height, r.source.yM], [g.crossXM, height, r.target.yM]);
    }

    points.push([r.target.xM, height, r.target.yM], [r.target.xM, b.portHeightM, r.target.yM]);

    return points;
  })();

  const routeKey = JSON.stringify(routePoints);
  const sceneRoute = useMemo(() => JSON.parse(routeKey), [routeKey]);

  const selectSceneRack = (item) => {
    setDeletionSelection(null);

    if (item.kind === "target") {
      setTargetId(item.id);
      setLayoutSelection({ type: "target", targetId: item.id });
    } else {
      setTargetId("");
      setRowId(item.rowId);
      setRackIndex(item.index);
      setLayoutSelection({ type: "rack", rackId: item.id });
    }
  };

  const openSceneRack = (item) => {
    selectSceneRack(item);
    changeView("rack");
  };

  const dismissStart = () => {
    change((p) => ({ ...p, onboardingComplete: true }));
    setShowStart(false);
    setHasSaved(true);
  };

  const createFromStart = (next) => {
    navigation.current = [];
    setDeletionSelection(null);
    setLayoutSelection(null);
    setPanelSelection(null);
    setCableFocus(null);
    setProject(next);
    setRowId(next.rows[0].id);
    setRackIndex(1);
    setTargetId("");
    setPathId(next.paths[0]?.id || "");
    setView("layout");
    setMode("3D");
    setTool("select");
    setRowPage(0);
    setRunPage(0);
    setShowStart(false);
    setHasSaved(true);
    resetCamera();
  };

  const zoomStep = (factor) => {
    if (is3D) sceneApi.current?.zoom(factor);
    else setCamera((c) => ({ ...c, scale: Math.max(0.5, Math.min(2, c.scale * factor)) }));
  };

  const workspaceSnapshot = () => ({
    view,
    rowId,
    rackIndex,
    targetId,
    pathId,
    cableIndex,
    panelIndex,
    selectedPort,
    selectedCassette,
    side,
  });

  const goBack = () => undoProject();

  const undoProject = () => {
    if (history.undo()) {
      setDeletionSelection(null);
      setSaved("Saving on this browser");
    }
  };

  const redoProject = () => {
    if (history.redo()) {
      setDeletionSelection(null);
      setSaved("Saving on this browser");
    }
  };

  const selectPanelForEditing = (number, open = false) => {
    setPanelIndex(number);
    setPanelSelection(null);
    setSelectedCassette("");

    if (open) changeView("panel");

    setDeletionSelection({ type: "panel", rackId: rack.id, panelIndex: number });
    workspaceRef.current?.focus({ preventScroll: true });
  };

  const clearPanelSelection = () => {
    setPanelSelection(null);
    setSelectedCassette("");
    setSelectedPort(0);
    setDeletionSelection(null);
  };

  const selectCassetteForEditing = (id) => {
    if (panelSelection?.type === "cassette" && selectedCassette === id) {
      clearPanelSelection();
      return;
    }

    setSelectedPort(0);
    setSelectedCassette(id);
    setPanelSelection({ type: "cassette", cassetteId: id, panelIndex: currentPanel });
    if (!face.isNemo)
      setDeletionSelection({ type: "cassette", rackId: rack.id, panelIndex: currentPanel, cassetteId: id });
    workspaceRef.current?.focus({ preventScroll: true });
  };

  const selectPatchForEditing = (number, cassetteId) => {
    const id = cassetteId || "";
    if (id && (selectedCassette !== id || panelSelection?.type !== "cassette")) {
      selectCassetteForEditing(id);
      return;
    }

    if (panelSelection?.type === "port" && panelSelection.port === number) {
      clearPanelSelection();
      return;
    }

    setSelectedPort(number);
    setSelectedCassette(id);
    setPanelSelection({ type: "port", port: number, cassetteId: id, panelIndex: currentPanel });
    setDeletionSelection({ type: "port", rackId: rack.id, panelIndex: currentPanel, port: number });
    workspaceRef.current?.focus({ preventScroll: true });
  };

  const addCassetteToRow = (rowIndex) => {
    if (face.isNemo) return;

    const plan = clone(facePlan(config, currentPanel));
    const requestedSize = config.panelSchema === IDS.app ? selectedCassettePortCount(config) : 12;
    const row = plan.rows?.[rowIndex] || [];
    const used = row.reduce((sum, item) => sum + (config.panelSchema === IDS.app ? Math.min(24, item) : 12), 0);
    const remaining = Math.max(0, 24 - used);

    let size = requestedSize;
    let span = config.panelSchema === IDS.app ? Math.min(24, size) : 12;

    if (span > remaining && config.panelSchema === IDS.app) {
      const fitting = appSizes()
        .filter((candidate) => Math.min(24, candidate) <= remaining)
        .sort((a, b) => b - a);

      if (fitting.length) {
        size = fitting[0];
        span = Math.min(24, size);
      }
    }

    if (span > remaining) {
      setMessage(
        `Row ${String.fromCharCode(65 + rowIndex)} is full. Remove or resize a cassette before adding another one.`
      );
      return;
    }

    plan.rows = Array.from({ length: plan.rows.length }, (_, index) =>
      index === rowIndex ? [...row, size] : [...(plan.rows[index] || [])]
    );
    const cassetteId = `${rowIndex}:${row.length}`;

    updateConfig({ panelFacePlan: { panelIndex: currentPanel, facePlan: plan }, faceMappingReview: true });
    setSelectedPort(0);
    setSelectedCassette(cassetteId);
    setPanelSelection({ type: "cassette", cassetteId, panelIndex: currentPanel });
    setDeletionSelection({ type: "cassette", rackId: rack.id, panelIndex: currentPanel, cassetteId });
  };

  const deleteSelected = (explicit) => {
    const selection = ["panel", "cassette", "port"].includes(explicit?.type) ? explicit : deletionSelection;

    if (!selection || selection.rackId !== rack.id || !["rack", "panel"].includes(view)) return;

    const affected = affectedRackIds(history.getCurrent(), rack.id).size;
    let subject;

    if (selection.type === "panel") {
      if (selection.panelIndex > config.panelQty) return;
      subject = `Panel ${selection.panelIndex}`;
      const scope =
        affected > 1
          ? `This changes all ${affected} racks using the shared configuration.`
          : "This changes the selected rack configuration.";
      if (
        !window.confirm(`Remove ${subject}?

${scope}
Assigned patch cords on the removed ports are removed from the count. Undo restores the deletion.`)
      )
        return;
    } else if (selection.type === "cassette") {
      const [r, s] = selection.cassetteId.split(":").map(Number);
      const selected = face.rows[r]?.groups[s];
      if (face.isNemo || !selected) return;
      subject = `Cassette ${selected.cassette}`;
    } else {
      subject = `Patch assignment on rack port ${selection.port}`;
    }

    change((p) => removeSelectionFromProject(p, selection));

    setDeletionSelection(null);
    setPanelIndex(
      selection.type === "panel" ? Math.max(1, Math.min(selection.panelIndex, config.panelQty - 1)) : currentPanel
    );
    setSelectedPort(selection.type === "port" ? selection.port : 1);
    setSelectedCassette("");
    workspaceRef.current?.focus({ preventScroll: true });
  };

  const stepRack = (delta) => {
    const racks = model.racks;
    if (!racks.length) return;
    const currentIndex = Math.max(
      0,
      racks.findIndex((item) => item.id === rack.id)
    );
    const next = racks[(currentIndex + delta + racks.length) % racks.length];
    if (next.kind === "target") {
      setTargetId(next.id);
    } else {
      setTargetId("");
      setRowId(next.rowId);
      setRackIndex(next.index);
    }
    setLayoutSelection({
      type: next.kind === "target" ? "target" : "rack",
      ...(next.kind === "target" ? { targetId: next.id } : { rackId: next.id }),
    });
    setDeletionSelection(null);
  };

  const stepPanel = (delta) => {
    const count = Math.max(1, config.panelQty);
    const next = ((currentPanel - 1 + delta + count) % count) + 1;
    setPanelIndex(next);
    clearPanelSelection();
  };

  const saveProjectFile = () => {
    const p = history.getCurrent();
    const filename = (p.name || "GoFoton-project").replace(/[<>:"/\\|?*\u0000-\u001F]/g, "-").slice(0, 100);

    downloadText(`${filename}.json`, JSON.stringify(p, null, 2), "application/json");
  };

  const shareProject = async () => {
    const p = history.getCurrent();
    const filename = (p.name || "GoFoton-project").replace(/[<>:"/\\|?*\u0000-\u001F]/g, "-").slice(0, 100);
    const text = JSON.stringify(p, null, 2);

    const downloadShareCopy = () => {
      downloadText(`${filename}.json`, text, "application/json");
    };

    setSharing(true);

    try {
      const file = new File([text], `${filename}.json`, { type: "application/json" });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: p.name,
          text: "GoFoton Solutions Builder project. Import this JSON file using Open.",
        });
      } else {
        downloadShareCopy();
      }
    } catch (error) {
      if (error.name !== "AbortError") downloadShareCopy();
    } finally {
      setSharing(false);
    }
  };

  const jumpToRearFields = () => {
    const section = workspaceRef.current?.querySelector("[data-rear-fields]");
    if (!section) return;

    section.scrollIntoView({ block: "nearest", behavior: "smooth" });
    section.querySelector("select,input")?.focus({ preventScroll: true });
  };

  const rearGroup = (() => {
    if (!selectedCassette) return null;
    const [r, s] = selectedCassette.split(":").map(Number);
    return face.rows[r]?.groups[s] || null;
  })();

  const rearDraft =
    config.separateModules && rearGroup
      ? config.moduleDrafts?.[rearGroup.ports] || config.moduleFields
      : panelFieldsFor(config, currentPanel);
  const rearLabels = [
    "Input Adapter Type",
    "Rear Adapter Type",
    "Inner Cable Options",
    "Rear Cable Connection / Inner Cabling",
  ];

  keyboardActions.current = { showStart, deleteSelected, undoProject, redoProject };

  useEffect(() => {
    const keydown = (event) => {
      const root = workspaceRef.current;
      const actions = keyboardActions.current;

      if (
        !root ||
        !actions ||
        actions.showStart ||
        event.defaultPrevented ||
        event.isComposing ||
        event.repeat ||
        editingText(event.target)
      )
        return;
      if (!root.contains(event.target) && !root.contains(document.activeElement)) return;
      if (document.querySelector('.sb-project-menu,[role="dialog"][aria-modal="true"]')) return;

      const key = event.key.toLowerCase();

      if ((event.ctrlKey || event.metaKey) && !event.altKey && key === "z") {
        event.preventDefault();
        event.shiftKey ? actions.redoProject() : actions.undoProject();
      } else if (event.ctrlKey && !event.metaKey && !event.altKey && key === "y") {
        event.preventDefault();
        actions.redoProject();
      } else if (!event.ctrlKey && !event.metaKey && !event.altKey && ["backspace", "delete"].includes(key)) {
        // Do not allow legacy browser Backspace navigation inside the workspace.
        event.preventDefault();
        actions.deleteSelected();
      }
    };

    document.addEventListener("keydown", keydown);
    return () => document.removeEventListener("keydown", keydown);
  }, []);

  return (
    <div className="solutions-builder" ref={workspaceRef} tabIndex={-1}>
      {showStart && (
        <SmartStart
          project={project}
          hasSaved={hasSaved}
          onContinue={dismissStart}
          onClose={dismissStart}
          onCreate={createFromStart}
        />
      )}

      <header className="sb-topbar">
        <img className="sb-logo" src={LOGO} alt="GoFoton" />
        <div className="sb-top-divider" />
        <div className="sb-app-title">Solutions Builder</div>

        <ProjectNameControl
          name={project.name}
          onRename={(name) => change((p) => ({ ...p, name }))}
          onDirty={setNameDirty}
          onSave={saveProjectFile}
          onOpen={() => importRef.current.click()}
        />

        <div
          className={`sb-save-state${saved === "Saved on this browser" && !nameDirty ? " is-saved" : saved.startsWith("Browser save failed") ? " is-error" : " is-pending"}`}
          role="status"
          title="Saved locally in this browser, not to a shared cloud document."
        >
          {saved === "Saved on this browser" && !nameDirty ? (
            <CheckCircle size={14} />
          ) : saved.startsWith("Browser save failed") ? (
            <AlertTriangle size={14} />
          ) : (
            <Info size={14} />
          )}
          <div className="sb-save-text">
            {nameDirty
              ? "Editing name"
              : saved === "Saved on this browser"
                ? "All changes saved"
                : saved.startsWith("Browser save failed")
                  ? "Save failed"
                  : "Saving"}
          </div>
        </div>

        <div className="sb-spacer" />

        <div className="sb-top-actions">
          <button type="button" className="sb-button" onClick={() => setShowStart(true)}>
            <Plus size={14} />
            New project
          </button>
          <button type="button" className="sb-button" onClick={() => importRef.current.click()}>
            <Upload size={14} />
            Open
          </button>
          <button type="button" className="sb-button" onClick={saveProjectFile}>
            <Download size={14} />
            Save project
          </button>

          <div className="sb-history-buttons" role="group" aria-label="Edit history">
            <button
              type="button"
              className="sb-button sb-icon-button"
              aria-label="Undo"
              title="Undo last edit (Ctrl/Cmd+Z)"
              disabled={!history.canUndo}
              onClick={goBack}
            >
              <Undo2 size={16} />
            </button>
            <button
              type="button"
              className="sb-button sb-icon-button"
              aria-label="Redo"
              title="Redo last edit (Ctrl/Cmd+Shift+Z)"
              disabled={!history.canRedo}
              onClick={redoProject}
            >
              <Redo2 size={16} />
            </button>
          </div>

          <button
            type="button"
            className="sb-button"
            onClick={shareProject}
            disabled={sharing}
            title="Share a project JSON snapshot"
          >
            <Share2 size={14} />
            {sharing ? "Sharing" : "Share"}
          </button>
          <button type="button" className="sb-button primary" onClick={exportBOM} disabled={!!model.error}>
            <FileSpreadsheet size={14} />
            Export BOM
          </button>
        </div>

        <input className="sb-hidden" ref={importRef} type="file" accept=".json" onChange={importProject} />
      </header>

      {message && (
        <div className="sb-message" role="alert">
          {message}
          <button aria-label="Dismiss message" onClick={() => setMessage("")}>
            <X size={15} />
          </button>
        </div>
      )}

      <div className="sb-workspace">
        <aside className="sb-left">
          <div className="sb-left-inner">
            {view === "layout" ? (
              <>
                <SidebarHeader
                  title="Layout"
                  subtitle={
                    layoutSelection ? "Editing the selected layout object" : "Project-wide layout and routing settings"
                  }
                  action={
                    layoutSelection ? (
                      <button
                        type="button"
                        className="sb-icon-control"
                        aria-label="Clear layout selection"
                        title="Show all layout settings"
                        onClick={() => setLayoutSelection(null)}
                      >
                        <X size={14} />
                      </button>
                    ) : null
                  }
                />

                {!layoutSelection ? (
                  <>
                    <Field label="Deployment type">
                      <Select
                        value={project.deploymentType || "custom"}
                        onChange={(value) => change((p) => ({ ...p, deploymentType: value }))}
                        options={DEPLOYMENTS.map((item) => ({ id: item.id, label: item.label }))}
                      />
                    </Field>

                    <SidebarSectionHeader title="Rows and rack quantities" onAdd={addRow} addLabel="Add row" />
                    <div className="sb-short-field-grid">
                      <Field label="Rows">
                        <NumberInput
                          value={setupRows}
                          onChange={(value) => {
                            setSetupRows(value);
                            resizeRows(value, setupRacks);
                          }}
                          min={1}
                          integer
                        />
                      </Field>
                      <Field label="Racks / row">
                        <NumberInput
                          value={setupRacks}
                          onChange={(value) => {
                            setSetupRacks(value);
                            resizeRows(setupRows, value);
                          }}
                          min={1}
                          integer
                        />
                      </Field>
                    </div>

                    <div className="sb-object-list compact">
                      {project.rows.map((item) => (
                        <button
                          key={item.id}
                          className="sb-list-choice"
                          onClick={() => {
                            setRowId(item.id);
                            setTargetId("");
                            setLayoutSelection({ type: "row", rowId: item.id });
                          }}
                        >
                          {item.name}
                          <small>{item.count} racks</small>
                        </button>
                      ))}
                    </div>

                    <SidebarSectionHeader title="Network / distribution" onAdd={addTarget} addLabel="Add destination" />
                    <div className="sb-object-list compact">
                      {project.targets.map((target) => (
                        <button
                          key={target.id}
                          className="sb-list-choice"
                          onClick={() => {
                            setTargetId(target.id);
                            setLayoutSelection({ type: "target", targetId: target.id });
                          }}
                        >
                          {target.name}
                          <small>
                            {fmt(target.xM)}, {fmt(target.yM)} m
                          </small>
                        </button>
                      ))}
                    </div>
                  </>
                ) : layoutSelection.type === "row" ? (
                  <>
                    <Field label="Row name">
                      <input
                        className="sb-input"
                        value={row.name}
                        onChange={(event) => updateRow({ name: event.target.value })}
                      />
                    </Field>
                    <Field label="Racks in this row">
                      <NumberInput
                        value={row.count}
                        min={1}
                        integer
                        onChange={(value) => updateRow({ count: value })}
                      />
                    </Field>
                    <Field label="Shared rack configuration">
                      <Select
                        value={row.configId}
                        onChange={(value) => updateRow({ configId: value })}
                        options={configOptions}
                      />
                    </Field>
                    <Field
                      label="Row start offset (m)"
                      hint="Moves the full row left or right while preserving rack pitch."
                    >
                      <NumberInput
                        value={row.offsetM}
                        min={-100000}
                        step={0.1}
                        onChange={(value) => updateRow({ offsetM: value })}
                      />
                    </Field>
                    <div className="sb-actions compact">
                      <button
                        className="sb-button"
                        onClick={() => {
                          setRackIndex(1);
                          setTargetId("");
                          setLayoutSelection({ type: "rack", rackId: rackKey(row.id, 1) });
                          changeView("rack");
                        }}
                      >
                        Open first rack
                        <ArrowRight size={12} />
                      </button>
                      <button
                        className="sb-button"
                        disabled={project.rows.length === 1}
                        onClick={() => {
                          if (window.confirm(`Remove ${row.name} and its rack overrides?`))
                            change((p) => {
                              p.rows = p.rows.filter((item) => item.id !== row.id);
                              p.paths = p.paths.filter((item) => item.sourceRowId !== row.id);
                              return p;
                            });
                        }}
                      >
                        Remove row
                      </button>
                    </div>
                  </>
                ) : layoutSelection.type === "target" ? (
                  <>
                    {selectedTarget && (
                      <>
                        <Field label="Destination name">
                          <input
                            className="sb-input"
                            value={selectedTarget.name}
                            onChange={(event) =>
                              change((p) => {
                                p.targets.find((item) => item.id === targetId).name = event.target.value;
                                return p;
                              })
                            }
                          />
                        </Field>
                        <Field label="X position (m)">
                          <NumberInput
                            value={selectedTarget.xM}
                            min={-100000}
                            step={0.1}
                            onChange={(value) =>
                              change((p) => {
                                p.targets.find((item) => item.id === targetId).xM = value;
                                return p;
                              })
                            }
                          />
                        </Field>
                        <Field label="Y position (m)">
                          <NumberInput
                            value={selectedTarget.yM}
                            min={-100000}
                            step={0.1}
                            onChange={(value) =>
                              change((p) => {
                                p.targets.find((item) => item.id === targetId).yM = value;
                                return p;
                              })
                            }
                          />
                        </Field>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Note>
                      {rack.label} uses the shared “{config.name}” configuration. Open Rack view to change its
                      equipment.
                    </Note>
                    {rack.kind === "source" && (
                      <button type="button" className="sb-button" onClick={duplicateSelectedRack}>
                        <Plus size={13} />
                        Duplicate rack
                      </button>
                    )}
                  </>
                )}
              </>
            ) : view === "cables" ? (
              <>
                <SidebarHeader
                  title="Cables"
                  subtitle={
                    cableFocus
                      ? {
                          a: "Editing Connector A",
                          cable: "Editing central cable",
                          b: "Editing Connector B",
                          breakout: "Editing breakout and furcation",
                          route: "Editing route and length",
                          variants: "Editing variant rules",
                        }[cableFocus]
                      : `${groupRuns.length} physical assemblies in the selected group`
                  }
                  action={
                    <button
                      type="button"
                      className="sb-icon-control"
                      aria-label="Add cable group"
                      title="Add cable group"
                      onClick={() => addGroup()}
                    >
                      <Plus size={14} />
                    </button>
                  }
                />

                {!group ? (
                  <Note>Add a cable group with the plus button.</Note>
                ) : cableFocus === "a" ? (
                  <>
                    <Field label="Source panel interface">
                      <Select
                        value={cableSourceConnectorCode}
                        onChange={(code) => updateCableEndpoint("a", code)}
                        options={[{ id: "", label: "Choose source interface" }, ...cableSourceConnectorChoices]}
                      />
                    </Field>
                    {!cableSourceConnectorChoices.length && (
                      <Note warn>
                        The source panel schema does not expose compatible front or rear adapter choices.
                      </Note>
                    )}
                    {group.endpointA?.kind === "lc" && (
                      <Field label="LC connector construction">
                        <Select
                          value={group.connectorStyleA || "standard"}
                          onChange={(value) => updateConnectorStyle("a", value)}
                          options={[
                            { id: "standard", label: "Standard clipped LC duplex" },
                            { id: "uniboot", label: "Uniboot LC duplex · custom code required" },
                          ]}
                        />
                      </Field>
                    )}
                  </>
                ) : cableFocus === "cable" ? (
                  <>
                    <SchemaEditor
                      schemaId={group.cableSchema}
                      draft={group.cableFields}
                      cable
                      only={cableConstructionLabels}
                      optionRule={(field, option) => cableOptionRule(group, field, option)}
                      onChange={(value) => updateGroup({ cableFields: value })}
                    />
                    {group.assemblyMode === "breakout" && (
                      <>
                        <Field
                          label="Breakout leg diameter (mm)"
                          hint="This is the diameter at the individual connectors. The central trunk diameter is selected above."
                        >
                          <NumberInput
                            value={normalizeBreakout(group).legDiameterMM}
                            min={0.25}
                            step={0.1}
                            onChange={(value) => updateBreakout({ legDiameterMM: value })}
                          />
                        </Field>
                        <div className="sb-cross-section-sidebar">
                          <BreakoutCrossSection fiberCount={breakoutTotalFibers(group)} />
                          <div>
                            <strong>{breakoutTotalFibers(group)} fibers</strong>
                            <small>Central cable cross-section preview</small>
                          </div>
                        </div>
                      </>
                    )}
                    <Check value={!!group.existingTrunk} onChange={(value) => updateGroup({ existingTrunk: value })}>
                      Cable assemblies already installed
                    </Check>
                  </>
                ) : cableFocus === "b" ? (
                  <>
                    {run && (
                      <Field label="Destination for selected assembly">
                        <Select
                          value={runOverride.targetId || group.targetId}
                          onChange={(value) => updateRun({ targetId: value === group.targetId ? null : value })}
                          options={project.targets.map((item) => ({ id: item.id, label: item.name }))}
                        />
                      </Field>
                    )}
                    <Field label="Destination panel interface">
                      <Select
                        value={cableTargetConnectorCode}
                        onChange={(code) => updateCableEndpoint("b", code)}
                        options={[{ id: "", label: "Choose destination interface" }, ...cableTargetConnectorChoices]}
                      />
                    </Field>
                    {group.endpointB?.kind === "lc" && (
                      <Field label="LC connector construction">
                        <Select
                          value={group.connectorStyleB || "standard"}
                          onChange={(value) => updateConnectorStyle("b", value)}
                          options={[
                            { id: "standard", label: "Standard clipped LC duplex" },
                            { id: "uniboot", label: "Uniboot LC duplex · custom code required" },
                          ]}
                        />
                      </Field>
                    )}
                  </>
                ) : cableFocus === "breakout" ? (
                  <>
                    <Field label="Breakout ends">
                      <Select
                        value={normalizeBreakout(group).breakoutEnds}
                        onChange={(value) => updateBreakout({ breakoutEnds: value })}
                        options={[
                          { id: "both", label: "Both ends" },
                          { id: "a", label: "End A only" },
                          { id: "b", label: "End B only" },
                        ]}
                      />
                    </Field>
                    <div className="sb-two-column-fields">
                      <Field label="Branches / end">
                        <NumberInput
                          value={normalizeBreakout(group).branchCount}
                          min={1}
                          integer
                          onChange={(value) => updateBreakout({ branchCount: value })}
                        />
                      </Field>
                      <Field label="Fibers / branch">
                        <Select
                          value={String(normalizeBreakout(group).fibersPerLeg)}
                          onChange={(value) => updateBreakout({ fibersPerLeg: Number(value) })}
                          options={[1, 2, 4, 8, 12, 16, 24].map((value) => ({ id: String(value), label: `${value}F` }))}
                        />
                      </Field>
                    </div>
                    <div className="sb-two-column-fields">
                      <Field label="End A leg length (m)">
                        <NumberInput
                          value={normalizeBreakout(group).legLengthAM}
                          min={0.1}
                          step={0.1}
                          onChange={(value) => updateBreakout({ legLengthAM: value })}
                        />
                      </Field>
                      <Field label="End B leg length (m)">
                        <NumberInput
                          value={normalizeBreakout(group).legLengthBM}
                          min={0.1}
                          step={0.1}
                          onChange={(value) => updateBreakout({ legLengthBM: value })}
                        />
                      </Field>
                    </div>
                    <Field label="Furcation / breakout point">
                      <Select
                        value={normalizeBreakout(group).furcation}
                        onChange={(value) => updateBreakout({ furcation: value })}
                        options={BREAKOUT_FURCATIONS}
                      />
                    </Field>
                    <Check
                      value={normalizeBreakout(group).staggered}
                      onChange={(value) => updateBreakout({ staggered: value })}
                    >
                      Stagger individual leg lengths
                    </Check>
                    {normalizeBreakout(group).staggered && (
                      <Field label="Stagger increment (mm)">
                        <NumberInput
                          value={normalizeBreakout(group).staggerStepMM}
                          min={0}
                          step={10}
                          onChange={(value) => updateBreakout({ staggerStepMM: value })}
                        />
                      </Field>
                    )}
                    <Check
                      value={normalizeBreakout(group).pullingGrip}
                      onChange={(value) => updateBreakout({ pullingGrip: value })}
                    >
                      Add pulling grip / protective installation sleeve
                    </Check>
                    <Field label="Polarity / fiber map">
                      <Select
                        value={normalizeBreakout(group).polarity}
                        onChange={(value) => updateBreakout({ polarity: value })}
                        options={BREAKOUT_POLARITIES}
                      />
                    </Field>
                    <Field label="Leg labels">
                      <Select
                        value={normalizeBreakout(group).labelMode}
                        onChange={(value) => updateBreakout({ labelMode: value })}
                        options={BREAKOUT_LABEL_MODES}
                      />
                    </Field>
                    <Note>
                      The furcation point is where the central cable separates into legs. A shrink tube protects the
                      transition; a breakout kit provides the mechanical structure and strain relief.
                    </Note>
                  </>
                ) : cableFocus === "route" ? (
                  <>
                    <Field label="Pathway">
                      <Select
                        value={project.routing.pathway}
                        onChange={(value) => updateRouting({ pathway: value })}
                        options={[
                          { id: "overhead", label: "Overhead tray" },
                          { id: "underfloor", label: "Underfloor pathway" },
                        ]}
                      />
                    </Field>
                    <Field label={project.routing.pathway === "overhead" ? "Tray height (m)" : "Pathway depth (m)"}>
                      <NumberInput
                        value={project.routing.pathwayM}
                        step={0.1}
                        onChange={(value) => updateRouting({ pathwayM: value })}
                      />
                    </Field>
                    <Field label="Rack pitch (m)">
                      <NumberInput
                        value={project.routing.rackPitchM}
                        min={0.001}
                        step={0.1}
                        onChange={(value) => updateRouting({ rackPitchM: value })}
                      />
                    </Field>
                    <Field label="Row pitch (m)">
                      <NumberInput
                        value={project.routing.rowPitchM}
                        min={0.001}
                        step={0.1}
                        onChange={(value) => updateRouting({ rowPitchM: value })}
                      />
                    </Field>
                    <Field label="Cross-aisle position (m)">
                      <NumberInput
                        value={group.crossXM}
                        min={-100000}
                        step={0.1}
                        onChange={(value) => updateGroup({ crossXM: value })}
                      />
                    </Field>
                    <Field label="Extra detour (m)">
                      <NumberInput
                        value={group.detourM}
                        step={0.1}
                        onChange={(value) => updateGroup({ detourM: value })}
                      />
                    </Field>
                    <Field label="Service allowance / end (m)">
                      <NumberInput
                        value={project.routing.slackM}
                        step={0.1}
                        onChange={(value) => updateRouting({ slackM: value })}
                      />
                    </Field>
                    <Field label="Additional route allowance (%)">
                      <NumberInput
                        value={project.routing.allowancePct}
                        onChange={(value) => updateRouting({ allowancePct: value })}
                      />
                    </Field>
                    {run && (
                      <>
                        <Check
                          value={runOverride.routeM != null}
                          onChange={(value) => updateRun({ routeM: value ? run.calculatedRoute : null })}
                        >
                          Override measured route
                        </Check>
                        {runOverride.routeM != null && (
                          <Field label="Measured route before allowances (m)">
                            <NumberInput
                              value={runOverride.routeM}
                              step={0.1}
                              onChange={(value) => updateRun({ routeM: value })}
                            />
                          </Field>
                        )}
                        <Check
                          value={runOverride.orderM != null}
                          onChange={(value) => updateRun({ orderM: value ? run.ordered : null })}
                        >
                          Override finished length
                        </Check>
                        {runOverride.orderM != null && (
                          <Field label="Finished length for this assembly (m)">
                            <NumberInput
                              value={runOverride.orderM}
                              min={0.001}
                              step={0.5}
                              onChange={(value) => updateRun({ orderM: value })}
                            />
                          </Field>
                        )}
                      </>
                    )}
                    <Section title="Optical-loss inputs">
                      {[
                        ["attenuation", "Fiber attenuation (dB/km)"],
                        ["pairs", "Mated connection pairs"],
                        ["pairLoss", "Loss per pair (dB)"],
                        ["splices", "Splices"],
                        ["spliceLoss", "Loss per splice (dB)"],
                        ["budget", "Allowed link loss (dB)"],
                      ].map(([key, label]) => (
                        <Field key={key} label={label}>
                          <NumberInput
                            value={group[key]}
                            nullable={!["splices", "spliceLoss"].includes(key)}
                            step={0.01}
                            onChange={(value) => updateGroup({ [key]: value })}
                          />
                        </Field>
                      ))}
                    </Section>
                  </>
                ) : cableFocus === "variants" ? (
                  <>
                    <Check
                      value={group.fixedLengthM != null}
                      onChange={(value) => updateGroup({ fixedLengthM: value ? 20 : null })}
                    >
                      Use one finished length for this group
                    </Check>
                    {group.fixedLengthM != null && (
                      <Field label="Group finished length (m)">
                        <NumberInput
                          value={group.fixedLengthM}
                          min={0.001}
                          step={0.5}
                          onChange={(value) => updateGroup({ fixedLengthM: value })}
                        />
                      </Field>
                    )}
                    <Field label="Round calculated lengths up to (m)">
                      <NumberInput
                        value={group.roundM}
                        min={0.01}
                        step={0.5}
                        onChange={(value) => updateGroup({ roundM: value })}
                      />
                    </Field>
                    <Field label="Preferred purchasable lengths (m)">
                      <input
                        className="sb-input"
                        placeholder="10, 15, 20, 30"
                        value={group.preferredM}
                        onChange={(event) => updateGroup({ preferredM: event.target.value })}
                      />
                    </Field>
                  </>
                ) : (
                  <>
                    <Field label="Group name">
                      <input
                        className="sb-input"
                        value={group.name}
                        onChange={(event) => updateGroup({ name: event.target.value })}
                      />
                    </Field>
                    <Field label="Assembly type">
                      <Select
                        value={group.assemblyMode || "jumper"}
                        onChange={updateAssemblyMode}
                        options={CABLE_ASSEMBLY_MODES}
                      />
                    </Field>
                    <Field label="Connection role">
                      <Select
                        value={group.connectionRole || "front"}
                        onChange={(value) => updateCableRole(value)}
                        options={[
                          { id: "front", label: "Front patch / equipment jumper" },
                          { id: "rear", label: "Rear trunk / backbone assembly" },
                        ]}
                      />
                    </Field>
                    <Field label="Source scope">
                      <Select
                        value={group.sourceRowId}
                        onChange={(value) => updateGroup({ sourceRowId: value })}
                        options={[
                          { id: "all", label: "All equipment rows" },
                          ...project.rows.map((item) => ({ id: item.id, label: item.name })),
                        ]}
                      />
                    </Field>
                    {group.assemblyMode === "breakout" ? (
                      <>
                        <Check
                          value={normalizeBreakout(group).onePerPanel}
                          onChange={(value) => updateBreakout({ onePerPanel: value })}
                        >
                          Generate one breakout assembly per source panel
                        </Check>
                        {!normalizeBreakout(group).onePerPanel && (
                          <Field label="Assemblies per source rack">
                            <NumberInput
                              value={group.cablesPerRack}
                              min={1}
                              integer
                              onChange={(value) => updateGroup({ cablesPerRack: value })}
                            />
                          </Field>
                        )}
                        <button
                          type="button"
                          className="sb-breakout-summary-button"
                          onClick={() => setCableFocus("breakout")}
                        >
                          <Cable size={16} />
                          <span>
                            <strong>
                              {normalizeBreakout(group).branchCount} × {normalizeBreakout(group).fibersPerLeg}F breakout
                            </strong>
                            <small>
                              {breakoutTotalFibers(group)}F trunk · {fmt(normalizeBreakout(group).legLengthAM)} /{" "}
                              {fmt(normalizeBreakout(group).legLengthBM)} m legs · polarity{" "}
                              {normalizeBreakout(group).polarity}
                            </small>
                          </span>
                          <ChevronRight size={15} />
                        </button>
                      </>
                    ) : (
                      <Field label="Assemblies per source rack">
                        <NumberInput
                          value={group.cablesPerRack}
                          min={1}
                          integer
                          onChange={(value) => updateGroup({ cablesPerRack: value })}
                        />
                      </Field>
                    )}
                    <Field label="Default destination">
                      <Select
                        value={group.targetId}
                        onChange={(value) => updateGroup({ targetId: value })}
                        options={project.targets.map((item) => ({ id: item.id, label: item.name }))}
                      />
                    </Field>
                    <button type="button" className="sb-routing-summary" onClick={() => setCableFocus("route")}>
                      <Route size={15} />
                      <span>
                        <strong>Routing and allowances</strong>
                        <small>
                          {project.routing.pathway === "overhead" ? "Overhead tray" : "Underfloor pathway"} ·{" "}
                          {fmt(project.routing.slackM)} m/end · {fmt(project.routing.allowancePct)}% additional
                        </small>
                      </span>
                      <ChevronRight size={15} />
                    </button>
                    <div className="sb-actions compact">
                      <button className="sb-button" onClick={() => addGroup(true)}>
                        Duplicate B path
                      </button>
                      <button className="sb-button" onClick={() => setCableFocus("variants")}>
                        <Tags size={12} />
                        Length rules
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                {view === "rack" ? (
                  <SidebarHeader
                    title="Rack"
                    subtitle={`${rack.label} · ${isCustom ? "individual configuration" : `${config.name} · shared by ${fmt(profileUses)} racks`}`}
                  />
                ) : (
                  <SidebarHeader
                    title="Panel"
                    subtitle={`${rack.label} · Panel ${currentPanel} of ${Math.max(1, config.panelQty)}`}
                  />
                )}

                <div className="sb-compact-check-row">
                  <Check value={isCustom} onChange={setIndividual}>
                    Customize this rack only
                  </Check>
                  <HelpTip text="When off, equipment changes apply to every rack using this shared configuration. Cable-length overrides remain individual." />
                </div>

                {deletionSelection && deletionSelection.rackId === rack.id && ["rack", "panel"].includes(view) && (
                  <div className="sb-delete-selection compact">
                    <div>
                      {deletionSelection.type === "panel"
                        ? `Panel ${deletionSelection.panelIndex}`
                        : deletionSelection.type === "cassette"
                          ? "Cassette"
                          : `Port ${deletionSelection.port}`}{" "}
                      selected
                    </div>
                    <button
                      type="button"
                      className="sb-icon-control danger"
                      onClick={deleteSelected}
                      aria-label="Remove selected item"
                      title="Remove selected item"
                    >
                      <Trash2 size={13} />
                    </button>
                    <small>Backspace / Delete · Undo restores it</small>
                  </div>
                )}

                {view === "rack" && (
                  <>
                    <div className="sb-field-heading">
                      <label>{isCustom ? "Configuration label" : "Shared configuration name"}</label>
                      <button
                        type="button"
                        className="sb-icon-control"
                        aria-label="New shared configuration"
                        title="New shared configuration"
                        onClick={newConfiguration}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <input
                      className="sb-input"
                      value={config.name}
                      onChange={(event) => updateConfig({ name: event.target.value })}
                    />

                    <PanelFamilyPicker
                      value={config.panelSchema}
                      onChange={(id) => updateConfig({ panelSchema: id, panelFields: {}, moduleFields: {} })}
                      options={PANEL_FAMILIES.map((item) => ({ id: item.id, label: item.label }))}
                      image={image}
                      name={
                        schema(config.panelSchema)?.productName ||
                        PANEL_FAMILIES.find((item) => item.id === config.panelSchema)?.label
                      }
                    />

                    <Field
                      label="Required fiber lines"
                      hint="A duplex LC adapter carries two fiber lines; simplex carries one."
                    >
                      <NumberInput
                        value={config.requiredPorts || Math.max(1, config.patchQty)}
                        min={1}
                        integer
                        onChange={(value) => updateConfig({ requiredPorts: value })}
                      />
                    </Field>

                    <div className="sb-compact-check-row">
                      <Check
                        value={config.autoSizeEnabled !== false}
                        onChange={(value) => updateConfig({ autoSizeEnabled: value })}
                      >
                        Auto-size panels and cassettes
                      </Check>
                      <HelpTip text="Automatically chooses panel height, panel quantity and cassette loading from required fiber lines and connector density. Turn this off to set panel RU and cassette loading manually." />
                    </div>

                    {config.autoSizeEnabled === false && (
                      <>
                        <div className="sb-short-field-grid">
                          <Field label="Panel height">
                            <Select
                              value={String(panelRU(config, 1))}
                              onChange={(value) => setManualPanelRU(Number(value))}
                              options={[1, 2, 4].map((value) => ({ id: String(value), label: `${value}RU` }))}
                            />
                          </Field>
                          <Field label="Panel assemblies">
                            <NumberInput
                              value={Math.max(1, config.panelQty)}
                              min={1}
                              integer
                              onChange={setManualPanelCount}
                            />
                          </Field>
                        </div>
                        <Note>
                          Manual cassette additions and removals update the installed adapter count, required fiber
                          lines and patch-cord quantity.
                        </Note>
                      </>
                    )}

                    <Field label="Passive split ratio">
                      <Select
                        value={String(config.splitRatio || 1)}
                        onChange={(value) => updateConfig({ splitRatio: Number(value) })}
                        options={[1, 2, 4, 8, 16, 32, 64].map((value) => ({
                          id: String(value),
                          label: value === 1 ? "1:1 · no passive split" : `1:${value}`,
                        }))}
                      />
                    </Field>

                    <Section title="Rack / distribution frame">
                      <Field label="Frame height and RU capacity">
                        <Select
                          value={config.frameType || "7ft"}
                          onChange={(value) => updateConfig({ frameType: value })}
                          options={[
                            ...FRAME_OPTIONS.map((option) => ({ id: option.id, label: option.label })),
                            { id: "custom", label: "Custom frame / rack" },
                          ]}
                        />
                      </Field>
                      {config.frameType === "custom" && (
                        <div className="sb-short-field-grid">
                          <Field label="Usable RU">
                            <NumberInput
                              value={config.customFrameRU || 40}
                              min={1}
                              integer
                              onChange={(value) => updateConfig({ customFrameRU: value })}
                            />
                          </Field>
                          <Field label="Height (m)">
                            <NumberInput
                              value={config.customFrameHeightM || 2.134}
                              min={0.1}
                              step={0.01}
                              onChange={(value) => updateConfig({ customFrameHeightM: value })}
                            />
                          </Field>
                        </div>
                      )}
                      <Field label="Panel mounting width">
                        <Select
                          value={String(config.mountingWidth || "23")}
                          onChange={(value) => updateConfig({ mountingWidth: value })}
                          options={[
                            { id: "23", label: "23-inch rack standard" },
                            { id: "19", label: "19-inch with adapter brackets" },
                          ]}
                        />
                      </Field>
                      <div className={`sb-frame-capacity${selectedFrameUsedRU > selectedFrameCapacity ? " over" : ""}`}>
                        <span>{selectedFrameUsedRU}RU used</span>
                        <strong>{selectedFrameCapacity}RU available</strong>
                      </div>
                      {selectedFrameUsedRU > selectedFrameCapacity && (
                        <Note warn>
                          The panel stack exceeds the selected frame capacity. The configuration is allowed, but the
                          rack/frame plan requires review.
                        </Note>
                      )}
                    </Section>

                    <Field label="Connection height above floor (m)">
                      <NumberInput
                        value={config.portHeightM}
                        step={0.1}
                        onChange={(value) => updateConfig({ portHeightM: value })}
                      />
                    </Field>
                    <Check value={config.existingPanel} onChange={(value) => updateConfig({ existingPanel: value })}>
                      Panels already installed
                    </Check>

                    <Section title={`Panel ordering options${panelPart.code ? " · complete" : " · required"}`}>
                      <SchemaEditor
                        schemaId={config.panelSchema}
                        draft={config.panelFields}
                        hidden={
                          config.autoSizeEnabled !== false
                            ? [...RU_FIELDS, "Number of Cassettes", "Number of Trays"]
                            : []
                        }
                        onChange={(draft) => updateConfig({ panelFields: draft })}
                      />
                    </Section>

                    <Section title="Optical modules in this rack">
                      <Field label="Transceiver">
                        <Select
                          value={config.opticalPart}
                          onChange={(value) => updateConfig({ opticalPart: value })}
                          options={[
                            { id: "", label: "None selected" },
                            ...knownOptics.map((item) => ({
                              id: item.code,
                              label: `${item.code} · ${item.description}`,
                            })),
                          ]}
                        />
                      </Field>
                      <Field label="Module quantity per rack">
                        <NumberInput
                          value={config.opticalQty}
                          integer
                          onChange={(value) => updateConfig({ opticalQty: value })}
                        />
                      </Field>
                      <Check
                        value={config.existingOptics}
                        onChange={(value) => updateConfig({ existingOptics: value })}
                      >
                        Modules already installed
                      </Check>
                    </Section>
                  </>
                )}

                {view === "panel" && (
                  <>
                    <div className="sb-scope focused">
                      <div className="sb-eyebrow">
                        {side === "rear"
                          ? "Rear interface"
                          : panelSelection?.type === "cassette"
                            ? "Selected cassette"
                            : panelSelection?.type === "port"
                              ? "Selected front port"
                              : "Panel configuration"}
                      </div>
                      <strong>
                        {side === "rear"
                          ? rearGroup
                            ? `Cassette ${rearGroup.cassette} · Panel ${currentPanel}`
                            : `Panel ${currentPanel} · select a cassette`
                          : panelSelection?.type === "cassette" && rearGroup
                            ? `Cassette ${rearGroup.cassette} · ${rearGroup.ports} ports`
                            : panelSelection?.type === "port"
                              ? `Rack port ${port}`
                              : `${schema(config.panelSchema)?.productName || "Panel"} · Panel ${currentPanel}`}
                      </strong>
                      <small>
                        {side === "rear"
                          ? "Only rear/internal cassette choices are shown below."
                          : panelSelection
                            ? "Only settings relevant to the selected item are shown."
                            : "Select a cassette header or adapter port in the drawing for focused editing."}
                      </small>
                    </div>

                    {(panelSelection || selectedCassette) && (
                      <button className="sb-button" onClick={clearPanelSelection}>
                        Show all panel settings
                      </button>
                    )}

                    {side === "rear" ? (
                      <>
                        {!rearGroup ? (
                          <Note>
                            Select a cassette in the rear drawing. Its rear adapter and internal fan-out options will
                            appear here.
                          </Note>
                        ) : (
                          <>
                            <div className="sb-editor-box" data-rear-fields>
                              <h3>Rear / internal cable specification</h3>
                              <small>
                                {config.separateModules
                                  ? `Applies to the selected ${rearGroup.ports}-adapter cassette specification.`
                                  : `Applies to panel ${currentPanel}'s loaded assembly specification.`}
                              </small>

                              <SchemaEditor
                                schemaId={config.panelSchema}
                                draft={rearDraft}
                                only={rearLabels}
                                onChange={(draft) => {
                                  if (config.separateModules)
                                    updateConfig({
                                      moduleDrafts: { ...config.moduleDrafts, [rearGroup.ports]: draft },
                                    });
                                  else updateCurrentPanelFields(draft);
                                }}
                              />

                              <small>
                                “16F” means 16 fibers in an internal leg. It is not a length and does not automatically
                                create an external MPO trunk.
                              </small>
                            </div>
                          </>
                        )}
                      </>
                    ) : panelSelection?.type === "cassette" ? (
                      <>
                        <PanelLayoutEditor
                          config={config}
                          panelIndex={currentPanel}
                          context="cassette"
                          onChange={updateConfig}
                          selectedCassette={selectedCassette}
                          onSelectCassette={selectCassetteForEditing}
                          onDeleteCassette={(id) =>
                            deleteSelected({
                              type: "cassette",
                              rackId: rack.id,
                              panelIndex: currentPanel,
                              cassetteId: id,
                            })
                          }
                        />

                        {config.separateModules && rearGroup && (
                          <Section title="Selected cassette ordering options" open>
                            <SchemaEditor
                              schemaId={config.panelSchema}
                              draft={config.moduleDrafts?.[rearGroup.ports] || config.moduleFields}
                              hidden={[...RU_FIELDS, "Cassette Front Adapter Port Count", "Number of Cassettes"]}
                              onChange={(draft) =>
                                updateConfig({ moduleDrafts: { ...config.moduleDrafts, [rearGroup.ports]: draft } })
                              }
                            />
                            <code>
                              {modulePart(config, rearGroup.ports).code || "Cassette ordering fields required"}
                            </code>
                          </Section>
                        )}
                      </>
                    ) : panelSelection?.type === "port" ? (
                      <>
                        <div className="sb-editor-box">
                          <h3>Rack port {port}</h3>
                          <small>
                            {face.adapter.label} · Panel {currentPanel}
                            {selectedCassette && rearGroup ? ` · Cassette ${rearGroup.cassette}` : ""}
                          </small>

                          {isPatchAssigned(config, port) ? (
                            <>
                              <Field label="Patch-cord length">
                                <NumberInput
                                  value={patchLength}
                                  min={0.001}
                                  step={0.5}
                                  onChange={(value) =>
                                    change((p) => {
                                      p.patchOverrides[patchKey] = value;
                                      return p;
                                    })
                                  }
                                />
                              </Field>

                              <Check
                                value={project.patchOverrides[patchKey] == null}
                                onChange={(useDefault) =>
                                  change((p) => {
                                    if (useDefault) delete p.patchOverrides[patchKey];
                                    else p.patchOverrides[patchKey] = config.patchM;
                                    return p;
                                  })
                                }
                              >
                                Use shared default length
                              </Check>

                              <small>
                                {project.patchOverrides[patchKey] == null
                                  ? `Using the ${fmt(config.patchM)} m rack default.`
                                  : "This port has an individual length override."}
                              </small>
                            </>
                          ) : (
                            <div className="sb-actions compact">
                              <Note>No patch cord is currently assigned to this populated adapter.</Note>
                              <button
                                className="sb-button"
                                onClick={() =>
                                  updateConfig({
                                    disabledPorts: (config.disabledPorts || []).filter((item) => item !== port),
                                  })
                                }
                              >
                                Assign patch cord
                              </button>
                            </div>
                          )}
                        </div>

                        <Section title="Patch-cord specification" open>
                          <SchemaEditor
                            schemaId={IDS.patch}
                            draft={config.patchFields}
                            cable
                            onChange={(draft) => updateConfig({ patchFields: draft })}
                          />
                        </Section>
                      </>
                    ) : (
                      <>
                        <PanelLayoutEditor
                          config={config}
                          panelIndex={currentPanel}
                          onChange={updateConfig}
                          selectedCassette={selectedCassette}
                          onSelectCassette={selectCassetteForEditing}
                          onDeleteCassette={(id) =>
                            deleteSelected({
                              type: "cassette",
                              rackId: rack.id,
                              panelIndex: currentPanel,
                              cassetteId: id,
                            })
                          }
                        />

                        {config.faceMappingReview && (
                          <div className="sb-editor-box">
                            <small>Review this face layout and the existing per-port length assignments.</small>
                            <button className="sb-button" onClick={() => updateConfig({ faceMappingReview: false })}>
                              Mark port assignments reviewed
                            </button>
                          </div>
                        )}

                        <Section title="Panel ordering options">
                          <SchemaEditor
                            schemaId={config.panelSchema}
                            draft={panelFieldsFor(config, currentPanel)}
                            hidden={[...RU_FIELDS, ...ADAPTER_FIELDS, ...(face.isNemo ? ["Number of Ports"] : [])]}
                            onChange={(draft) => updateCurrentPanelFields(draft)}
                          />
                          <code>{panelPart.code || "Complete matching ordering fields"}</code>
                          {panelPart.issues.slice(0, 4).map((text) => (
                            <small key={text}>{text}</small>
                          ))}
                        </Section>

                        {!face.isNemo && (
                          <Section title="Separate cassette purchases">
                            <Check
                              value={config.separateModules}
                              onChange={(value) => updateConfig({ separateModules: value })}
                            >
                              Order cassettes separately from an empty chassis
                            </Check>
                            {config.separateModules && (
                              <Note>
                                Loaded assemblies already include cassettes. Use an empty chassis code and select a
                                cassette to complete its individual order specification.
                              </Note>
                            )}
                          </Section>
                        )}

                        <h3>Patch-cord defaults</h3>
                        <Field label="Assigned patch cords per rack">
                          <NumberInput
                            value={config.patchQty}
                            integer
                            onChange={(value) => updateConfig({ patchQty: value })}
                          />
                        </Field>
                        <Field label="Default finished length (m)">
                          <NumberInput
                            value={config.patchM}
                            min={0.001}
                            step={0.5}
                            onChange={(value) => updateConfig({ patchM: value })}
                          />
                        </Field>
                        <Check
                          value={config.existingPatch}
                          onChange={(value) => updateConfig({ existingPatch: value })}
                        >
                          Patch cords already installed
                        </Check>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </aside>

        <main className={`sb-main${is3D ? " sb-main-3d" : ""}`}>
          <div className="sb-toolbar sb-view-switcher">
            {Object.entries(VIEWS).map(([id, label]) => (
              <button className={view === id ? "active" : ""} key={id} onClick={() => changeView(id)}>
                {label}
              </button>
            ))}
          </div>

          {is3D ? (
            <>
              <Layout3D
                layoutKey={layoutKey}
                selectedId={rack.id}
                tool={tool}
                route={sceneRoute}
                showCovers={showRackCovers}
                showLabels={showRackLabels}
                apiRef={sceneApi}
                onSelect={selectSceneRack}
                onClear={() => setLayoutSelection(null)}
                onOpen={openSceneRack}
                onZoom={setThreeZoom}
                onFallback={() => setMode("2D")}
              />
              <div className="sb-scene-selection">
                <div>
                  <strong>{rack.label}</strong>
                  <small>{isCustom ? "Individual configuration" : config.name}</small>
                </div>
                <button className="sb-button primary" onClick={() => changeView("rack")}>
                  View rack
                  <ArrowRight size={13} />
                </button>
              </div>
            </>
          ) : (
            <div
              className={`sb-canvas${tool === "pan" ? " pan" : ""}${dragging ? " dragging" : ""}`}
              onClick={(event) => {
                const background =
                  event.target === event.currentTarget ||
                  event.target === stageRef.current ||
                  event.target.classList?.contains("sb-layout");
                if (!background) return;
                if (view === "layout") setLayoutSelection(null);
                if (view === "panel") clearPanelSelection();
                if (view === "cables") setCableFocus(null);
              }}
              onPointerDown={startPan}
              onPointerMove={movePan}
              onPointerUp={endPan}
              onPointerCancel={endPan}
              onLostPointerCapture={() => {
                dragRef.current = null;
                setDragging(false);
              }}
            >
              <div className="sb-stage" ref={stageRef}>
                {model.error ? (
                  <Note warn>{model.error}</Note>
                ) : view === "layout" ? (
                  <div className="sb-layout">
                    <div className="sb-view-head">
                      <div>
                        <h2>Infrastructure overview</h2>
                        <p className="sb-muted">
                          {fmt(model.totalRacks)} racks · {fmt(model.totalRuns)} physical trunk runs · not to scale
                        </p>
                      </div>
                      <button className="sb-button" onClick={addRow}>
                        <Plus size={13} />
                        Row
                      </button>
                    </div>

                    <Layout2DMap
                      project={project}
                      selectedId={layoutSelection?.type === "rack" || layoutSelection?.type === "target" ? rack.id : ""}
                      selectedRowId={layoutSelection?.type === "row" ? layoutSelection.rowId : ""}
                      showLabels={showRackLabels}
                      onClear={() => setLayoutSelection(null)}
                      onSelectRow={(item) => {
                        setRowId(item.id);
                        setTargetId("");
                        setLayoutSelection({ type: "row", rowId: item.id });
                      }}
                      onSelect={(item) => {
                        if (item.kind === "target") {
                          setTargetId(item.id);
                          setLayoutSelection({ type: "target", targetId: item.id });
                        } else {
                          setTargetId("");
                          setRowId(item.rowId);
                          setRackIndex(item.index);
                          setLayoutSelection({ type: "rack", rackId: item.id });
                        }
                      }}
                      onOpen={(item) => {
                        if (item.kind === "target") {
                          setTargetId(item.id);
                          setLayoutSelection({ type: "target", targetId: item.id });
                        } else {
                          setTargetId("");
                          setRowId(item.rowId);
                          setRackIndex(item.index);
                          setLayoutSelection({ type: "rack", rackId: item.id });
                        }

                        changeView("rack");
                      }}
                    />
                  </div>
                ) : view === "rack" ? (
                  <div className="sb-center-card sb-rack-elevation-card">
                    <div className="sb-view-head sb-navigable-heading sb-rack-heading">
                      <button
                        type="button"
                        className="sb-icon-control"
                        aria-label="Previous rack"
                        title="Previous rack"
                        onClick={() => stepRack(-1)}
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <div className="sb-rack-title-block">
                        <ProductImage url={image} name={config.name} />
                        <div>
                          <h2>{rack.label}</h2>
                          <p className="sb-muted">
                            {isCustom ? "This rack only" : `${config.name} · shared by ${fmt(profileUses)} racks`}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="sb-icon-control"
                        aria-label="Next rack"
                        title="Next rack"
                        onClick={() => stepRack(1)}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>

                    <div className="sb-rack-countline">
                      <strong>
                        {config.panelQty} panels · {totalPanelRU(config)} of {frameCapacityRU(config)} RU ·{" "}
                        {mountingWidthIn(config)}-inch mounting
                      </strong>
                      <small>Click a panel header to select it. Edit opens its shared design.</small>
                    </div>

                    <div className="sb-rack-elevation">
                      {Array.from({ length: config.panelQty }, (_, i) => (
                        <RackPanel
                          key={`${rack.id}:${i}`}
                          config={config}
                          index={i + 1}
                          selected={
                            deletionSelection?.type === "panel" &&
                            deletionSelection.rackId === rack.id &&
                            deletionSelection.panelIndex === i + 1
                          }
                          selectedPort={port}
                          onSelect={() => selectPanelForEditing(i + 1)}
                          onOpen={() => selectPanelForEditing(i + 1, true)}
                          onPort={(n, cass) => {
                            setPanelIndex(i + 1);
                            changeView("panel");
                            selectPatchForEditing(n, cass);
                          }}
                        />
                      ))}

                      {!config.panelQty && (
                        <Note>No panels are installed. Set the panel quantity in the left sidebar.</Note>
                      )}
                    </div>
                  </div>
                ) : view === "panel" ? (
                  <div
                    className="sb-center-card sb-panel-detail"
                    onClick={(event) => {
                      if (!event.target.closest("button,input,select,summary,a,.sb-real-panel")) clearPanelSelection();
                    }}
                  >
                    <div className="sb-view-head sb-navigable-heading sb-panel-heading">
                      <div className="sb-panel-vertical-nav">
                        <button
                          type="button"
                          className="sb-icon-control"
                          aria-label="Previous panel"
                          title="Previous panel"
                          onClick={() => stepPanel(-1)}
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          type="button"
                          className="sb-icon-control"
                          aria-label="Next panel"
                          title="Next panel"
                          onClick={() => stepPanel(1)}
                        >
                          <ChevronDown size={16} />
                        </button>
                      </div>
                      <div>
                        <h2>
                          {schema(config.panelSchema)?.productName || "Panel"} · Panel {currentPanel} of{" "}
                          {Math.max(1, config.panelQty)}
                        </h2>
                        <p className="sb-muted">
                          {rack.label} · {face.ru}RU · click empty workspace to clear the current selection
                        </p>
                      </div>

                      <div className="sb-segment">
                        {["front", "rear"].map((s) => (
                          <button
                            key={s}
                            className={side === s ? "active" : ""}
                            onClick={() => {
                              setSide(s);
                              setPanelSelection(null);
                              setSelectedCassette("");
                              setDeletionSelection(null);
                            }}
                          >
                            {s === "front" ? "Front" : "Rear"}
                          </button>
                        ))}
                      </div>
                    </div>

                    {!config.panelQty && (
                      <Note>No panel is installed. The drawing below is the shared design preview.</Note>
                    )}

                    <PanelFace
                      config={config}
                      panelIndex={currentPanel}
                      selectedPort={panelSelection?.type === "port" ? port : 0}
                      selectedCassette={selectedCassette}
                      onPort={selectPatchForEditing}
                      onCassette={selectCassetteForEditing}
                      onAddCassette={addCassetteToRow}
                      onClear={clearPanelSelection}
                      side={side}
                    />

                    <div className="sb-panel-captionline">
                      {face.isNemo ? "NEMO adapter carriers" : "One-row-high cassette outlines"} · physical appearance
                      reference, not a certified mechanical drawing.
                    </div>

                    {face.warnings.map((w) => (
                      <Note key={w} warn>
                        {w}
                      </Note>
                    ))}

                    {side === "front" && face.adapters > 0 && config.panelQty > 0 && (
                      <div className="sb-selection-info">
                        <strong>
                          Rack port {port} · {face.adapter.label}
                        </strong>
                        <div>
                          {isPatchAssigned(config, port)
                            ? `${fmt(patchLength)} m patch cord · ${project.patchOverrides[patchKey] == null ? "default length" : "individual length"}`
                            : "Installed adapter, no patch assigned"}
                        </div>
                      </div>
                    )}

                    {side === "rear" && (
                      <Section title={`Rack cable schedule · ${rackRuns.length} trunks`}>
                        <Note>
                          These are rack-level cable runs. Rear cassette labels describe internal fan-out choices and do
                          not automatically create an external trunk.
                        </Note>

                        {rackRuns.map((item) => (
                          <button key={item.key} className="sb-list-choice" onClick={() => selectRun(item)}>
                            {item.source.label} → {item.target.label}
                            <small>
                              {fmt(item.ordered)} m · {item.index}
                            </small>
                          </button>
                        ))}
                      </Section>
                    )}
                  </div>
                ) : view === "cables" ? (
                  <CableConfiguratorView
                    group={group}
                    run={run}
                    groupRuns={groupRuns}
                    buckets={cableBuckets}
                    project={project}
                    optical={optical}
                    focus={cableFocus}
                    onFocus={setCableFocus}
                    onSelectRun={selectRun}
                    onExport={exportSchedule}
                    paths={project.paths}
                    pathId={group?.id || ""}
                    onPathChange={(id) => {
                      setPathId(id);
                      setCableIndex(1);
                      setCableFocus(null);
                    }}
                    displayMode={cableDisplay}
                    onDisplayMode={setCableDisplay}
                  />
                ) : (
                  <div className="sb-center-card sb-link-card">
                    <div className="sb-view-head">
                      <div>
                        <h2>{group?.name || "Connection groups"}</h2>
                        <p className="sb-muted">{fmt(groupRuns.length)} physical cables in this group</p>
                      </div>
                      <button className="sb-button" onClick={exportSchedule}>
                        <Download size={13} />
                        Cable schedule
                      </button>
                    </div>

                    {run ? (
                      <>
                        <div className="sb-path-scroll">
                          <div className="sb-path">
                            {[
                              ["Equipment", run.source.label],
                              ["Patch cord", `${fmt(configFor(project, run.source).patchM)} m default`],
                              ["Source panel", run.source.label],
                              ["Trunk", `${fmt(run.ordered)} m · cable ${run.index}`],
                              ["Destination panel", run.target.label],
                              ["Patch cord", `${fmt(configFor(project, run.target).patchM)} m default`],
                              ["Equipment", run.target.label],
                            ].map(([label, sub], i) => (
                              <div className="sb-path-node" key={i}>
                                <div className="sb-path-circle">{i + 1}</div>
                                <strong>{label}</strong>
                                <small>{sub}</small>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="sb-stats three">
                          <Stat label="Required incl. slack" value={fmt(run.required)} unit="m" />
                          <Stat label="Finished cable" value={fmt(run.ordered)} unit="m" />
                          <Stat label="Extra remaining" value={fmt(run.ordered - run.required)} unit="m" />
                        </div>

                        <div className="sb-route-breakdown">
                          Horizontal {fmt(run.horizontal)} m + vertical {fmt(run.vertical)} m + detour{" "}
                          {fmt(runGroup.detourM)} m
                          {runOverride.routeM != null ? `; measured route override ${fmt(run.route)} m` : ""}. Add{" "}
                          {project.routing.allowancePct}% route allowance and {fmt(project.routing.slackM * 2)} m
                          service allowance.
                        </div>

                        <Note warn={run.tooShort || !run.part.code}>
                          {run.status}
                          {run.custom ? " · preferred sizes exhausted; request custom length" : ""}.{" "}
                          {run.part.code || run.part.issues[0]}
                        </Note>

                        <div className="sb-loss">
                          <h3>Representative optical loss</h3>

                          {optical ? (
                            <>
                              <div className="sb-stats three">
                                <Stat label="Cable length" value={fmt(optical.length)} unit="m" />
                                <Stat label="Estimated loss" value={fmt(optical.total)} unit="dB" />
                                <Stat label="Budget margin" value={fmt(optical.margin)} unit="dB" />
                              </div>

                              <small>
                                Uses full ordered cable length and the two rack patch defaults. Individual patch
                                overrides and actual port-to-port mapping require engineering review.
                              </small>
                            </>
                          ) : (
                            <p className="sb-muted">
                              Enter verified attenuation, connection losses, and an allowed link loss in the left panel
                              to calculate this estimate.
                            </p>
                          )}
                        </div>

                        <div className="sb-table-scroll">
                          <table className="sb-table">
                            <thead>
                              <tr>
                                <th>Source</th>
                                <th>Cable</th>
                                <th>Required</th>
                                <th>Finished</th>
                                <th>Status</th>
                              </tr>
                            </thead>

                            <tbody>
                              {groupRuns.slice(actualRunPage * 25, actualRunPage * 25 + 25).map((item) => (
                                <tr key={item.key} className={item.key === run.key ? "selected" : ""}>
                                  <td>
                                    <button onClick={() => selectRun(item)}>{item.source.label}</button>
                                  </td>
                                  <td>{item.index}</td>
                                  <td>{fmt(item.required)} m</td>
                                  <td>{fmt(item.ordered)} m</td>
                                  <td>{item.status}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <Pager page={actualRunPage} pages={runPages} onChange={setRunPage} />
                      </>
                    ) : (
                      <Note>Add a connection group and choose its source rows and destination.</Note>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {view === "layout" && (
            <div className="sb-bottom-tools is-layout">
              <>
                <button
                  type="button"
                  className={tool === "select" ? "active" : ""}
                  title="Select and orbit"
                  onClick={() => setTool("select")}
                >
                  <MousePointer size={14} />
                  <span>Select</span>
                </button>
                <button
                  type="button"
                  className={tool === "pan" ? "active" : ""}
                  title="Pan"
                  onClick={() => setTool("pan")}
                >
                  <Hand size={14} />
                  <span>Pan</span>
                </button>
                <i />
                <button
                  type="button"
                  className={mode === "2D" ? "active" : ""}
                  onClick={() => {
                    setMode("2D");
                    setTool("select");
                  }}
                >
                  2D
                </button>
                <button
                  type="button"
                  className={mode === "3D" ? "active" : ""}
                  onClick={() => {
                    setMode("3D");
                    setTool("select");
                  }}
                >
                  3D
                </button>
                <i />
                <button type="button" title="Fit layout" onClick={resetCamera}>
                  <Maximize2 size={14} />
                  <span>Fit</span>
                </button>
                <button
                  type="button"
                  title="Focus selected rack"
                  disabled={!is3D || !layoutSelection}
                  onClick={() => sceneApi.current?.focus()}
                >
                  <MousePointer size={14} />
                  <span>Focus</span>
                </button>
                <button
                  type="button"
                  className={showRackLabels ? "active" : ""}
                  title="Show rack labels"
                  onClick={() => setShowRackLabels((value) => !value)}
                >
                  {showRackLabels ? <Eye size={14} /> : <EyeOff size={14} />}
                  <span>Labels</span>
                </button>
                {is3D && (
                  <button
                    type="button"
                    className={showRackCovers ? "active" : ""}
                    title="Show rack covers"
                    onClick={() => setShowRackCovers((value) => !value)}
                  >
                    <Server size={14} />
                    <span>Covers</span>
                  </button>
                )}
                {is3D && (
                  <button
                    type="button"
                    className={showRoute ? "active" : ""}
                    title="Show selected cable path"
                    onClick={() => setShowRoute((value) => !value)}
                  >
                    <Route size={14} />
                    <span>Path</span>
                  </button>
                )}
                <i />
              </>
              <button type="button" aria-label="Zoom out" onClick={() => zoomStep(1 / 1.15)}>
                −
              </button>
              <div className="sb-bottom-zoom-value">{is3D ? threeZoom : Math.round(camera.scale * 100)}%</div>
              <button type="button" aria-label="Zoom in" onClick={() => zoomStep(1.15)}>
                +
              </button>
              <button type="button" title="Reset view" onClick={resetCamera}>
                <RotateCcw size={13} />
              </button>
            </div>
          )}
        </main>

        <aside className="sb-right">
          <div className="sb-tabs">
            {["summary", "bom", "validation"].map((tab) => (
              <button key={tab} className={rightTab === tab ? "active" : ""} onClick={() => setRightTab(tab)}>
                {tab === "bom" ? "Live BOM" : tab === "summary" ? "Summary" : "Validation"}
              </button>
            ))}
          </div>

          {rightTab === "summary" ? (
            <>
              <div className="sb-profile-card">
                <div className="sb-view-head">
                  <div>
                    <h3>Configuration profile</h3>

                    <small>
                      {summaryRackScoped
                        ? "Relative planning indicators for the selected rack configuration"
                        : "Weighted planning indicators for the full project"}
                    </small>
                  </div>
                </div>

                {summaryProfile.metrics.map((metric) => (
                  <MetricBar key={metric.label} {...metric} />
                ))}
              </div>

              <div className="sb-stats two">
                <Stat label={summaryRackScoped ? "Selected rack" : "All racks"} value={fmt(summaryRacks)} />
                <Stat label="Physical trunk runs" value={fmt(summaryRuns)} />
                <Stat label="Panels" value={fmt(summaryPanels)} />

                <Stat label="Patch cords" value={fmt(summaryPatches)} />

                <Stat label="Required lines" value={fmt(summarySizing.requiredPorts)} />

                <Stat label="Installed line capacity" value={fmt(summaryProfile.capacity)} />

                <Stat label="Spare lines" value={fmt(summaryProfile.spare)} />

                <Stat label="Rack units required" value={fmt(summarySizing.requiredRU)} unit="RU" />
              </div>

              <div className="sb-summary-card">
                <h3>Length review</h3>
                <strong>
                  {fmt(model.runs.length - estimated)} of {fmt(model.runs.length)} reviewed
                </strong>
                <p>
                  {fmt(estimated)} estimates · {fmt(tooShort)} too short
                </p>
                <progress value={model.runs.length - estimated} max={model.runs.length || 1} />
              </div>

              {summaryRackScoped ? (
                <div className="sb-summary-card">
                  <h3>{rack.label}</h3>
                  <ProductImage url={image} name={config.name} />
                  <p>{isCustom ? "Custom equipment" : config.name}</p>
                  <strong>{config.panelQty} panel assemblies</strong>
                  <p>
                    {assignedPatchCount(config)} patch cords · {rackRuns.length} trunk runs
                  </p>
                  <small>
                    {totalAdapterPorts(config)} populated adapter ports · {totalPanelRU(config)} of{" "}
                    {frameCapacityRU(config)} RU used
                  </small>
                  <button className="sb-button primary" onClick={() => changeView("rack")}>
                    View rack
                  </button>
                </div>
              ) : (
                <div className="sb-summary-card">
                  <h3>Whole project</h3>
                  <strong>
                    {fmt(model.totalRacks)} racks · {fmt(model.panels)} panels
                  </strong>
                  <p>
                    {fmt(projectSummary.requiredLines)} required lines · {fmt(projectSummary.installedLines)} installed
                    capacity
                  </p>
                  <small>
                    {fmt(projectSummary.usedRU)} of {fmt(projectSummary.availableRU)} total frame RU used
                  </small>
                </div>
              )}

              <Note>
                Hardware quantities are explicit inputs. They are not inferred from a transceiver speed or claimed to be
                a validated network design.
              </Note>
            </>
          ) : rightTab === "bom" ? (
            <>
              <div className="sb-view-head sb-bom-side-head">
                <div>
                  <h3>Draft purchase list</h3>
                  <small>Live</small>
                </div>
                <button
                  type="button"
                  className="sb-icon-control"
                  aria-label="Open full bill of materials"
                  title="Open full bill of materials"
                  onClick={() => setShowBomPreview(true)}
                >
                  <Maximize2 size={15} />
                </button>
              </div>

              {model.error && <Note warn>{model.error}</Note>}

              {model.bom.map((item) => (
                <div className={`sb-bom-row${item.existing ? " existing" : ""}`} key={item.key}>
                  <ProductImage url={images[item.schemaId]} name={item.name} />

                  <div className="sb-bom-copy">
                    <strong>{item.name}</strong>
                    {item.length != null && <div>{fmt(item.length)} m finished</div>}
                    <code>{item.part || "Ordering fields required"}</code>
                    <small>
                      {item.existing
                        ? "Existing · excluded from purchase"
                        : item.estimated
                          ? `${item.estimated} estimated lengths`
                          : "Specification recorded"}
                    </small>
                  </div>

                  <b>{fmt(item.qty)}</b>
                </div>
              ))}

              <button className="sb-button primary full" disabled={!!model.error} onClick={exportBOM}>
                Export draft BOM
              </button>
            </>
          ) : (
            <>
              <h3>Checks</h3>

              {[
                {
                  ok: !model.error,
                  text: model.error || "Project is within the browser processing limit.",
                  kind: "rack",
                  action: !!model.error,
                },
                {
                  ok: missing === 0,
                  text: `${missing} purchase line(s) need ordering fields.`,
                  kind: "bom",
                  action: missing > 0,
                },
                {
                  ok: tooShort === 0,
                  text: `${tooShort} cable(s) are shorter than the calculated requirement.`,
                  kind: "short",
                  action: tooShort > 0,
                },
                {
                  ok: estimated === 0,
                  text: `${estimated} cable length(s) need review.`,
                  kind: "review",
                  action: estimated > 0,
                },
                {
                  ok: false,
                  text: "Cassette mechanical fit, product compatibility, polarity, destination capacity and A/B route separation still require engineering approval.",
                  kind: "panel",
                  action: true,
                },
              ].map((item, i) =>
                item.action ? (
                  <button
                    type="button"
                    className="sb-validation action"
                    key={i}
                    onClick={() => openValidationLocation(item.kind, item.text)}
                  >
                    {item.ok ? <CheckCircle size={15} /> : <AlertTriangle size={15} />}
                    <div>{item.text}</div>
                    <ArrowRight size={13} />
                  </button>
                ) : (
                  <div className="sb-validation" key={i}>
                    {item.ok ? <CheckCircle size={15} /> : <AlertTriangle size={15} />}
                    <div>{item.text}</div>
                  </div>
                )
              )}

              {model.issues.slice(0, 20).map((text) => (
                <button
                  type="button"
                  className="sb-issue-action"
                  key={text}
                  onClick={() => openValidationLocation("auto", text)}
                >
                  <AlertTriangle size={14} />
                  <span>{text}</span>
                  <ArrowRight size={13} />
                </button>
              ))}
              {model.issues.length > 20 && <small>{model.issues.length - 20} additional capacity warnings.</small>}

              <Note>
                Length review means a user reviewed that cable’s dimensions. It does not certify optical performance or
                product compatibility.
              </Note>
            </>
          )}
        </aside>
      </div>

      {showBomPreview && (
        <BomPreview
          project={project}
          model={model}
          images={images}
          onClose={() => setShowBomPreview(false)}
          onExport={exportBOM}
        />
      )}
    </div>
  );
}
